import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import PlantillasFeedback from '../components/PlantillasFeedback';
import GraficaDePeso from '../components/GraficaDePeso';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { useConfirm } from '../components/ui/confirm';
import { alternarMaterial, anadirLesion, yaEstaLaLesion } from '../lib/materialYLesiones';
import { accionDeEntrenador, avisoAlAsignarmelo } from '../lib/asignarEntrenador';
import { PlanBadge } from './ClientDashboard';
import { sexoLabel, objetivoLabel, equipamientoLabel, EQUIPAMIENTO_OPCIONES, plural, estadoClienteLabel, estadoDeAcceso } from '../lib/labels';
import { OBJETIVOS, nombreDelObjetivo, normalizarObjetivo } from '../lib/objetivos';
import ObjetivoDelCliente from '../components/panel/ObjetivoDelCliente';
import PicoDeForma from '../components/panel/PicoDeForma';
import { fotosDelAlta } from '../lib/fotosDelAlta';
import { BIBLIOTECA_DE_CLIENTES } from '../lib/menuFuentes';
import { sinElRelleno, notaDelCliente } from '../lib/rellenoDelImport';
import { claseDelTono, letraDelReporte, PREGUNTA_DEL_MENSUAL, respuestaDelReporte } from '../lib/coloresDelReporte';
import { celdaDelMensualNuevo } from '../lib/coloresDelMensualNuevo';
import { MEDIDAS, valorAnterior, diferencia, filasDeMedidas, tomaDelReporteYAnterior } from '../lib/medidas';
import { NOMBRE_POSE } from '../lib/comparativaFotos';
import { FilaDeRespuesta, TablaDeMedidas, TextoDelCliente, TEMA_CLARO } from '../components/panel/RespuestasEnFilas';
import CoachCheckins from '../components/CoachCheckins';
import EvolucionMedidas from '../components/EvolucionMedidas';
import InformeMensual from '../components/reports/InformeMensual';
import { FoodFilterBar } from '../components/nutrition/SearchFoodModal';
import {
    ArrowLeft, User, Mail, Phone, Calendar, CreditCard, Dumbbell, Apple,
    FileText, Scale, Target, Zap, Save, Loader2, History, Shield,
    ClipboardList, TrendingUp, Utensils, Activity, ChevronDown, ChevronUp, SlidersHorizontal, UserCog,
    AlertCircle, CheckCircle2, Pill, X, Sparkles, Pencil, Trash2, RotateCcw,
    Headphones, CalendarClock, Camera, Power, PauseCircle, Check, Paperclip,
} from 'lucide-react';
import { etiquetaAcompanamiento, etiquetaFrecuencia, loQueLlevaElPlan, planDelCatalogo } from '../lib/planAccess';
import OpcionesDePlan from '../components/OpcionesDePlan';
import { revisarPeso, fechaDelPesaje } from '../lib/pesoValido';
import { RANGOS_PERFIL, datoDelPerfilUsable } from '../lib/rangosDelPerfil';
// Los kilos, con coma (12-10, F12): salían «81.5 kg» en media ficha. El formateador de la casa.
import { num1 as kilos } from '../lib/numeros';
import { mensajeDeError } from '../lib/mensajeDeError';
import { hoyEnEspana, horaEnEspana, diaDeCalendario, pintarDia, ahoraDeLaApp } from '../lib/horaEspana';
import {
    fechaDeLaFila, filasQueSePintan, laQueRige, nombreDelOrigen, pesoParaElEditor, ultimoPesoDelHistorial, versionesDelDia,
} from '../lib/historialDeMacros';
import { resumenDelPeso, pesoActual as pesoActualDeLaFicha, ultimoDeLaSerie } from '../lib/resumenDelPeso';
import { estadoDeLaPausa, mensajeTrasLaPausa, fraseDelRechazo, avisoDePausaAlDesactivar } from '../lib/estadoDeLaPausa';
import { pestanaDeEntrada, pestanasDeLaFicha } from '../lib/pantallasDelPanel';
import { nombreDelInforme } from '../lib/nombreDelInforme';
import { preguntaAntesDePublicar } from '../lib/publicarInforme';
import AsignarTarea from '../components/AsignarTarea';
import FechasDelPlan from '../components/panel/FechasDelPlan';
import SuplementosDelCliente from '../components/panel/SuplementosDelCliente';
import AsiLoVeElCliente from '../components/panel/AsiLoVeElCliente';
import NotasDelEntrenador from '../components/NotasDelEntrenador';
import ActivarFechaDePrueba from '../components/ActivarFechaDePrueba';
import { PREFERENCE_CATEGORIES } from '../components/nutrition/PreferencesSetup';

const USER_ROLES = [
    { value: 'client', label: 'Cliente' },
    { value: 'trainer', label: 'Entrenador' },
    { value: 'admin', label: 'Admin' },
];

// EL «HOY» DEL PANEL ES EL DE ESPAÑA (10-10), no el del navegador de quien mira. El panel es
// del equipo y sus fechas son plazos del negocio: el servidor decide qué macros rigen «hoy»
// con el día de España, y el cuestionario fecha con ese mismo día. Con el del navegador, un
// «Vigente desde» propuesto desde Argentina era el día anterior, y los macros nuevos quedaban
// por detrás de los que ya tenía el cliente.
const hoyISO = hoyEnEspana;
// EL DÍA SIGUIENTE AL DE HOY EN ESPAÑA (21-10): la «Fecha de asignación de macros» arranca ahí.
const mananaISO = () => {
    const d = new Date(`${hoyISO()}T12:00:00Z`);
    d.setUTCDate(d.getUTCDate() + 1);
    return d.toISOString().slice(0, 10);
};

// Fecha de una dieta. Hay documentos antiguos con la fecha corrupta (basura que dejo el
// harness de simulacion antes de que la ruta validara el formato): antes salia
// "Invalid Date" y dejaba la lista inservible.
const _fechaDieta = (f, opts = { day: 'numeric', month: 'short', year: 'numeric' }) => {
    if (typeof f !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(f)) return 'Sin fecha válida';
    const d = new Date(f + 'T12:00:00');
    return isNaN(d) ? 'Sin fecha válida' : d.toLocaleDateString('es-ES', opts);
};

// Colores de los dos ejes del perfil (motor y respondedor): alto verde, bajo rojo.
const MOTOR_COLOR = { alto: 'text-emerald-400', medio: 'text-amber-400', bajo: 'text-red-400' };

// CON EL AÑO CUANDO NO ES ESTE (MAC-22, 14-10): un ajuste programado para 2030 se leía
// «lunes, 14 de octubre», como si fuera el de este año.
const _fechaLarga = (iso) => {
    const d = iso ? new Date(String(iso).slice(0, 10) + 'T12:00:00') : null;
    if (!d || isNaN(d)) return '';
    const deOtroAnio = d.getFullYear() !== ahoraDeLaApp().getFullYear();
    return d.toLocaleDateString('es-ES', {
        weekday: 'long', day: 'numeric', month: 'long', ...(deOtroAnio ? { year: 'numeric' } : {}),
    });
};

// El reporte del que sale el peso del ajuste: el ultimo que traiga peso (punto 25). Aqui
// arriba y no dentro del componente porque hace falta en dos sitios: al cargar la ficha,
// para rellenar el editor, y al pintar, para decir de que reporte viene.
// El último punto de una serie {fecha, valor} y el peso actual de la ficha viven en
// `lib/resumenDelPeso` desde el 21-10 (`ultimoDeLaSerie`, `pesoActual`): los leen el Resumen
// y la sección «Peso» de Seguimiento, y tienen que decir el mismo número.

// "hace 3 días", "ayer", "hoy". Con la fecha exacta al lado cuando ya no es reciente.
const _haceCuanto = (iso) => {
    if (!iso) return '';
    const d = new Date(String(iso).slice(0, 10) + 'T00:00:00');
    if (isNaN(d)) return '';
    const dias = Math.floor((ahoraDeLaApp().setHours(0, 0, 0, 0) - d.getTime()) / 86400000);
    if (dias <= 0) return 'hoy';
    if (dias === 1) return 'ayer';
    if (dias < 30) return `hace ${dias} días`;
    return `del ${_fechaCorta(String(iso).slice(0, 10))}`;
};

// ===== Buscador de menús para el coach (biblioteca real de clientes + recetario) =====
const MenuFinder = ({ api, clientId, clientUserId, clientName }) => {
    const [macros, setMacros] = useState({ P: '', H: '', G: '' });
    const [momento, setMomento] = useState('comida');
    const [fuente, setFuente] = useState(BIBLIOTECA_DE_CLIENTES ? 'ambas' : 'recetario');
    const [foods, setFoods] = useState([]);
    const [resultados, setResultados] = useState(null);
    const [relajado, setRelajado] = useState(false);
    const [buscando, setBuscando] = useState(false);
    const [enviando, setEnviando] = useState(null);   // id del menú que se está enviando

    // Texto plano del menú (para copiar o mandar por el chat)
    const menuATexto = (op) => {
        const t = op.macros_totales || {};
        const lineas = (op.items || []).map(it => `• ${it.nombre}: ${it.cantidad_g} g`).join('\n');
        return `Menú propuesto (${t.P?.toFixed(0)}P · ${t.H?.toFixed(0)}H · ${t.G?.toFixed(0)}G · ${t.kcal} kcal):\n${lineas}`;
    };

    const copiarMenu = async (op) => {
        try {
            await navigator.clipboard.writeText(menuATexto(op));
            toast.success('Menú copiado al portapapeles');
        } catch { toast.error('No se pudo copiar'); }
    };

    // Envía el menú al CHAT del cliente (le llega como mensaje del coach, con su
    // contador de no leídos). Es la forma de "hacer algo" con un menú encontrado.
    const enviarMenu = async (op) => {
        if (!clientUserId) { toast.error('Este cliente no tiene usuario de chat'); return; }
        const key = op.biblioteca_id || op.plantilla_id || op.letra;
        setEnviando(key);
        try {
            await api.post('/messages', { receiver_id: clientUserId, content: menuATexto(op) });
            toast.success(`Menú enviado al chat de ${clientName || 'el cliente'}`);
        } catch { toast.error('No se pudo enviar el menú'); }
        setEnviando(null);
    };

    // FoodFilterBar espera el api estilo fetch del área cliente; adaptamos el axios del admin
    const fetchApi = React.useCallback(
        (endpoint) => api.get(endpoint.replace(/^\/api/, '')).then(r => r.data),
        [api]
    );

    const buscar = async (foodsOverride) => {
        const P = parseFloat(macros.P), H = parseFloat(macros.H), G = parseFloat(macros.G);
        if ([P, H, G].some(v => isNaN(v))) { toast.error('Indica los tres macros objetivo'); return; }
        const f = foodsOverride ?? foods;
        setBuscando(true);
        try {
            const res = await api.post('/calculator/menu-options', {
                momento,
                macros_objetivo: { P, H, G },
                alimento_ids: f.map(x => x.id),
                fuentes: fuente === 'ambas' ? ['recetario', 'clientes'] : [fuente],
                client_id: clientId,
            });
            setResultados(res.data.opciones || []);
            setRelajado(!!res.data.relajado);
        } catch { toast.error('Error buscando menús'); }
        setBuscando(false);
    };

    const cambiarFoods = (f) => { setFoods(f); if (resultados !== null) buscar(f); };

    return (
        <div className="space-y-4">
            <Card className="bg-white border-[#E5E2DB]"><CardContent className="p-5 space-y-4">
                <p className="text-xs font-bold text-[#6F6B64] uppercase tracking-wider">Buscar menús para este cliente</p>
                <div className="grid grid-cols-3 gap-3">
                    {['P', 'H', 'G'].map(m => (
                        <div key={m}>
                            <Label className="text-[#57544E] text-xs">{{ P: 'Proteína (g)', H: 'Hidratos (g)', G: 'Grasas (g)' }[m]}</Label>
                            <Input type="number" value={macros[m]} onChange={e => setMacros(v => ({ ...v, [m]: e.target.value }))}
                                placeholder="0" className="bg-white border-[#D6D2C9] text-[#1C1B19] mt-1" data-testid={`menufinder-${m}`} />
                        </div>
                    ))}
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <Label className="text-[#57544E] text-xs">Momento (para el recetario)</Label>
                        <select value={momento} onChange={e => setMomento(e.target.value)}
                            className="w-full mt-1 h-10 rounded-md bg-white border border-[#D6D2C9] text-[#1C1B19] text-sm px-3">
                            {['desayuno', 'comida', 'merienda', 'cena'].map(x => <option key={x} value={x}>{x}</option>)}
                        </select>
                    </div>
                    {/* Con la biblioteca de clientes apagada (06-08-2026) solo queda una
                        fuente: un desplegable de una opción es ruido. */}
                    {BIBLIOTECA_DE_CLIENTES && (
                        <div>
                            <Label className="text-[#57544E] text-xs">Fuente</Label>
                            <select value={fuente} onChange={e => setFuente(e.target.value)}
                                className="w-full mt-1 h-10 rounded-md bg-white border border-[#D6D2C9] text-[#1C1B19] text-sm px-3" data-testid="menufinder-fuente">
                                <option value="ambas">Ambas fuentes</option>
                                <option value="clientes">Solo menús reales (clientes)</option>
                                <option value="recetario">Solo recetario</option>
                            </select>
                        </div>
                    )}
                </div>
                <div className="rounded-xl border border-[#E5E2DB]">
                    <FoodFilterBar api={fetchApi} selected={foods} onChange={cambiarFoods} />
                </div>
                <Button onClick={() => buscar()} disabled={buscando}
                    className="w-full bg-[#FF671F] hover:bg-[#FF671F]/90 text-white font-bold" data-testid="menufinder-buscar">
                    {buscando ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Buscar menús
                </Button>
            </CardContent></Card>

            {resultados !== null && (
                <Card className="bg-white border-[#E5E2DB]"><CardContent className="p-5 space-y-3">
                    {relajado && (
                        <p className="text-xs text-[#854D0E] font-medium">
                            No hay menús con todos esos alimentos a la vez: se muestran los que más se acercan.
                        </p>
                    )}
                    {resultados.length === 0 ? (
                        <p className="text-[#6F6B64] text-sm text-center py-6">Ningún menú encaja con esos macros y alimentos.</p>
                    ) : resultados.map((op) => (
                        <div key={op.biblioteca_id || op.plantilla_id || op.letra} className="border border-[#E5E2DB] rounded-xl p-4 space-y-2" data-testid={`menufinder-result-${op.letra}`}>
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                                <p className="font-semibold text-[#1C1B19] text-sm flex-1 min-w-0 truncate">{op.nombre}</p>
                                <div className="flex items-center gap-1.5 flex-shrink-0">
                                    {op.fuente === 'clientes'
                                        ? <Badge className="bg-[#FDEBDF] text-[#B43E06] border border-[#FF671F]/30 text-[10px]">REAL · {op.popularidad?.clientes || 0} clientes · {op.popularidad?.usos || 0} usos</Badge>
                                        : <Badge className="bg-[#EFECE6] text-[#57544E] border-0 text-[10px]">RECETARIO</Badge>}
                                    {op.cuadrada
                                        ? <Badge className="bg-[#DCFCE7] text-[#15803D] border-0 text-[10px]">Cuadrada</Badge>
                                        : <Badge className="bg-[#FEF3C7] text-[#854D0E] border-0 text-[10px]">≈ Aproximada</Badge>}
                                </div>
                            </div>
                            <div className="space-y-0.5">
                                {op.items.map((it, i) => (
                                    <div key={i} className="flex justify-between text-sm">
                                        <span className="text-[#57544E]">{it.nombre}</span>
                                        <span className="text-[#6F6B64] font-mono">{it.cantidad_g}g</span>
                                    </div>
                                ))}
                            </div>
                            <div className="flex gap-2 pt-1 items-center flex-wrap">
                                <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#DCFCE7] text-[#15803D]">{op.macros_totales?.P?.toFixed(0)}P</span>
                                <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#DBEAFE] text-[#1D4ED8]">{op.macros_totales?.H?.toFixed(0)}H</span>
                                <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#FEF3C7] text-[#854D0E]">{op.macros_totales?.G?.toFixed(0)}G</span>
                                <span className="px-2 py-0.5 rounded-full text-[11px] text-[#6F6B64]">{op.macros_totales?.kcal} kcal</span>
                                <span className="flex-1" />
                                <Button size="sm" variant="outline" onClick={() => copiarMenu(op)}
                                    className="h-7 px-2.5 text-xs bg-transparent border-[#D6D2C9] text-[#57544E] hover:text-[#1C1B19] hover:border-[#FF671F]"
                                    data-testid={`menufinder-copiar-${op.letra}`}>
                                    Copiar
                                </Button>
                                <Button size="sm" onClick={() => enviarMenu(op)}
                                    disabled={enviando === (op.biblioteca_id || op.plantilla_id || op.letra)}
                                    className="h-7 px-2.5 text-xs bg-[#FF671F] hover:bg-[#FF671F]/90 text-white font-semibold"
                                    data-testid={`menufinder-enviar-${op.letra}`}>
                                    {enviando === (op.biblioteca_id || op.plantilla_id || op.letra)
                                        ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Enviar por chat'}
                                </Button>
                            </div>
                        </div>
                    ))}
                </CardContent></Card>
            )}
        </div>
    );
};

/**
 * LAS RUTINAS QUE EL EQUIPO YA TIENE ESCRITAS, para ponérsela a este cliente.
 *
 * Se escriben en el panel de Rutinas y aquí solo se eligen. Al asignarla se COPIA: si luego
 * se le retoca un ejercicio a este cliente, no se le cambia a los demás que la tienen.
 *
 * Si la biblioteca está vacía no se pinta nada: una tarjeta que solo dice «no hay nada» es
 * ruido en una pantalla que ya tiene diez.
 */
// EL PDF DE LA RUTINA (bloque 11, doc 19-08): se sigue generando fuera, como hasta ahora;
// aquí solo se sube, y el cliente lo abre desde Entreno. Se abre vía blob porque el visor
// del navegador no manda la cabecera de autenticación.
// EL CICLO DE ESTE CLIENTE, CUANDO NO ES EL DE SU PLAN (punto 44 del doc del 24-08).
//
// «El catálogo define un solo ciclo por plan y hay Premium mensuales y de 5 semanas.» Los
// tres campos que pisan el plan existían desde el punto 36 y estaban vacíos en los 198
// perfiles de producción, porque no había ni una pantalla para ponerlos: esta es.
//
// La pantalla dice lo que mueve ANTES de guardar, que no es poco: de aquí cuelgan qué
// reporte le toca cada semana, la ventana para mandarlo y la del ajuste. A quien esté a
// mitad de ventana se le puede abrir o cerrar un reporte de golpe.
const TIPOS_DE_REPORTE = [
    ['', 'ninguno'], ['semanal', 'semanal'], ['quincenal', 'quincenal'],
    ['mensual', 'mensual'], ['trimestral', 'trimestral'],
];

/** LA RUTINA QUE SE VENDE APARTE (César, 17-09-2026).
 *
 *      «Que se habilite cuando la compra; si no, permanece deshabilitada. De pronto hacer
 *       un botón para habilitar y deshabilitar, y ya, porque igual alguien va a estar
 *       pendiente de que pague, y si pagó pues se la habilita.»
 *
 *  Bronze y Mantenimiento traen la rutina como «opcional» en el catálogo, que no es tenerla:
 *  es que se vende suelta. Mientras no la compren, su pantalla de Rutina no existe. Este
 *  interruptor es lo que la enciende, porque hoy ese pago entra por fuera de la app.
 *
 *  Solo sale en los planes de rutina opcional: en los que la llevan de serie no pinta nada,
 *  y enseñarlo ahí invitaría a apagar algo que este interruptor no apaga.
 */
const RutinaSuelta = ({ api, clientId, profile, habilitaciones, onGuardado, esAdmin }) => {
    const [guardando, setGuardando] = useState(false);
    if ((habilitaciones?.rutina || '') !== 'opcional') return null;
    const tiene = !!profile?.rutina_comprada;

    const cambiar = async () => {
        setGuardando(true);
        try {
            await api.put(`/admin/clients/${clientId}/rutina-comprada`, { comprada: !tiene });
            toast.success(tiene ? 'Le quitamos la rutina' : 'Rutina habilitada para este cliente');
            // El cambio se pinta YA, sin esperar a que la ficha entera vuelva a cargar
            // (12-10, G2: seguía en «No la tiene» hasta recargar a mano).
            onGuardado?.({ rutina_comprada: !tiene });
        } catch (e) {
            console.error('[rutina suelta]', e);
            toast.error(mensajeDeError(e, 'No pudimos cambiarlo'));
            // Y si falló la respuesta pero no el cambio, la ficha dice lo que hay de verdad.
            onGuardado?.();
        } finally {
            setGuardando(false);
        }
    };

    return (
        <Card className="bg-white border-[#E5E2DB]" data-testid="rutina-suelta">
            <CardHeader className="pb-2">
                <CardTitle className="text-sm text-[#6F6B64] uppercase tracking-wider">La rutina, aparte</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                    <span className={`text-sm font-semibold ${tiene ? 'text-[#15803D]' : 'text-[#57544E]'}`}
                          data-testid="rutina-suelta-estado">
                        {tiene ? 'La tiene habilitada' : 'No la tiene'}
                    </span>
                    {/* Habilitar sin pago una rutina que se vende, solo un administrador (12-10). */}
                    {esAdmin ? (
                    <Button onClick={cambiar} disabled={guardando}
                            data-testid="rutina-suelta-boton"
                            className={tiene ? 'bg-[#EFECE6] text-[#1C1B19] hover:bg-[#E5E2DB]' : 'bg-[#FF671F] text-white'}>
                        {guardando ? <Loader2 className="w-4 h-4 animate-spin" />
                            : tiene ? 'Quitársela' : 'Habilitársela'}
                    </Button>
                    ) : (
                        <span className="text-[#6F6B64] text-xs">Lo cambia un administrador</span>
                    )}
                </div>
            </CardContent>
        </Card>
    );
};

// LA FECHA DE RUTINA (César, 04-11). Qué rutina del mes ve (la actual o la anterior) y de
// qué fecha sale: su último pago, o la que se pone aquí a mano para el anual o el que pagó
// por otro lado. Solo cambia la rutina que ve: ni cobros, ni ciclo, ni acceso. La regla
// vive en el servidor (`core/cual_rutina_del_mes`); aquí solo se enseña y se edita.
const FUENTE_DE_LA_FECHA = { a_mano: 'puesta a mano', ultimo_pago: 'su último pago', hoja_de_pagos: 'la hoja de pagos', ciclo_de_stripe: 'su ciclo de Stripe' };
const fechaCorta = (f) => (f ? new Date(`${f}T12:00:00`).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' }) : '');

const FechaDeRutina = ({ api, clientId, esAdmin }) => {
    const [datos, setDatos] = useState(null);
    const [abierto, setAbierto] = useState(false);
    const [fecha, setFecha] = useState('');
    const [guardando, setGuardando] = useState(false);
    const cargar = useCallback(() => {
        api.get(`/admin/routines/rutina-del-mes/${clientId}`)
            .then(r => setDatos(r.data || null))
            .catch(e => { console.error('[fecha de rutina]', e); setDatos(null); });
    }, [api, clientId]);
    useEffect(() => { cargar(); }, [cargar]);
    if (!datos?.incluida) return null;

    const guardar = async (valor) => {
        setGuardando(true);
        try {
            await api.put(`/admin/routines/rutina-del-mes/${clientId}`, { fecha: valor || null });
            toast.success(valor ? 'Fecha de rutina guardada' : 'Vuelve a contar su pago');
            setAbierto(false);
            cargar();
        } catch (e) {
            toast.error(mensajeDeError(e, 'No se pudo guardar la fecha de rutina'));
        } finally {
            setGuardando(false);
        }
    };

    const ve = datos.ve;
    return (
        <div className="mt-4 border-t border-[#E5E2DB] pt-3" data-testid="fecha-de-rutina">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                    <p className="text-xs text-[#6F6B64] uppercase tracking-wider">Fecha de rutina</p>
                    <p className="text-sm text-[#1C1B19]" data-testid="fecha-de-rutina-texto">
                        {datos.dia_de_referencia ? fechaCorta(datos.dia_de_referencia) : 'sin fecha'}
                        {datos.fuente && <span className="text-[#6F6B64]"> · {FUENTE_DE_LA_FECHA[datos.fuente] || datos.fuente}</span>}
                    </p>
                    {ve && (
                        <p className="text-xs text-[#6F6B64]">
                            Ve la {ve.hueco === 'anterior' ? 'anterior' : 'actual'}{ve.nombre ? ` (${ve.nombre})` : ''}
                        </p>
                    )}
                </div>
                {esAdmin && (
                    <Button variant="outline" size="sm" data-testid="editar-fecha-de-rutina"
                        onClick={() => { setFecha(datos.fecha_de_rutina || datos.dia_de_referencia || ''); setAbierto(a => !a); }}>
                        {abierto ? 'Cerrar' : 'Cambiar'}
                    </Button>
                )}
            </div>
            {abierto && (
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <Input type="date" value={fecha} onChange={e => setFecha(e.target.value)}
                        className="w-auto" data-testid="fecha-de-rutina-input" />
                    <Button size="sm" disabled={guardando || !fecha} onClick={() => guardar(fecha)}
                        className="bg-[#FF671F] text-white" data-testid="guardar-fecha-de-rutina">
                        {guardando ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar'}
                    </Button>
                    {datos.fecha_de_rutina && (
                        <Button size="sm" variant="outline" disabled={guardando} onClick={() => guardar(null)}
                            data-testid="quitar-fecha-de-rutina">
                            Volver a su pago
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
};

const CicloDelCliente = ({ api, clientId, profile, onGuardado }) => {
    const cal = profile?.calendario_resuelto || {};
    const [abierto, setAbierto] = useState(false);
    const [semanas, setSemanas] = useState('');
    const [entrada, setEntrada] = useState('');
    const [patron, setPatron] = useState([]);
    const [patronTocado, setPatronTocado] = useState(false);
    const [guardando, setGuardando] = useState(false);

    // Al abrir se parte de lo que hay HOY resuelto (su contrato si lo tiene, y si no el de
    // su plan): así se ve de qué se parte en vez de un formulario en blanco.
    useEffect(() => {
        if (!abierto) return;
        setSemanas(profile?.ciclo_semanas != null ? String(profile.ciclo_semanas) : '');
        setEntrada(profile?.semana_de_entrada != null ? String(profile.semana_de_entrada) : '');
        setPatron([...(cal.patron || [])]);
        setPatronTocado(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [abierto, profile]);

    const propio = profile?.ciclo_semanas != null || profile?.semana_de_entrada != null
        || !!profile?.calendario_reportes;

    const guardar = async (volverAlPlan = false) => {
        setGuardando(true);
        try {
            const cuerpo = volverAlPlan
                ? { ciclo_semanas: null, semana_de_entrada: null, calendario_reportes: null }
                : {
                    ciclo_semanas: semanas.trim() === '' ? null : Number(semanas),
                    semana_de_entrada: entrada.trim() === '' ? null : Number(entrada),
                    ...(patronTocado ? { calendario_reportes: { ...(profile?.calendario_reportes || {}), patron } } : {}),
                };
            await api.put(`/admin/clients/${clientId}/ciclo`, cuerpo);
            toast.success(volverAlPlan ? 'Vuelve al ciclo de su plan' : 'Ciclo guardado');
            setAbierto(false);
            onGuardado?.();
        } catch (e) {
            toast.error(mensajeDeError(e, 'No se pudo guardar el ciclo'));
        } finally {
            setGuardando(false);
        }
    };

    return (
        <div className="mt-4 border-t border-[#E5E2DB] pt-3" data-testid="ciclo-del-cliente">
            <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                    <p className="text-xs text-[#6F6B64] uppercase tracking-wider">Su ciclo</p>
                    <p className="text-sm text-[#1C1B19]">
                        {cal.duracion_semanas ? `${cal.duracion_semanas} semanas` : 'sin ciclo declarado'}
                        {cal.semana_de_entrada ? ` · entra en la semana ${cal.semana_de_entrada}` : ''}
                        <span className="text-[#6F6B64]"> · {propio ? 'suyo, pisa al plan' : 'el de su plan'}</span>
                    </p>
                </div>
                <Button variant="outline" size="sm" onClick={() => setAbierto(a => !a)}
                    data-testid="editar-ciclo"
                    className="bg-transparent border-[#D6D2C9] text-[#1C1B19] hover:bg-[#F6F5F2] text-xs">
                    <CalendarClock className="w-3.5 h-3.5 mr-1" /> {abierto ? 'Cerrar' : 'Cambiar el ciclo'}
                </Button>
            </div>

            {abierto && (
                <div className="mt-3 space-y-3 bg-[#F6F5F2] border border-[#E5E2DB] rounded-lg p-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <Label className="text-[#57544E] text-xs">Semanas del ciclo</Label>
                            <Input value={semanas} onChange={e => setSemanas(e.target.value.replace(/\D/g, ''))}
                                inputMode="numeric" placeholder={cal.duracion_semanas ? String(cal.duracion_semanas) : '12'}
                                data-testid="ciclo-semanas"
                                className="bg-white border-[#D6D2C9] text-[#1C1B19] mt-1" />
                        </div>
                        <div>
                            <Label className="text-[#57544E] text-xs">Entra en la semana</Label>
                            <Input value={entrada} onChange={e => setEntrada(e.target.value.replace(/\D/g, ''))}
                                inputMode="numeric" placeholder="1"
                                data-testid="ciclo-entrada"
                                className="bg-white border-[#D6D2C9] text-[#1C1B19] mt-1" />
                        </div>
                    </div>
                    {patron.length > 0 && (
                        <div>
                            <Label className="text-[#57544E] text-xs">Qué reporte le toca cada semana</Label>
                            <div className="flex flex-wrap gap-2 mt-1">
                                {patron.map((t, i) => (
                                    <div key={i} className="flex items-center gap-1">
                                        <span className="text-[11px] text-[#6F6B64]">S{i + 1}</span>
                                        <select value={t || ''} data-testid={`ciclo-patron-${i}`}
                                            onChange={e => {
                                                const nuevo = [...patron];
                                                nuevo[i] = e.target.value;
                                                setPatron(nuevo);
                                                setPatronTocado(true);
                                            }}
                                            className="bg-white border border-[#D6D2C9] text-[#1C1B19] text-xs rounded-lg px-2 py-1">
                                            {TIPOS_DE_REPORTE.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                        </select>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    <div className="flex flex-wrap items-center gap-2">
                        <Button size="sm" onClick={() => guardar(false)} disabled={guardando}
                            data-testid="guardar-ciclo"
                            className="bg-[#FF671F] hover:bg-[#FF671F]/90 text-white text-xs">
                            {guardando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Guardar el ciclo'}
                        </Button>
                        {propio && (
                            <Button size="sm" variant="outline" onClick={() => guardar(true)} disabled={guardando}
                                className="bg-transparent border-[#D6D2C9] text-[#57544E] hover:bg-[#F6F5F2] text-xs">
                                Volver al ciclo de su plan
                            </Button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const PdfDeRutina = ({ api, clientId, onInfo }) => {
    const [info, setInfo] = useState(null);
    const [subiendo, setSubiendo] = useState(false);
    // LOS DOS DATOS QUE VAN CON EL PDF (tarea 7.1 del 21-08): el reparto de grupos por
    // día de entreno («Empuje, Tirón, Pierna, Empuje») y las semanas que dura. Los pone
    // el entrenador aquí y viajan con el PDF (db.rutina_pdfs): con ellos, la pantalla de
    // Rutina del cliente puede decir «Semana 3 de 8» y qué grupo toca cada día.
    const [reparto, setReparto] = useState('');
    const [semanas, setSemanas] = useState('');
    const [guardandoDetalles, setGuardandoDetalles] = useState(false);

    // `onInfo` le cuenta al padre lo que hay: el hueco de «Rutina actual» lo necesita
    // para no decir «Sin rutina asignada» cuando la rutina está entregada en PDF.
    const cargar = useCallback(() => {
        api.get(`/admin/routines/pdf/${clientId}/info`)
            .then(r => {
                setInfo(r.data); onInfo?.(r.data);
                setReparto((r.data?.reparto || []).join(', '));
                setSemanas(r.data?.semanas != null ? String(r.data.semanas) : '');
            })
            .catch(() => { setInfo(null); onInfo?.(null); });
    }, [api, clientId, onInfo]);
    useEffect(() => { cargar(); }, [cargar]);

    const subir = async (e) => {
        const f = e.target.files?.[0];
        e.target.value = '';
        if (!f) return;
        setSubiendo(true);
        try {
            const fd = new FormData();
            fd.append('file', f);
            // Los dos campos van en la misma subida: se rellenan antes de elegir el
            // archivo. Vacíos no pasan nada, el PDF se ve igual (pero la semana del
            // cliente no sabrá decir el grupo).
            if (reparto.trim()) fd.append('reparto', reparto.trim());
            if (semanas.trim()) fd.append('semanas', semanas.trim());
            await api.post(`/admin/routines/pdf/${clientId}`, fd);
            toast.success('Rutina en PDF subida');
            cargar();
        } catch (err) {
            toast.error(mensajeDeError(err, 'No se pudo subir el PDF'));
        } finally {
            setSubiendo(false);
        }
    };

    // Corregir el reparto o las semanas del PDF que ya está subido, sin resubirlo.
    const guardarDetalles = async () => {
        setGuardandoDetalles(true);
        try {
            await api.patch(`/admin/routines/pdf/${clientId}/detalles`,
                { reparto: reparto.trim(), semanas: semanas.trim() });
            toast.success('Reparto y semanas guardados');
            cargar();
        } catch (err) {
            toast.error(mensajeDeError(err, 'No se pudieron guardar los detalles'));
        } finally {
            setGuardandoDetalles(false);
        }
    };

    const ver = async () => {
        try {
            const r = await api.get(`/admin/routines/pdf/${clientId}`, { responseType: 'blob' });
            window.open(URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' })), '_blank');
        } catch { toast.error('No se pudo abrir el PDF'); }
    };

    return (
        <Card className="bg-white border-[#E5E2DB]" data-testid="pdf-rutina">
            <CardHeader className="pb-2"><CardTitle className="text-sm text-[#6F6B64] uppercase tracking-wider">Rutina en PDF</CardTitle></CardHeader>
            <CardContent className="space-y-3">
                {info?.hay ? (
                    <p className="text-xs text-[#57544E]">
                        {info.filename} · subida el {new Date(info.uploaded_at).toLocaleDateString('es-ES')}
                        <button onClick={ver} className="ml-2 text-[#B43E06] hover:underline" data-testid="ver-pdf-rutina">ver</button>
                    </p>
                ) : (
                    <p className="text-xs text-[#6F6B64]">Todavía no hay ninguna.</p>
                )}
                <div className="grid grid-cols-[1fr_92px] gap-2">
                    <div>
                        <Label className="text-[#57544E] text-xs">Reparto por día de entreno</Label>
                        <Input value={reparto} onChange={e => setReparto(e.target.value)}
                            placeholder="Empuje, Tirón, Pierna, Empuje"
                            className="bg-white border-[#D6D2C9] text-[#1C1B19] mt-1 text-xs" data-testid="pdf-rutina-reparto" />
                    </div>
                    <div>
                        <Label className="text-[#57544E] text-xs">Semanas</Label>
                        <Input type="number" min="1" max="52" value={semanas} onChange={e => setSemanas(e.target.value)}
                            placeholder="8"
                            className="bg-white border-[#D6D2C9] text-[#1C1B19] mt-1 text-xs" data-testid="pdf-rutina-semanas" />
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <label className="inline-flex items-center gap-2 text-xs text-[#1C1B19] bg-white border border-[#D6D2C9] rounded-lg px-3 py-2 cursor-pointer hover:border-[#FF671F]/50">
                        {subiendo ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                        {info?.hay ? 'Subir una nueva' : 'Subir PDF'}
                        <input type="file" accept="application/pdf" className="hidden" onChange={subir} data-testid="input-pdf-rutina" />
                    </label>
                    {info?.hay && (
                        <button onClick={guardarDetalles} disabled={guardandoDetalles} data-testid="pdf-rutina-guardar-detalles"
                            className="inline-flex items-center gap-2 text-xs text-[#1C1B19] bg-white border border-[#D6D2C9] rounded-lg px-3 py-2 hover:border-[#FF671F]/50 disabled:opacity-50">
                            {guardandoDetalles ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                            Guardar reparto y semanas
                        </button>
                    )}
                </div>
            </CardContent>
        </Card>
    );
};

const RutinasGuardadas = ({ api, clientId, onAsignada }) => {
    const [rutinas, setRutinas] = useState([]);
    const [poniendo, setPoniendo] = useState(null);

    useEffect(() => {
        api.get('/admin/routines/biblioteca')
            .then(r => setRutinas(r.data?.rutinas || []))
            .catch(() => { /* sin biblioteca, esta tarjeta no sale */ });
    }, [api]);

    const asignar = (r) => {
        setPoniendo(r.id);
        api.post(`/admin/routines/biblioteca/${r.id}/asignar`, { client_id: clientId })
            .then(() => { toast.success(`Le hemos puesto «${r.nombre}»`); onAsignada?.(); })
            .catch(e => toast.error(mensajeDeError(e, 'No hemos podido asignársela. Inténtalo de nuevo.')))
            .finally(() => setPoniendo(null));
    };

    if (!rutinas.length) return null;

    return (
        <Card className="bg-white border-[#E5E2DB]">
            <CardHeader className="pb-2">
                <CardTitle className="text-sm text-[#6F6B64] uppercase tracking-wider">Ponerle una rutina guardada</CardTitle>
            </CardHeader>
            <CardContent><div className="space-y-2">
                {rutinas.map(r => (
                    <div key={r.id} className="flex items-center justify-between gap-3 p-3 bg-[#F6F5F2] rounded-lg border border-[#E5E2DB]">
                        <div className="min-w-0">
                            <p className="text-[#1C1B19] text-sm">{r.nombre}</p>
                            <p className="text-[#6F6B64] text-xs">
                                {plural(r.dias_de_entreno, 'día')} · {plural(r.ejercicios, 'ejercicio')}
                                {r.objetivo ? ` · ${r.objetivo}` : ''}{r.nivel ? ` · ${r.nivel}` : ''}
                            </p>
                        </div>
                        <Button size="sm" variant="outline" disabled={poniendo === r.id}
                            onClick={() => asignar(r)} data-testid={`asignar-rutina-${r.id}`}
                            className="shrink-0 bg-transparent border-[#FF671F]/40 text-[#B43E06] hover:bg-[#FDEBDF] text-xs">
                            {poniendo === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Ponérsela'}
                        </Button>
                    </div>
                ))}
            </div></CardContent>
        </Card>
    );
};

// Las pestañas de la ficha que se enseñan (`lib/pantallasDelPanel`).
const TAB_CONFIG = pestanasDeLaFicha([
    { id: 'resumen', label: 'Resumen', icon: User },
    { id: 'macros', label: 'Macros', icon: Apple },
    { id: 'membresia', label: 'Membresía', icon: CreditCard },
    { id: 'cuestionario', label: 'Cuestionario inicial', icon: ClipboardList },
    { id: 'entrenamiento', label: 'Entreno', icon: Dumbbell },
    { id: 'nutricion', label: 'Nutrición', icon: Utensils },
    { id: 'menus', label: 'Menús', icon: ClipboardList },
    { id: 'suplementos', label: 'Suplementos', icon: Pill },
    { id: 'seguimiento', label: 'Reportes', icon: TrendingUp },
]);

const ClientDetailPage = () => {
    const { clientId } = useParams();
    const navigate = useNavigate();
    const { api, user: adminUser, planCatalog, actuarComo } = useAuth();
    const { confirm } = useConfirm();
    const [client, setClient] = useState(null);
    const [loading, setLoading] = useState(true);
    // Con la que pide la dirección (`?pestana=seguimiento`, el botón de los correos al
    // entrenador, 16-10); si no, Resumen.
    const location = useLocation();
    const [activeTab, setActiveTab] = useState(() => pestanaDeEntrada(location.search, TAB_CONFIG));

    // Macros: el editor vive en la propia pestaña (precargado con los macros
    // actuales). El modal solo se usa para editar/repetir entradas del historial.
    const [entryModalOpen, setEntryModalOpen] = useState(false);
    const [sugerencia, setSugerencia] = useState(null);
    // La serie del % graso de la app tal como la devolvió el último guardado (null: la de la
    // ficha). Vive aquí y no en cada casilla: Seguimiento tiene dos (Fotos de los mensuales y
    // Comparativa) y tienen que leer la misma, o una enseña el valor viejo que la otra cambió.
    const [grasaGuardada, setGrasaGuardada] = useState(null);
    // Y en cuanto la ficha se vuelve a leer, manda la ficha (un ajuste de macros con % graso
    // también escribe la serie).
    useEffect(() => { setGrasaGuardada(null); }, [clientId, client]);
    const [sugiriendo, setSugiriendo] = useState(false);
    const MACROS_FORM_VACIO = {
        training: { protein: '', carbs: '', fat: '' },
        rest: { protein: '', carbs: '', fat: '' },
        peri: { protein: '', carbs: '' },
        note: '',
        // Modelo predictivo (paso 1): criterio interno del coach y % graso del momento.
        criterio: '',
        porcentaje_graso: '',
        // El peso con el que se hace el ajuste: el último guardado con unos macros (20-10).
        peso: '',
        // Y la fecha en que se peso, que NO es la del ajuste (punto 27): se archiva ahi.
        peso_fecha: '',
        // La rutina de este ajuste: llega con la siguiente (`rutina_sugerida`, 20-10).
        rutina_numero: '',
        // MAÑANA (21-10): los macros nuevos empiezan al día siguiente de guardarlos.
        effective_date: mananaISO(),
    };
    const [macrosForm, setMacrosForm] = useState(MACROS_FORM_VACIO);
    const [entryForm, setEntryForm] = useState(MACROS_FORM_VACIO);
    const [savingMacros, setSavingMacros] = useState(false);
    // EL HISTÓRICO, PLEGADO (vídeo de Jesús, 20-10): arriba el botón «Mostrar histórico» y
    // debajo, siempre a la vista, el editor de los macros nuevos.
    const [verHistorico, setVerHistorico] = useState(false);
    // Lo ultimo que se guardo de verdad, para dejarlo escrito en pantalla (punto 28). El aviso
    // flotante se va solo a los tres segundos: si el coach mira despues, no sabe si guardo, y
    // entonces guarda otra vez (y el ajuste duplicado se le queda como el ultimo).
    const [ultimoGuardado, setUltimoGuardado] = useState(null);
    const [savingEntry, setSavingEntry] = useState(false);
    // Sugerencia de la IA que dio pie al ajuste que hay ahora en el editor (si la hubo).
    const [sugerenciaId, setSugerenciaId] = useState(null);
    const editorMacrosRef = useRef(null);
    const feedbackMacrosRef = useRef(null);

    // Routine
    const [generatingRoutine, setGeneratingRoutine] = useState(false);
    const [routineInstructions, setRoutineInstructions] = useState('');
    const [generatedRoutine, setGeneratedRoutine] = useState(null);
    // El info del PDF lo carga PdfDeRutina y lo sube aquí: sin él, el hueco de «Rutina
    // actual» decía «Sin rutina asignada» dos tarjetas por debajo del propio PDF.
    const [pdfRutina, setPdfRutina] = useState(null);

    // Suplementos
    // El protocolo, versionado por fecha (punto 33). `actual` y `siguiente` los resuelve el
    // backend por fecha; `versiones` es el histórico completo, que antes no existía.
    const [supProtocol, setSupProtocol] = useState({ actual: [], siguiente: [], actual_fecha: '', siguiente_fecha: '', nota: '', versiones: [] });
    // Pestaña de entrenamiento: maquinaria, lesiones y observaciones (2.5)
    const [entrenoForm, setEntrenoForm] = useState({ equipment: [], injuries: [], training_notes: '' });
    const [nuevaLesion, setNuevaLesion] = useState('');
    const [savingEntreno, setSavingEntreno] = useState(false);
    const [supCatalog, setSupCatalog] = useState([]);
    const [supSaving, setSupSaving] = useState(false);

    // Visor de dietas (pestaña Nutrición)
    const [selectedDietDate, setSelectedDietDate] = useState(null);
    const [selectedDiet, setSelectedDiet] = useState(null);
    const [dietLoading, setDietLoading] = useState(false);
    const openDiet = async (fecha) => {
        setSelectedDietDate(fecha); setSelectedDiet(null); setDietLoading(true);
        try {
            const r = await api.get(`/admin/clients/${clientId}/diet`, { params: { fecha } });
            setSelectedDiet(r.data);
        } catch {
            toast.error('No se pudo cargar la dieta de esa fecha');
        } finally {
            setDietLoading(false);
        }
    };

    // Asignación de coach
    const [trainers, setTrainers] = useState([]);
    // MIENTRAS SE GUARDA, SE VE (I8, 12-10). `assigningTrainer` lleva lo ELEGIDO ('none' o el
    // id), no un sí o no: el desplegable volvía al valor de antes, apagado y sin decir nada,
    // y quien salía de la ficha creyendo que no había pasado nada se iba sin el cambio hecho.
    const [assigningTrainer, setAssigningTrainer] = useState(false);
    const changeTrainer = async (value) => {
        setAssigningTrainer(value || 'none');
        try {
            const trainerId = value === 'none' ? null : value;
            await api.put(`/admin/clients/${clientId}/trainer`, { trainer_id: trainerId });
            toast.success(trainerId ? 'Entrenador asignado' : 'Entrenador quitado');
            // Se espera a la ficha recargada: sin esperar, el desplegable volvía al
            // entrenador de antes hasta que llegaba, y parecía que no se había guardado.
            await fetchClient();
        } catch (e) {
            toast.error(mensajeDeError(e, 'No se pudo cambiar el entrenador'));
        } finally { setAssigningTrainer(false); }
    };

    // "Asignármelo": si ya lo lleva otro entrenador, se dice quién y se pregunta antes.
    const asignarmelo = async () => {
        const trainerId = client?.profile?.trainer_id || null;
        const aviso = avisoAlAsignarmelo({
            cliente: client?.user?.name || client?.user?.email, entrenadorId: trainerId,
            entrenadorNombre: trainers.find(t => t.id === trainerId)?.name, miId: adminUser?.id,
        });
        if (aviso && !await confirm(aviso)) return;
        await changeTrainer(adminUser.id);
    };

    useEffect(() => { fetchClient(); }, [clientId]); // eslint-disable-line
    useEffect(() => { api.get('/admin/supplements/catalog').then(r => setSupCatalog(r.data || [])).catch(() => {}); }, []); // eslint-disable-line
    useEffect(() => { api.get('/admin/trainers').then(r => setTrainers(r.data || [])).catch(() => {}); }, []); // eslint-disable-line

    const fetchClient = async () => {
        try {
            const response = await api.get(`/admin/clients/${clientId}`);
            setClient(response.data);
            const sp = response.data.supplement_protocol;
            if (sp) setSupProtocol({
                actual: sp.actual || [], siguiente: sp.siguiente || [],
                actual_fecha: sp.actual_fecha || '', siguiente_fecha: sp.siguiente_fecha || '',
                nota: sp.nota || '', versiones: sp.versiones || [],
            });
            // El editor de la pestana Macros arranca siempre con los macros guardados
            // (mismo criterio que macrosActuales: 0 es un valor valido, no un hueco).
            const p = response.data.profile;
            const _v = (m, k1, k2) => { const v = m?.[k1] ?? m?.[k2]; return v == null ? '' : String(v); };
            // EL PESO DEL EDITOR ES EL DEL REPORTE MENSUAL ACTUAL (21-10), con el día de ese
            // reporte como fecha del pesaje; sin mensual con peso, el del último ajuste
            // (`lib/historialDeMacros.pesoParaElEditor`). Sin ninguno, vacío.
            const pesoHist = pesoParaElEditor(response.data.reports, response.data.macro_history, diaDeCalendario);
            setMacrosForm({
                training: { protein: _v(p?.macros_training, 'protein', 'proteinas'), carbs: _v(p?.macros_training, 'carbs', 'hidratos'), fat: _v(p?.macros_training, 'fat', 'grasas') },
                rest: { protein: _v(p?.macros_rest, 'protein', 'proteinas'), carbs: _v(p?.macros_rest, 'carbs', 'hidratos'), fat: _v(p?.macros_rest, 'fat', 'grasas') },
                peri: { protein: _v(p?.macros_periworkout, 'protein', 'proteinas'), carbs: _v(p?.macros_periworkout, 'carbs', 'hidratos') },
                note: '',
                criterio: '',
                porcentaje_graso: p?.body_fat != null ? String(p.body_fat) : '',
                peso: pesoHist ? String(pesoHist.peso) : '',
                peso_fecha: pesoHist?.fecha || '',
                // LA RUTINA DEL AJUSTE (vídeo de Jesús, 20-10): la decide el servidor
                // (`core/rutina_que_pasa_sola`); de momento, la misma en la que está.
                rutina_numero: response.data.rutina_sugerida?.siguiente || '',
                effective_date: mananaISO(),
            });
            setEntrenoForm({
                equipment: Array.isArray(p?.equipment) ? p.equipment : [],
                injuries: Array.isArray(p?.injuries) ? p.injuries : [],
                training_notes: p?.training_notes || '',
            });
        } catch (error) {
            // QUÉ HA PASADO, NO «ERROR AL CARGAR» (6-09). Se llega aquí sobre todo desde un
            // aviso del panel cuyo cliente ya no está: la ficha contestaba 404, salía una
            // frase técnica y te echaba a la lista sin decir por qué. El 404 tiene su propia
            // explicación porque es el caso frecuente y el único accionable.
            const s = error?.response?.status;
            toast.error(s === 404
                ? 'Esa ficha ya no existe. Puede que se borrara al cliente después de crearse el aviso.'
                : 'No hemos podido abrir la ficha. Inténtalo en un momento.');
            console.error('[ficha] no se pudo cargar', clientId, s || error);
            navigate('/admin/clients');
        } finally { setLoading(false); }
    };

    // Editar/eliminar/repetir entradas del historial de macros
    const [editingEntryId, setEditingEntryId] = useState(null);
    const _g = (m, a, b) => { const v = m?.[a] ?? m?.[b]; return v == null ? '' : v; };
    const macroFormFromEntry = (h, opts = {}) => ({
        training: { protein: _g(h.training, 'protein', 'proteinas'), carbs: _g(h.training, 'carbs', 'hidratos'), fat: _g(h.training, 'fat', 'grasas') },
        rest: { protein: _g(h.rest, 'protein', 'proteinas'), carbs: _g(h.rest, 'carbs', 'hidratos'), fat: _g(h.rest, 'fat', 'grasas') },
        peri: { protein: _g(h.peri || h.macros_periworkout, 'protein', 'proteinas'), carbs: _g(h.peri || h.macros_periworkout, 'carbs', 'hidratos') },
        effective_date: opts.today ? hoyISO() : (h.effective_date || hoyISO()),
        note: opts.note != null ? opts.note : (h.note || ''),
        criterio: h.criterio || '',
        porcentaje_graso: h.body_fat != null ? String(h.body_fat) : '',
    });
    // Agente de re-ajuste de macros: pide la sugerencia y, si el coach la acepta,
    // pre-rellena el editor de macros con la propuesta (vigente manana).
    const pedirSugerencia = async () => {
        setSugiriendo(true); setSugerencia(null);
        try {
            const r = await api.post(`/admin/clients/${clientId}/sugerir-ajuste`);
            if (r.data?.propuesta) setSugerencia(r.data);
            else toast.error('El asistente no devolvió una propuesta válida');
        } catch (e) { toast.error(mensajeDeError(e, 'No se pudo obtener la sugerencia')); }
        finally { setSugiriendo(false); }
    };
    // Vuelca la propuesta de la IA en el editor de la pestaña: el coach la ve
    // sobre los macros actuales, la retoca si quiere y guarda desde ahi.
    const usarSugerencia = () => {
        const p = sugerencia?.propuesta; if (!p) return;
        // Arrastramos el id: al guardar, el backend compara lo que propuso la IA con
        // lo que dejaste tu y registra la correccion.
        setSugerenciaId(sugerencia.sugerencia_id || null);
        setMacrosForm(prev => ({
            // La rutina no es cosa de la propuesta: se queda la que había.
            rutina_numero: prev.rutina_numero,
            training: { protein: p.entreno?.proteina ?? '', carbs: p.entreno?.hidratos ?? '', fat: p.entreno?.grasa ?? '' },
            rest: { protein: p.descanso?.proteina ?? '', carbs: p.descanso?.hidratos ?? '', fat: p.descanso?.grasa ?? '' },
            peri: { protein: p.perientreno?.proteina ?? '', carbs: p.perientreno?.hidratos ?? '' },
            // EL RAZONAMIENTO VA AL CRITERIO INTERNO, NO AL FEEDBACK DEL CLIENTE (punto 4.2).
            // Estaba al revés: se escribía en «Feedback para el cliente», que es lo que le
            // llega como novedad, y el criterio interno se quedaba vacío. Ese texto está
            // escrito para el entrenador -- habla del cliente en tercera persona y con jerga
            // ("venía sin intra", "el coach ya le había dejado contenido de hidrato") --, así
            // que el cliente recibía un informe sobre sí mismo escrito como si no lo fuera a
            // leer.
            // El feedback se deja EN BLANCO a propósito: es obligatorio para guardar, así que
            // el entrenador tiene que escribirlo. Rellenarlo con algo genérico solo
            // conseguiría que se enviara sin mirarlo.
            criterio: (sugerencia?.razonamiento || '').slice(0, 1000),
            note: '',
            effective_date: mananaISO(),
        }));
        toast.success('Propuesta cargada: el razonamiento va en el criterio interno. Escribe el feedback del cliente');
        editorMacrosRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    const openEditEntry = (h) => { setEditingEntryId(h.id); setEntryForm(macroFormFromEntry(h)); setEntryModalOpen(true); };
    const openRepeatEntry = (h) => {
        setEditingEntryId(null);
        // EL FEEDBACK SE ESCRIBE, NO VIENE PUESTO (12-10, F1). Venía relleno con «Repetición
        // de los macros del...», así que el obligatorio se cumplía solo y al cliente le
        // llegaba esa frase como si se la hubiera escrito su entrenador. El criterio interno
        // tampoco se arrastra: era el de la decisión de entonces, no el de hoy.
        setEntryForm({ ...macroFormFromEntry(h, { today: true, note: '' }), criterio: '' });
        setEntryModalOpen(true);
    };
    // Modelo predictivo (paso 1): evaluar como salio la fase que abrio un ajuste.
    const [evalEntry, setEvalEntry] = useState(null);   // entrada del historial en edicion
    const [evalForm, setEvalForm] = useState({ resultado: 'buena', causa: 'cliente', nota: '' });
    const [savingEval, setSavingEval] = useState(false);
    const openEvaluar = (h) => {
        const ev = h.evaluacion || {};
        setEvalForm({ resultado: ev.resultado || 'buena', causa: ev.causa || 'cliente', nota: ev.nota || '' });
        setEvalEntry(h);
    };
    const guardarEvaluacion = async () => {
        if (!evalEntry) return;
        setSavingEval(true);
        try {
            await api.put(`/admin/clients/${clientId}/macro-history/${evalEntry.id}/evaluacion`, {
                resultado: evalForm.resultado,
                causa: evalForm.resultado === 'mala' ? evalForm.causa : null,
                nota: evalForm.nota.trim() || null,
            });
            toast.success('Evaluación guardada');
            setEvalEntry(null);
            fetchClient();
        } catch (e) { toast.error(mensajeDeError(e, 'No se pudo guardar la evaluación')); }
        finally { setSavingEval(false); }
    };

    const deleteMacroEntry = async (h) => {
        if (!await confirm({
            title: '¿Eliminar esta entrada del historial?',
            // LO QUE PASA DE VERDAD (MAC-08, 14-10). Decía «No afecta a los macros que el
            // cliente tiene ahora», y no es así cuando la fila es la que rige: el cliente come
            // con lo que dice el historial, así que al borrarla vuelve al ajuste anterior.
            description: 'Se borra el registro de ese cambio. Si es el que manda hoy, el cliente vuelve al ajuste anterior; si no queda ninguno, sigue con los macros que hay en la ficha.',
            confirmLabel: 'Eliminar', danger: true,
        })) return;
        try { await api.delete(`/admin/clients/${clientId}/macro-history/${h.id}`); toast.success('Entrada eliminada'); fetchClient(); }
        catch { toast.error('No se pudo eliminar la entrada'); }
    };

    // Gestión de usuario (rol, plan cortesía, baja lógica) desde la ficha del cliente
    const [savingMgmt, setSavingMgmt] = useState(false);
    const changeUserRole = async (role) => {
        const uid = client?.user?.id; if (!uid) return;
        // PASAR A ALGUIEN AL EQUIPO SE PREGUNTA (12-10): era un clic en el desplegable y la
        // cuenta ya veía la bandeja común, los leads y las fichas de los clientes.
        if (role !== 'client' && role !== client.user.role) {
            const quien = client.user.name || client.user.email || 'esta cuenta';
            const esAdmin = role === 'admin';
            if (!await confirm({
                title: esAdmin ? '¿Hacer administrador a esta cuenta?' : '¿Hacer entrenador a esta cuenta?',
                description: esAdmin
                    ? `${quien} pasa a ver y cambiar todo el panel: clientes, cobros, planes, ajustes y equipo.`
                    : `${quien} pasa a entrar en el panel del equipo: ve los mensajes, los leads y las fichas de los clientes.`,
                confirmLabel: esAdmin ? 'Hacer administrador' : 'Hacer entrenador', danger: true,
            })) return;
        }
        setSavingMgmt(true);
        try { await api.put(`/admin/users/${uid}`, { role }); toast.success('Rol actualizado'); fetchClient(); }
        catch (e) { toast.error(mensajeDeError(e, 'Error al cambiar el rol')); }
        finally { setSavingMgmt(false); }
    };
    const setUserPlan = async (plan, comp) => {
        const uid = client?.user?.id; if (!uid) return;
        // CAMBIAR EL PLAN SE CONFIRMA (14-10): cambiaba al soltar el desplegable, y un plan es
        // el contrato del cliente (lo que ve, sus reportes y lo que se le cobra).
        if ((plan || '') !== (client?.profile?.plan || '')) {
            const nombreDe = (codigo) => planCatalog?.[codigo]?.name || codigo || 'Sin plan';
            if (!await confirm({
                title: `¿Cambiar el plan de ${client.user.name || client.user.email || 'este cliente'}?`,
                description: `Pasa de «${nombreDe(client?.profile?.plan)}» a «${nombreDe(plan)}». Cambia lo que ve en la app y lo que le toca.`,
                confirmLabel: 'Cambiar el plan',
            })) return;
        }
        try { await api.put(`/admin/users/${uid}`, { plan: plan || null, comp_plan: comp }); toast.success('Plan actualizado'); fetchClient(); }
        catch (e) { toast.error(mensajeDeError(e, 'No se pudo actualizar el plan')); }
    };
    // RESTABLECER LA CONTRASEÑA DESDE LA FICHA (punto 48 del doc del 24-08).
    //
    // El endpoint existía y funciona con clientes, pero no había botón en ninguna parte:
    // Usuarios solo lista al equipo, así que para dar acceso a un cliente que no puede
    // entrar no había ni una pantalla. La clave temporal se enseña UNA vez y se copia: el
    // servidor no la guarda en claro y no hay forma de volver a verla.
    //
    // Y AVISA ANTES: al cliente que vino de Calma le borra el hash heredado de Firebase
    // (admin.py), o sea que su clave de siempre deja de valer sin que él lo sepa. Ese aviso
    // es el motivo de que esto sea un diálogo y no un botón a secas.
    const [claveTemporal, setClaveTemporal] = useState(null);
    const restablecerClave = async () => {
        const uid = client?.user?.id; if (!uid) return;
        if (!await confirm({
            title: '¿Restablecer la contraseña?',
            description: 'Se genera una clave temporal nueva y se te enseña aquí para que se la pases tú. '
                + 'Ojo: la contraseña que tenía deja de funcionar en ese momento, también la de siempre '
                + 'si vino de la plataforma anterior. No se le avisa por correo.',
            confirmLabel: 'Restablecer', danger: true,
        })) return;
        try {
            const r = await api.post(`/admin/users/${uid}/reset-password`);
            setClaveTemporal(r.data?.temp_password || null);
        } catch (e) {
            toast.error(mensajeDeError(e, 'No se pudo restablecer la contraseña'));
        }
    };

    // ACTIVAR O DESACTIVAR AL CLIENTE (Francisco, 24-09). No es dar de baja la cuenta: el
    // cliente desactivado se queda sin acceso y fuera de la cartera, pero la cuenta y sus
    // datos siguen ahí, y se vuelve a activar con un toque.
    const alternarActivo = async () => {
        const pid = client?.profile?.id; if (!pid) return;
        const estaActivo = client.profile.status === 'activo';
        if (estaActivo && !await confirm({
            title: '¿Desactivar a este cliente?',
            description: 'Se queda sin acceso a la app y sale de la cartera del equipo. Su cuenta y sus datos se conservan, y lo puedes volver a activar cuando quieras.'
                + avisoDePausaAlDesactivar(client.profile),
            confirmLabel: 'Desactivar', danger: true,
        })) return;
        // UNA SOLA VEZ (14-10, BAJ-07): "Activar cliente" no pregunta, y con doble clic salían
        // dos peticiones y dos avisos. El mismo `ref` de "Reactivar usuario", hasta que la
        // ficha se ha vuelto a leer.
        if (reactivandoRef.current) return;
        reactivandoRef.current = true;
        try {
            await api.post('/admin/clients/estado', { ids: [pid], activo: !estaActivo });
            toast.success(estaActivo ? 'Cliente desactivado' : 'Cliente activado');
            await fetchClient();
        } catch (e) { toast.error(mensajeDeError(e, estaActivo ? 'No se pudo desactivar' : 'No se pudo activar')); }
        finally { reactivandoRef.current = false; }
    };

    // PAUSAR (Francisco, 24-09): mientras está en pausa sigue usando la app, pero la semana
    // no se le mueve. Al quitarla, las fechas se corren las semanas que duró (sigue donde
    // estaba y lo pagado acaba más tarde). Ver backend/core/pausa.py.
    const alternarPausa = async () => {
        const pid = client?.profile?.id; if (!pid) return;
        // Qué pausa es (la de siempre o la del catálogo, que se pide y tiene plazo) lo decide
        // lo que trae el detalle: lib/estadoDeLaPausa.
        const pausa = estadoDeLaPausa(client.profile);
        if (!await confirm(pausa.confirmar)) return;
        try {
            const r = await api.post(`/admin/clients/${pid}/pausa`, { pausar: pausa.pausar });
            toast.success(mensajeTrasLaPausa(pausa, r.data));
            fetchClient();
        } catch (e) {
            const rechazo = fraseDelRechazo(e, pausa.errorFijo);
            if (rechazo) console.error('[pausa]', e);
            toast.error(rechazo || mensajeDeError(e, pausa.errorFijo));
        }
    };

    const reactivandoRef = useRef(false);
    const toggleUserBaja = async () => {
        const uid = client?.user?.id; if (!uid) return;
        if (client.user.deleted_at) {
            // UNA SOLA VEZ (12-10): con doble clic salía dos veces, con dos avisos y dos líneas
            // en Actividad. El botón se apaga mientras responde Y hasta que la ficha se ha
            // vuelto a leer (tardaba y seguía ofreciendo "Reactivar usuario"); el `ref` cubre
            // el segundo clic que llega antes de que React apague el botón.
            if (reactivandoRef.current) return;
            reactivandoRef.current = true;
            setSavingMgmt(true);
            // Con el motivo del servidor, como los otros dos de esta tarjeta: este se quedó
            // atrás cuando se arreglaron los demás y «No se pudo reactivar» a secas no dice
            // si es un permiso, un usuario que ya no está o un fallo de red.
            try {
                await api.post(`/admin/users/${uid}/restore`);
                toast.success('Usuario reactivado', { id: 'usuario-reactivado' });
                await fetchClient();
            } catch (e) { toast.error(mensajeDeError(e, 'No se pudo reactivar')); }
            finally { reactivandoRef.current = false; setSavingMgmt(false); }
            return;
        }
        if (!await confirm({
            title: '¿Dar de baja a este usuario?',
            description: 'No podrá entrar, pero los datos se conservan y se puede reactivar cuando quieras.',
            confirmLabel: 'Dar de baja', danger: true,
        })) return;
        try { await api.delete(`/admin/users/${uid}`); toast.success('Usuario dado de baja'); fetchClient(); }
        catch (e) { toast.error(mensajeDeError(e, 'No se pudo dar de baja')); }
    };

    const macrosFormToBody = (f) => ({
        training: { protein: parseFloat(f.training.protein), carbs: parseFloat(f.training.carbs), fat: parseFloat(f.training.fat) },
        rest: { protein: parseFloat(f.rest.protein), carbs: parseFloat(f.rest.carbs), fat: parseFloat(f.rest.fat) },
        peri: { protein: parseFloat(f.peri.protein) || 0, carbs: parseFloat(f.peri.carbs) || 0 },
        note: f.note,
        criterio: (f.criterio || '').trim() || null,
        porcentaje_graso: f.porcentaje_graso === '' || f.porcentaje_graso == null ? null : parseFloat(f.porcentaje_graso),
        peso: f.peso === '' || f.peso == null ? null : parseFloat(f.peso),
        // La fecha del pesaje viaja aparte de la del ajuste (punto 27): el peso se archiva en
        // el dia en que se peso el cliente, no en el dia en que el coach mueve los macros.
        peso_fecha: f.peso_fecha || null,
        effective_date: f.effective_date,
        // La valida el servidor (`core/rutina_que_pasa_sola.rutina_escrita`).
        rutina_numero: String(f.rutina_numero ?? '').trim() || null,
    });
    const macrosFormIncompleto = (f) => ['training', 'rest'].some(
        b => ['protein', 'carbs', 'fat'].some(k => f[b][k] === '' || f[b][k] == null || isNaN(parseFloat(f[b][k])))
    );

    // Guarda los macros del cliente desde el editor de la pestaña.
    const handleSaveMacros = async () => {
        if (macrosFormIncompleto(macrosForm)) { toast.error('Completa proteína, hidratos y grasa de entrenamiento y descanso'); return; }
        const f = macrosForm;
        // Primero lo que está mal escrito y luego lo que falta (12-10, F8): con un peso
        // imposible y el feedback vacío salía antes «el feedback es obligatorio», y el aviso
        // del peso no aparecía hasta haber escrito el texto entero.
        if (f.peso) {
            const previo = revisarPeso(f.peso, pesoUltimoAjuste, { ajeno: true });
            if (!previo.ok) { toast.error(previo.error); return; }
        }
        // El % graso y la fecha, dichos a quien los escribe (MAC-22, 14-10). Sin esto llegaba
        // del servidor «Revisa tu porcentaje de grasa», y con la fecha vacía se leía
        // «Vigente desde el · peso 80 kg» y se guardaba con la de hoy sin decirlo.
        if (f.porcentaje_graso !== '' && f.porcentaje_graso != null
            && !datoDelPerfilUsable('body_fat', f.porcentaje_graso)) {
            toast.error(`El % graso tiene que estar entre ${RANGOS_PERFIL.body_fat[0]} y ${RANGOS_PERFIL.body_fat[1]}.`);
            return;
        }
        if (!f.effective_date) { toast.error('Pon la fecha desde la que valen estos macros.'); return; }
        if (!macrosForm.note.trim()) { toast.error('El feedback para el cliente es obligatorio'); return; }
        // EL PESO DEL AJUSTE TAMBIÉN ENTRA EN EL HISTÓRICO (#48 del 15-08: «77,1 → 94 → 75 →
        // 94 → 50 kg, saltos de 44 kg entre ajustes seguidos»). El campo ya no acepta
        // cualquier número, pero 94 y 50 son los dos pesos posibles: lo que canta es el
        // salto, y eso solo se ve comparando con el ajuste anterior.
        if (f.peso) {
            const chequeo = revisarPeso(f.peso, pesoUltimoAjuste, { ajeno: true });
            if (!chequeo.ok) { toast.error(chequeo.error); return; }
            if (chequeo.confirmar && !await confirm({
                title: 'Ese peso da un salto grande',
                description: `${chequeo.confirmar} Si es un dedazo, corrígelo: de este peso salen `
                    + 'la gráfica del cliente y el ritmo con el que se decide el próximo ajuste.',
                confirmLabel: 'Es correcto', cancelLabel: 'Lo corrijo',
            })) return;
        }
        // Confirmación antes de guardar (2.4): "a veces guardo por error y ya me salta como
        // último macro". Se resume lo que se va a guardar para poder repasarlo de un vistazo.
        // ¿Le cambia la rutina? Lo decide el servidor (`rutina_que_cambia`, 21-10); aquí solo
        // se anuncia, comparando con la que llevaba.
        const _rutinaEscrita = String(f.rutina_numero ?? '').trim();
        const _rutinaEra = client?.rutina_sugerida?.era;
        const rutinaQueCambia = /^\d+$/.test(_rutinaEscrita)
            && (_rutinaEra == null || Number(_rutinaEra) !== Number(_rutinaEscrita)) ? _rutinaEscrita : null;
        if (!await confirm({
            title: '¿Guardar estos macros?',
            // Más ancha (21-10): el botón lleva la fecha y en la de siempre no cabía.
            ancho: 'max-w-xl',
            // La excepción del cliente, la primera línea (punto 39): este es uno de los
            // momentos en que hay que acordarse de ella, y el coach está mirando aquí.
            description: (client?.profile?.excepcion ? `⚠ ${client.profile.excepcion}\n\n` : '')
                + `Entreno ${f.training.protein}/${f.training.carbs}/${f.training.fat} · `
                // «Perientreno», que es como se llama el bloque en el editor de arriba y en
                // el resto de la app (punto 4.18). Aquí decía «Intra», que además es solo uno
                // de sus cuatro modos: el coach confirmaba un número con el nombre de otra cosa.
                + `Perientreno ${f.peri.protein || 0}/${f.peri.carbs || 0} · `
                + `Descanso ${f.rest.protein}/${f.rest.carbs}/${f.rest.fat}`
                + `\nFecha de asignación de macros: ${_fechaLarga(f.effective_date)}`
                + (f.peso ? ` · peso ${kilos(f.peso)} kg${f.peso_fecha ? ` del ${_fechaCorta(f.peso_fecha)}` : ' puesto a mano'}` : '')
                + (String(f.rutina_numero ?? '').trim() ? `\nRutina ${String(f.rutina_numero).trim()}` : '')
                + (mismosNumeros
                    ? (rutinaQueCambia
                        ? `\nSon los mismos números que tiene: solo le llega el aviso de la rutina ${rutinaQueCambia}; el feedback va a su último reporte si aún no tiene uno.`
                        : '\nSon los mismos números que tiene: no le llega aviso; el feedback va a su último reporte si aún no tiene uno.')
                    : (rutinaQueCambia ? `\nEl aviso dice que pasa a la rutina ${rutinaQueCambia}.` : '')),
            confirmLabel: f.effective_date ? `Guardar macros para el ${_fechaLarga(f.effective_date)}` : 'Guardar macros',
        })) return;
        setSavingMacros(true);
        try {
            // Y SI ENTRENO Y DESCANSO SALEN IGUALES LLEVANDO PERI, SE DICE (Jesús, 11-09,
            // punto 12). No se bloquea -- el que los pone manda --, pero el aviso está, que
            // es lo que faltaba: hay diez clientes con los dos bloques idénticos.
            const r = await api.put(`/admin/clients/${clientId}/macros`, { ...macrosFormToBody(macrosForm), sugerencia_id: sugerenciaId });
            if (r?.data?.aviso) toast.warning(r.data.aviso, { duration: 12000 });
            else toast.success('Macros actualizados');
            // Y ademas queda escrito, con la hora (punto 28).
            setUltimoGuardado({
                // La hora de España, como el resto del panel (12-10): con la del navegador,
                // la banda decía una hora y el "Vigente desde" contaba con otro reloj.
                hora: horaEnEspana(ahoraDeLaApp()),
                desde: f.effective_date,
                peso: f.peso || null,
                pesoFecha: f.peso_fecha || null,
            });
            setSugerencia(null);
            setSugerenciaId(null);
            fetchClient();
        } catch (error) { toast.error(mensajeDeError(error, 'Error al guardar')); }
        finally { setSavingMacros(false); }
    };

    // Guarda una entrada del historial (editar) o la reaplica como macros actuales (repetir).
    const handleSaveEntry = async () => {
        if (macrosFormIncompleto(entryForm)) { toast.error('Completa proteína, hidratos y grasa de entrenamiento y descanso'); return; }
        if (!editingEntryId && !entryForm.note.trim()) { toast.error('El motivo es obligatorio: escribe el feedback que le llega al cliente con estos macros'); return; }
        setSavingEntry(true);
        try {
            const body = macrosFormToBody(entryForm);
            if (editingEntryId) {
                await api.put(`/admin/clients/${clientId}/macro-history/${editingEntryId}`, body);
                toast.success('Entrada del historial actualizada');
            } else {
                await api.put(`/admin/clients/${clientId}/macros`, body);
                toast.success('Macros actualizados');
            }
            setEntryModalOpen(false);
            setEditingEntryId(null);
            fetchClient();
        } catch (error) { toast.error(mensajeDeError(error, 'Error al guardar')); }
        finally { setSavingEntry(false); }
    };

    const handleGenerateRoutine = async () => {
        setGeneratingRoutine(true);
        try {
            const response = await api.post('/admin/routines/generate', { client_id: clientId, instructions: routineInstructions });
            setGeneratedRoutine(response.data.routine);
            toast.success('Rutina generada con IA');
        } catch (error) { toast.error('Error al generar rutina'); }
        finally { setGeneratingRoutine(false); }
    };

    // Con su «Guardando...» y recargando SIEMPRE la ficha al acabar (12-10, G2): el botón no
    // decía nada mientras guardaba, y si la respuesta fallaba con la rutina ya guardada, la
    // «Rutina actual» seguía enseñando la vieja hasta recargar a mano.
    const [savingRoutine, setSavingRoutine] = useState(false);
    const handleSaveRoutine = async () => {
        if (!generatedRoutine || savingRoutine) return;
        setSavingRoutine(true);
        try {
            await api.post(`/admin/routines/save?client_id=${clientId}`, generatedRoutine);
            toast.success('Rutina guardada: ya es la rutina actual del cliente');
            setGeneratedRoutine(null);
        } catch (error) {
            console.error('[rutina de IA] no se pudo guardar', error);
            toast.error(mensajeDeError(error, 'No hemos podido guardar la rutina. Inténtalo de nuevo.'));
        } finally {
            await fetchClient();
            setSavingRoutine(false);
        }
    };

    // ── Suplementos ──
    // `comida` viaja con lo demás (punto 174 del 27-08): la línea del cliente es una copia de
    // la ficha, y si no se copiara, el suplemento pautado hoy saldría en otra comida que el
    // mismo suplemento pautado mañana. Vacío significa «que lo deduzca del ¿Cuándo?», que es
    // lo normal, y el `catalog_id` deja que la ficha siga mandando si algún día se cambia.
    const catalogToItem = (c) => ({ catalog_id: c.id, titulo: c.titulo, imagen: c.imagen, enlaces: c.enlaces || [], cuando: c.cuando || '', cuanto: c.cuanto || '', observaciones: c.observaciones || '', comida: c.comida || '' });
    const supAdd = (bloque, catId) => {
        const c = supCatalog.find(x => x.id === catId);
        if (!c) return;
        setSupProtocol(prev => ({ ...prev, [bloque]: [...prev[bloque], catalogToItem(c)] }));
    };
    const supRemove = (bloque, idx) => setSupProtocol(prev => ({ ...prev, [bloque]: prev[bloque].filter((_, i) => i !== idx) }));
    // Editar un suplemento ya puesto (2.7): dosis, momento u observaciones.
    // Los dos campos que deciden en qué comida sale (ver lib/comidaDelSuplemento). Al tocar
    // cualquiera de ellos, lo que dice la línea de abajo se ha quedado viejo: lo calculó el
    // servidor con los datos de antes. Se borra para que diga «guarda para verlo» en vez de
    // enseñar una comida que ya no es la suya.
    const CAMBIAN_LA_COMIDA = ['comida', 'cuando'];
    const supEdit = (bloque, idx, campo, valor) => setSupProtocol(prev => ({
        ...prev,
        [bloque]: prev[bloque].map((it, i) => {
            if (i !== idx) return it;
            const nuevo = { ...it, [campo]: valor };
            if (CAMBIAN_LA_COMIDA.includes(campo)) delete nuevo.en_comidas;
            return nuevo;
        }),
    }));
    // Maquinaria, lesiones y observaciones del entrenamiento (2.5)
    const guardarEntreno = async () => {
        setSavingEntreno(true);
        try {
            await api.put(`/admin/clients/${clientId}`, {
                equipment: entrenoForm.equipment,
                injuries: entrenoForm.injuries,
                training_notes: entrenoForm.training_notes || '',
            });
            toast.success('Entrenamiento actualizado');
            fetchClient();
        } catch (e) { toast.error('No se pudo guardar'); }
        finally { setSavingEntreno(false); }
    };

    // `cambios` pisa lo que hay en pantalla solo para este guardado: la pestaña manda la fecha
    // de la siguiente que enseña aunque no se haya tocado (el lunes que viene por defecto).
    const supSave = async (cambios = {}) => {
        const datos = { ...supProtocol, ...cambios };
        // Misma confirmación que en macros (2.4): "lo mismo al guardar la suplementación".
        const n = (datos.actual || []).length, s = (datos.siguiente || []).length;
        if (!await confirm({
            title: '¿Guardar la suplementación?',
            description: (client?.profile?.excepcion ? `⚠ ${client.profile.excepcion}\n\n` : '')
                + `${n} suplemento${n === 1 ? '' : 's'} ahora`
                + (s ? ` y ${s} para el siguiente protocolo${datos.siguiente_fecha ? ` (desde el ${_fechaLarga(datos.siguiente_fecha)})` : ''}` : '')
                + '. El cliente lo ve en su apartado de suplementación.',
            confirmLabel: 'Guardar',
        })) return;
        setSupSaving(true);
        try {
            await api.post(`/admin/supplements/save?client_id=${clientId}`, {
                actual: datos.actual, siguiente: datos.siguiente,
                // Desde cuándo aplica cada bloque (punto 33): son dos versiones fechadas.
                actual_fecha: datos.actual_fecha || null,
                siguiente_fecha: datos.siguiente_fecha || null, nota: datos.nota || null,
            });
            toast.success('Suplementación guardada');
            fetchClient();
        } catch (e) { toast.error('Error al guardar suplementación'); }
        finally { setSupSaving(false); }
    };

    // La excepción del cliente (punto 39). Texto libre y vacío = quitarla.
    const [guardandoExcepcion, setGuardandoExcepcion] = useState(false);
    const guardarExcepcion = async (texto) => {
        setGuardandoExcepcion(true);
        try {
            await api.put(`/admin/clients/${clientId}`, { excepcion: texto });
            toast.success(texto.trim() ? 'Excepción guardada' : 'Excepción quitada');
            fetchClient();
        } catch (e) { toast.error(mensajeDeError(e, 'No se pudo guardar la excepción')); }
        finally { setGuardandoExcepcion(false); }
    };

    // Borrar una versión del histórico del protocolo (punto 33).
    const supBorrarVersion = async (fecha) => {
        if (!await confirm({
            title: `¿Borrar el protocolo del ${_fechaCorta(fecha)}?`,
            description: 'Se borra ese registro del histórico. El resto de versiones se queda.',
            confirmLabel: 'Borrar', destructive: true,
        })) return;
        try {
            await api.delete(`/admin/supplements/version/${fecha}?client_id=${clientId}`);
            toast.success('Versión borrada');
            fetchClient();
        } catch (e) { toast.error(mensajeDeError(e, 'No se pudo borrar')); }
    };

    if (loading) return <div className="p-6 bg-white [color-scheme:light] min-h-screen"><div className="animate-pulse space-y-4"><div className="h-8 bg-[#EFECE6] rounded w-1/4" /><div className="h-48 bg-white rounded-xl" /></div></div>;
    if (!client) return <div className="p-6 bg-white [color-scheme:light] min-h-screen text-center text-[#57544E]">Cliente no encontrado</div>;

    const { profile, user, routines, reports, payments, macro_history, nutrition_stats, calma_raw, acceso, rutina_puesta } = client;
    const pausaDeLaFicha = estadoDeLaPausa(profile);
    const mt = profile?.macros_training;
    const mr = profile?.macros_rest;
    const mp = profile?.macros_periworkout;
    const activeRoutine = routines?.find(r => r.status === 'active');
    // EL PDF ENTREGADO, PARA EL RESUMEN Y PARA ENTRENO (4-09, punto 80 del artefacto «La
    // app, pantalla por pantalla»; Gonzalo: «la ficha dice Sin rutina y en la pestaña
    // Entreno está la rutina 152 en PDF»). Lo dice el backend con la misma función que el
    // panel (`rutina_puesta`), así el Resumen lo sabe sin haber pasado por Entreno; y si
    // en esta sesión se sube o se quita uno desde Entreno, manda lo que diga esa pestaña.
    const pdfEntregado = pdfRutina
        ? (pdfRutina.hay ? pdfRutina : null)
        : (rutina_puesta?.en_pdf ? { uploaded_at: rutina_puesta.pdf_uploaded_at } : null);

    // Macros guardados ahora mismo, en el mismo formato que el formulario: sirven
    // para precargar el editor, marcar lo que el coach ha tocado y poder descartar.
    const _cur = (m, k1, k2) => { const v = m?.[k1] ?? m?.[k2]; return v == null ? '' : String(v); };
    const macrosActuales = {
        training: { protein: _cur(mt, 'protein', 'proteinas'), carbs: _cur(mt, 'carbs', 'hidratos'), fat: _cur(mt, 'fat', 'grasas') },
        rest: { protein: _cur(mr, 'protein', 'proteinas'), carbs: _cur(mr, 'carbs', 'hidratos'), fat: _cur(mr, 'fat', 'grasas') },
        peri: { protein: _cur(mp, 'protein', 'proteinas'), carbs: _cur(mp, 'carbs', 'hidratos') },
    };
    const bfActual = profile?.body_fat != null ? String(profile.body_fat) : '';

    // El peso del último ajuste (contra el que se compara) y el que trae el editor: el del
    // reporte mensual actual o, sin él, ese mismo (`lib/historialDeMacros`, 21-10).
    const pesoDelHistorial = ultimoPesoDelHistorial(macro_history);
    const pesoDelEditor = pesoParaElEditor(reports, macro_history, diaDeCalendario);
    // El % graso por fecha para las dos casillas de Seguimiento: la serie de Calma y la de
    // la app (la que se acaba de guardar si la hay). Una fecha de la app pisa a la de Calma.
    const grasaPorFecha = {};
    [...(calma_raw?.porcentajes_grasos || []), ...(grasaGuardada || profile?.porcentajes_grasos || [])]
        .forEach(g => { if (g?.fecha) grasaPorFecha[String(g.fecha).slice(0, 10)] = g.valor; });
    const pesoActual = pesoDelEditor ? String(pesoDelEditor.peso) : '';
    // La fecha del pesaje que se guardará con ese peso (punto 27).
    const pesoFechaActual = pesoDelEditor?.fecha || '';
    // ¿Se ha tocado algo de la pestaña de entrenamiento respecto a lo guardado?
    const _mismo = (a, b) => JSON.stringify([...(a || [])].sort()) === JSON.stringify([...(b || [])].sort());
    const entrenoTocado = !_mismo(entrenoForm.equipment, profile?.equipment)
        || !_mismo(entrenoForm.injuries, profile?.injuries)
        || (entrenoForm.training_notes || '') !== (profile?.training_notes || '');
    // Peso con el que se hizo el ajuste anterior: es contra el que compara el coach.
    // Sin useMemo a propósito: aquí ya se ha pasado por los early returns del componente
    // y un hook a estas alturas rompe las reglas de hooks (y el orden entre renders).
    const pesoUltimoAjuste = pesoDelHistorial?.peso ?? null;
    // Los mismos números que tiene: guardarlos cuenta como macros puestos (19-10), pero el
    // servidor no le manda aviso (`avisar_macros` con `sin_cambios`, «Seguimos igual» se
    // quitó el 17-10) y el feedback solo va a su último reporte si aún no tiene uno.
    const mismosNumeros = !['training', 'rest', 'peri'].some(
        b => Object.keys(macrosActuales[b]).some(k => String(macrosForm[b][k] ?? '') !== macrosActuales[b][k]));
    // La rutina con la que llega el editor (`core/rutina_que_pasa_sola`).
    const rutinaSugerida = client.rutina_sugerida || null;
    const rutinaActual = rutinaSugerida?.siguiente || '';
    const macrosTocados = !mismosNumeros || String(macrosForm.porcentaje_graso ?? '') !== bfActual
      || String(macrosForm.peso ?? '') !== pesoActual
      || String(macrosForm.rutina_numero ?? '') !== rutinaActual;
    const setMacroCampo = (bloque, campo, valor) => setMacrosForm(prev => ({ ...prev, [bloque]: { ...prev[bloque], [campo]: valor } }));
    const descartarCambiosMacros = () => (setSugerenciaId(null), setMacrosForm({
        ...macrosActuales, note: '', criterio: '', porcentaje_graso: bfActual, peso: pesoActual,
        peso_fecha: pesoFechaActual, effective_date: mananaISO(), rutina_numero: rutinaActual,
    }));

    // LA PESTAÑA "ENTRENO" NO SE ENSEÑA DE MOMENTO (13-10, lo pidió el equipo: las rutinas
    // se llevan fuera del panel). Lo decide `lib/pantallasDelPanel`; su contenido sigue
    // escrito más abajo (TabsContent "entrenamiento") y vuelve cambiando un valor allí.
    // Nada de esta ficha salta a esa pestaña, así que no queda ningún enlace sin destino.
    return (
        <div className="p-4 md:p-6 space-y-5 animate-fade-in bg-[#EFEDE8] [color-scheme:light] tema-claro text-[#1C1B19] min-h-screen" data-testid="client-detail">
            {/* Header */}
            <div className="flex items-center gap-3">
                <Button variant="ghost" size="icon" onClick={() => navigate('/admin/clients')} className="text-[#57544E] hover:text-[#1C1B19]"><ArrowLeft className="w-5 h-5" /></Button>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h1 className="text-2xl font-bold text-[#1C1B19] truncate" style={{ fontFamily: 'Barlow Condensed' }}>{user?.name?.toUpperCase()}</h1>
                        <PlanBadge plan={profile?.plan} />
                        {/* El estado con palabras: «pendiente_pago» es el código de la base (#64). */}
                        {(() => {
                            /* El mismo estado que ve él al entrar, no la etiqueta del perfil. */
                            const e = estadoDeAcceso({ ...(profile || {}), acceso });
                            return <Badge className={`${e.tono === 'ok' ? 'bg-[#DCFCE7] text-[#15803D]'
                                : e.tono === 'aviso' ? 'bg-[#FEF3C7] text-[#854D0E]'
                                : 'bg-[#FEE2E2] text-[#B91C1C]'} border-0`}>{e.texto}</Badge>;
                        })()}
                    </div>
                    <p className="text-[#6F6B64] text-sm truncate">{user?.email}</p>
                </div>
            </div>

            {/* LA EXCEPCIÓN (punto 39). Va aquí arriba, antes de las pestañas y sin poder
                plegarse: si hay que abrir algo para verla, ya está tan escondida como en la
                hoja de la que viene. Esto le costó dinero a Jesús: se le cobró una renovación
                a una clienta a la que le había perdonado un mes, porque la excepción solo
                estaba en su cabeza. */}
            <ExcepcionDelCliente
                excepcion={profile?.excepcion}
                guardando={guardandoExcepcion}
                onGuardar={guardarExcepcion} />

            {/* 8 Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <div className="overflow-x-auto -mx-4 px-4">
                    <TabsList className="inline-flex w-auto min-w-full bg-[#F6F5F2] border border-[#E5E2DB] p-1 rounded-xl gap-0.5">
                        {TAB_CONFIG.map(t => (
                            <TabsTrigger key={t.id} value={t.id} className="text-xs px-3 py-2 data-[state=active]:bg-[#FF671F] data-[state=active]:text-white data-[state=active]:shadow-none text-[#57544E] hover:text-[#1C1B19] rounded-lg whitespace-nowrap" data-testid={`tab-${t.id}`}>
                                <t.icon className="w-3.5 h-3.5 mr-1.5" />{t.label}
                            </TabsTrigger>
                        ))}
                    </TabsList>
                </div>

                {/* ========== TAB 1: RESUMEN ========== */}
                <TabsContent value="resumen">
                    <Card className="bg-white border-[#E5E2DB]"><CardContent className="p-5">
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            <InfoItem icon={User} label="Nombre" value={user?.name} />
                            <InfoItem icon={Mail} label="Email" value={user?.email} />
                            <InfoItem icon={Phone} label="Teléfono" value={user?.phone || '-'} />
                            <InfoItem icon={Shield} label="Plan" value={<PlanBadge plan={profile?.plan} planName={planCatalog?.[profile?.plan]?.name} />} />
                            <InfoItem icon={Activity} label="Estado" value={estadoClienteLabel(profile?.status)} />
                            <InfoItem icon={Calendar} label="Semana" value={(() => {
                                const sem = planCatalog?.[profile?.plan]?.ciclo?.semanas;
                                // La semana 0 existe (ha pagado y su lunes no ha llegado): con `|| 1` la
                                // ficha decía «1/12» de quien el listado y su perfil dan en la 0.
                                const semana = profile?.week ?? 1;
                                // El plan de duración fija que se acabó sin renovar se queda en
                                // su última semana y se dice, como en el Reloj (12-10).
                                const vencido = profile?.plan_vencido ? ' · Plan vencido' : '';
                                return sem ? `${semana}/${sem}${vencido}` : `${semana}${vencido}`;
                            })()} />
                            {/* Lo que le corresponde por plan: si le toca entrenador y cada
                                cuánto se le escribe. Sale del catálogo, no de este cliente. */}
                            <InfoItem icon={Headphones} label="Acompañamiento"
                                value={etiquetaAcompanamiento(planCatalog?.[profile?.plan]?.habilitaciones?.acompanamiento)} />
                            <InfoItem icon={CalendarClock} label="Contacto"
                                value={etiquetaFrecuencia(planCatalog?.[profile?.plan]?.habilitaciones?.frecuencia_contacto)} />
                            <InfoItem icon={Dumbbell} label="Entrenador" value={(() => {
                                const trainerId = profile?.trainer_id || null;
                                const trainerName = trainers.find(t => t.id === trainerId)?.name || trainerId;
                                // QUÉ PUEDE HACER QUIEN MIRA (12-10, Francisco; `lib/asignarEntrenador`):
                                // el entrenador se lo asigna a sí mismo, tenga o no otro entrenador
                                // (con otro, se pregunta antes); pasárselo a otro o quitarlo es del
                                // administrador, que es quien ve el desplegable.
                                const accion = accionDeEntrenador({ rol: adminUser?.role, miId: adminUser?.id, entrenadorId: trainerId });
                                if (accion === 'mio') return trainerName;
                                if (accion === 'asignarmelo') return (
                                    <span className="inline-flex items-center gap-2 flex-wrap">
                                        {trainerId && <span>{trainerName}</span>}
                                        <Button size="sm" disabled={!!assigningTrainer} onClick={asignarmelo}
                                            className="bg-[#FF671F] hover:bg-[#FF671F]/90 text-white text-xs h-7 px-2" data-testid="assign-me-trainer">
                                            {assigningTrainer ? 'Guardando…' : 'Asignármelo'}
                                        </Button>
                                    </span>
                                );
                                if (accion !== 'elegir') return trainerName || '-';
                                // El administrador: puede asignar, traspasar o quitar
                                return (
                                    <span className="inline-flex items-center gap-2">
                                        <select value={assigningTrainer || trainerId || 'none'} disabled={!!assigningTrainer} onChange={e => changeTrainer(e.target.value)}
                                            className="bg-white border border-[#D6D2C9] text-[#1C1B19] text-sm rounded-lg px-2 py-1 disabled:opacity-60" data-testid="trainer-select">
                                            <option value="none">Sin asignar</option>
                                            {trainerId && !trainers.some(t => t.id === trainerId) && <option value={trainerId}>{trainerName}</option>}
                                            {trainers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                        </select>
                                        {assigningTrainer && (
                                            <span className="text-xs text-[#57544E]" data-testid="trainer-guardando">Guardando…</span>
                                        )}
                                    </span>
                                );
                            })()} />
                            {/* «EN PDF» CUANDO LA TIENE ENTREGADA EN PDF (punto 80, 4-09). Esto miraba
                                solo las estructuradas, y a Montalvo, con su rutina-152.pdf abierta por
                                el cliente, le ponía «Sin rutina». Las mismas tres palabras que la lista
                                de Rutinas: Activa (con sus días) / En PDF / Sin rutina. */}
                            <InfoItem icon={Target} label="Rutina" value={<span data-testid="resumen-rutina">{activeRoutine ? plural(activeRoutine.days?.filter(d => !d.is_rest).length || 0, 'día') : pdfEntregado ? 'En PDF' : rutina_puesta?.estado === 'del_mes' ? 'La del mes' : 'Sin rutina'}</span>} />
                            <InfoItem icon={CreditCard} label="Próx. cobro" value={pintarDia(profile?.next_payment, { day: 'numeric', month: 'short' }, '-')} />
                            {/* «ALTA» Y «INICIO DEL CICLO» SON DOS COSAS (2-09). Esto ponía
                                «Inicio» y pintaba `created_at`, justo al lado de un «Semana
                                4/12» que se cuenta desde `cycle_start`. En la ficha de
                                Montalvo se leía «INICIO 5/7/2026 · SEMANA 4/12», y del 5 de
                                julio a hoy van nueve semanas, no cuatro: la tarjeta se
                                contradecía sola porque enseñaba un anclaje y contaba con
                                otro. Ahora se dicen los dos, con su nombre. */}
                            <InfoItem icon={Calendar} label="Alta" value={pintarDia(profile?.created_at, undefined, '-')} />
                            <InfoItem icon={Calendar} label="Inicio del ciclo"
                                value={pintarDia(profile?.cycle_start, undefined, '-')} />
                            {/* Punto 30: el peso, con su fecha. "94 kg · hace 3 días". El
                                número es el último de la serie; si el cliente es antiguo y
                                no tiene serie todavía, se cae al campo de la ficha. */}
                            <InfoItem icon={Scale} label="Peso" value={(() => {
                                const p = pesoActualDeLaFicha(profile);
                                if (!p) return '-';
                                return p.fecha
                                    ? <>{kilos(p.peso)} kg <span className="text-[#6F6B64] font-normal">· {_haceCuanto(p.fecha)}</span></>
                                    : `${kilos(p.peso)} kg`;
                            })()} />
                            <InfoItem icon={Scale} label="% graso" value={(() => {
                                const g = ultimoDeLaSerie(profile?.porcentajes_grasos);
                                if (g) return <>{g.valor}% <span className="text-[#6F6B64] font-normal">· {_haceCuanto(g.fecha)}</span></>;
                                return profile?.body_fat != null ? `${profile.body_fat}%` : '-';
                            })()} />
                        </div>

                        {/* EL OBJETIVO LO PONE EL ENTRENADOR (doc de Jesús del 2-09, fase 2;
                            Francisco, 4-09). Aquí se leía `goal` a secas, que venía del
                            cuestionario y el cliente reescribía cada mes desde el reporte.
                            Ahora son tres campos que se guardan desde aquí: el objetivo del
                            ciclo, el actual y el foco. */}
                        <ObjetivoDelCliente api={api} clientId={clientId} profile={profile}
                            ciclo={profile?.ciclo_actual || client?.ciclo_actual} onGuardado={fetchClient} />

                        {/* LO QUE LLEVA Y LO QUE NO (Francisco, 25-08). Antes había que
                            saberse el catálogo de memoria para responder a las dos preguntas
                            de cada día: ¿a este le subo rutina?, ¿le toco yo los macros? El
                            plan salía escrito arriba y nada más. Se pinta lo que NO lleva
                            también, apagado: una lista de la que falta la mitad se lee como
                            que lo lleva todo. */}
                        <LoQueLleva plan={planDelCatalogo(planCatalog, profile?.plan)} />
                        <CompradoAparte profile={profile} />
                    </CardContent></Card>

                    {/* LAS TAREAS SOBRE ESTE CLIENTE (doc 19-08, apartado 05). El registro
                        que sustituye al WhatsApp: qué se le pidió, quién lo hizo y cuándo:
                        «el mes que viene, al abrir esta ficha, se ve que se le contactó». */}
                    <TareasDelCliente api={api} clientId={clientId}
                        nombre={user?.name || user?.email} />
                </TabsContent>

                {/* ========== TAB 2: MACROS ========== */}
                <TabsContent value="macros" className="space-y-4">
                    {/* EL HISTÓRICO VA ARRIBA Y PLEGADO (vídeo de Jesús, 20-10): «Mostrar
                        histórico» lo despliega y debajo queda, siempre a la vista, el editor de
                        los macros nuevos. Se sigue comparando con la escalera anterior (vídeo del
                        05-08): la fila que se está escribiendo sale en gris hasta que se guarda. */}
                    <div className="flex flex-col gap-3">
                        <Button onClick={() => setVerHistorico(v => !v)} aria-expanded={verHistorico}
                            className="self-start bg-[#FF671F] hover:bg-[#FF671F]/90 text-white" data-testid="ver-historico-macros">
                            <History className="w-4 h-4 mr-1.5" />{verHistorico ? 'Ocultar histórico' : 'Mostrar histórico'}
                        </Button>
                        {verHistorico && (
                            <MacroHistoryTable items={macro_history} programados={client.macro_history_programados}
                                cambios={client.macro_history_cambios} onEdit={openEditEntry} onRepeat={openRepeatEntry}
                                onDelete={deleteMacroEntry} onEvaluar={openEvaluar}
                                borrador={macrosTocados ? {
                                    _borrador: true,
                                    effective_date: macrosForm.effective_date,
                                    training: macrosForm.training, peri: macrosForm.peri, rest: macrosForm.rest,
                                    peso: _numeroOno(macrosForm.peso),
                                    // «80 kg · NaN%» antes de guardar (punto 4.18). `Number('')` da 0
                                    // pero `Number(undefined)` y `Number('25,5')` dan NaN, y el NaN se
                                    // colaba tal cual a la pantalla. Con coma decimal, que es como se
                                    // escribe aquí, pasaba siempre.
                                    body_fat: _numeroOno(macrosForm.porcentaje_graso),
                                    rutina_numero: String(macrosForm.rutina_numero ?? '').trim() || null,
                                    criterio: macrosForm.criterio, note: macrosForm.note,
                                } : null} />
                        )}
                    </div>
                    {/* LA PESTAÑA COMO EN EL LIENZO «Gestor de clientes con su ficha» (19-10): los
                        macros nuevos a todo el ancho, con la propuesta del asistente dentro, y
                        el histórico encima, plegado (20-10). El último reporte se lee en Seguimiento (Francisco,
                        19-10), y sin él la columna de la derecha dejaba un hueco. */}
                    <Card className="bg-white border-[#E5E2DB] w-full min-w-0" ref={editorMacrosRef}><CardContent className="p-4 space-y-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="flex flex-wrap items-center gap-2">
                                <h2 className="m-0 font-heading text-lg font-bold uppercase tracking-wide text-[#1C1B19]">Macros nuevos</h2>
                                {mt && <Badge className={`border-0 text-[10px] ${profile?.macros_source === 'auto' ? 'bg-[#DCFCE7] text-[#15803D]' : 'bg-[#FEF3C7] text-[#854D0E]'}`}>{profile?.macros_source || 'manual'}</Badge>}
                                {macrosTocados && <Badge className="border-0 text-[10px] bg-[#FDEBDF] text-[#B43E06]">sin guardar</Badge>}
                            </div>
                            {/* REPETIR TAMBIÉN ES DECIDIR (Francisco, 19-10): guardar los mismos
                                números tras el mensual cuenta como macros puestos. Carga los
                                actuales y lleva al feedback, que es lo único que falta. */}
                            <div className="flex flex-wrap items-center gap-2">
                            <Button size="sm" variant="outline" className="bg-transparent border-[#FF671F]/40 text-[#B43E06] hover:bg-[#FDEBDF] text-xs" onClick={pedirSugerencia} disabled={sugiriendo} data-testid="suggest-macros-btn">{sugiriendo ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}{sugerencia?.propuesta ? 'Pedir otra propuesta' : 'Sugerir ajuste (IA)'}</Button>
                            {mt && (
                                <Button size="sm" variant="outline" className="bg-transparent border-[#D6D2C9] text-[#1C1B19] hover:bg-[#F6F5F2] text-xs"
                                    onClick={() => {
                                        // Solo los números: el feedback y el criterio ya escritos se quedan.
                                        setSugerenciaId(null);
                                        setMacrosForm(prev => ({ ...prev, ...macrosActuales }));
                                        feedbackMacrosRef.current?.focus();
                                    }} data-testid="repetir-actuales-btn">
                                    Repetir los actuales
                                </Button>
                            )}
                            </div>
                        </div>
                        {!mt && <p className="text-[#6F6B64] text-xs">Aún no tiene macros asignados.</p>}
                        {/* LO QUE COME HOY, CUANDO NO ES LO QUE PONE AQUÍ (17-08-2026).
                            Este editor carga `macros_training`, que es un espejo; el reparto
                            del día usa la fila vigente del historial, que es lo que el cliente
                            tiene delante en Nutrición. Cuando alguien escribe uno sin el otro
                            -- la sincronización de Calma lo ha hecho hoy mismo en dos --, el
                            coach ajustaría sobre un número que el cliente no tiene. */}
                        {profile?.macros_descuadrados && profile?.macros_vigentes && (
                            <div className="rounded-lg border border-[#FCD34D] bg-[#FFFBEB] p-3">
                                <p className="text-[#1C1B19] text-sm">
                                    Ojo: hoy está comiendo con{' '}
                                    <b className="text-[#854D0E]">
                                        {profile.macros_vigentes.entreno?.protein} P ·{' '}
                                        {profile.macros_vigentes.entreno?.carbs} H ·{' '}
                                        {profile.macros_vigentes.entreno?.fat} G
                                    </b>{' '}
                                    en día de entreno, que no es lo que hay escrito aquí.
                                </p>
                                <p className="text-[#57544E] text-xs mt-1">
                                    Es lo que manda desde el {_fechaLarga(profile.macros_vigentes.desde) || profile.macros_vigentes.desde}
                                    {profile.macros_vigentes.quien ? ` · lo puso ${profile.macros_vigentes.quien}` : ''}
                                </p>
                            </div>
                        )}
{/* La propuesta del asistente (revisar y confirmar): se pide con el botón de
                            arriba y se vuelca en la tabla; nada se guarda hasta «Guardar macros». */}
                        {sugerencia?.propuesta && (
                            <div className="rounded-lg border border-[#FF671F]/30 bg-[#FFF7F2] p-4 space-y-3" data-testid="propuesta-asistente">
                                <p className="m-0 text-sm font-bold text-[#1C1B19] flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#B43E06]" />Propuesta del asistente</p>
                                <div className="flex items-center gap-2">
                                    <span className="text-[#6F6B64] text-[10px]">confianza: {sugerencia.confianza || '-'}{sugerencia._modelo ? ` · ${sugerencia._modelo}` : ''}</span>
                                </div>
                                {/* Perfil derivado del camino del cliente (motor x respondedor) */}
                                {sugerencia.perfil && (
                                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs bg-[#F6F5F2] rounded-lg p-3 border border-[#E5E2DB]">
                                        <span className="text-[#6F6B64] uppercase tracking-wider text-[10px]">Perfil</span>
                                        <span className="text-[#57544E]">Motor <b className={MOTOR_COLOR[sugerencia.perfil.motor] || 'text-[#1C1B19]'}>{sugerencia.perfil.motor || 'sin dato'}</b>
                                            {sugerencia.perfil.hc_kg_techo != null && <span className="text-[#6F6B64]"> ({sugerencia.perfil.hc_kg_techo} g HC/kg en su techo)</span>}</span>
                                        <span className="text-[#57544E]">Respondedor <b className={MOTOR_COLOR[sugerencia.perfil.respondedor] || 'text-[#6F6B64]'}>{sugerencia.perfil.respondedor === 'sin_dato' ? 'sin dato' : sugerencia.perfil.respondedor}</b>
                                            {sugerencia.perfil.indice_hidrato_grasa_techo != null && <span className="text-[#6F6B64]"> (índice {sugerencia.perfil.indice_hidrato_grasa_techo})</span>}</span>
                                        {sugerencia.perfil.techo_hc != null && <span className="text-[#57544E]">Techo/suelo <b className="text-[#1C1B19]">{sugerencia.perfil.techo_hc}/{sugerencia.perfil.suelo_hc} g</b></span>}
                                        {sugerencia.perfil.umbral_definicion != null && <span className="text-[#57544E]">Umbral def. <b className="text-[#1C1B19]">{sugerencia.perfil.umbral_definicion} g</b></span>}
                                        {sugerencia.perfil.umbral_volumen != null && <span className="text-[#57544E]">Umbral vol. <b className="text-[#1C1B19]">{sugerencia.perfil.umbral_volumen} g</b></span>}
                                        {sugerencia.contexto_usado?.n_reglas_perfil > 0 && <span className="text-[#6F6B64]">{sugerencia.contexto_usado.n_reglas_perfil} reglas de su perfil</span>}
                                    </div>
                                )}
                                {sugerencia.contexto_decision && Object.keys(sugerencia.contexto_decision).length > 0 && (
                                    <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs bg-[#F6F5F2] rounded-lg p-3 border border-[#E5E2DB]">
                                        {sugerencia.contexto_decision.peso_actual != null && (
                                            <span className="text-[#57544E]">Último peso: <b className="text-[#1C1B19]">{kilos(sugerencia.contexto_decision.peso_actual)} kg</b>{sugerencia.contexto_decision.fecha_actual ? <span className="text-[#6F6B64]"> · {sugerencia.contexto_decision.fecha_actual}</span> : null}</span>
                                        )}
                                        {sugerencia.contexto_decision.delta_ultimo != null && (
                                            <span className="text-[#57544E]">Desde el anterior: <b className="text-[#1C1B19]">{sugerencia.contexto_decision.delta_ultimo > 0 ? '+' : ''}{kilos(sugerencia.contexto_decision.delta_ultimo)} kg</b>{sugerencia.contexto_decision.dias_desde_anterior != null ? <span className="text-[#6F6B64]"> ({plural(sugerencia.contexto_decision.dias_desde_anterior, 'día')})</span> : null}</span>
                                        )}
                                        {sugerencia.contexto_decision.delta_inicio != null && (
                                            <span className="text-[#57544E]">Desde el inicio: <b className="text-[#1C1B19]">{sugerencia.contexto_decision.delta_inicio > 0 ? '+' : ''}{kilos(sugerencia.contexto_decision.delta_inicio)} kg</b></span>
                                        )}
                                        {sugerencia.contexto_decision.cumplimiento_dieta && (
                                            <span className="text-[#57544E]">Cumplimiento: <b className="text-[#1C1B19]">{sugerencia.contexto_decision.cumplimiento_dieta}</b></span>
                                        )}
                                    </div>
                                )}
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                    {[['Entrenamiento', sugerencia.propuesta.entreno, true], ['Perientreno', sugerencia.propuesta.perientreno, false], ['Descanso', sugerencia.propuesta.descanso, true]].map(([t, mm, g]) => (
                                        <div key={t} className="bg-[#F6F5F2] rounded-xl p-3 border border-[#E5E2DB]">
                                            <p className="text-[10px] text-[#6F6B64] uppercase tracking-wider mb-1">{t}</p>
                                            <p className="text-sm font-bold"><span className="text-[#C2410C]">{mm?.proteina}P</span> · <span className="text-[#1D4ED8]">{mm?.hidratos}H</span>{g && <> · <span className="text-[#854D0E]">{mm?.grasa}G</span></>}</p>
                                        </div>
                                    ))}
                                </div>
                                {sugerencia.cambios?.length > 0 && <p className="text-[#57544E] text-xs"><span className="text-[#6F6B64]">Cambios: </span>{sugerencia.cambios.join('  ·  ')}</p>}
                                {sugerencia.razonamiento && <p className="text-[#1C1B19] text-sm leading-relaxed">{sugerencia.razonamiento}</p>}
                                {sugerencia.avisos?.length > 0 && (
                                    <ul className="space-y-1">{sugerencia.avisos.map((a, i) => <li key={i} className="text-[#854D0E] text-xs flex gap-1.5"><AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" /><span>{a}</span></li>)}</ul>
                                )}
                                {sugerencia.guardarrail?.length > 0 && (
                                    <div className="rounded-lg border border-[#FCA5A5] bg-[#FEF2F2] p-2">
                                        <p className="text-[#B91C1C] text-[11px] font-bold mb-0.5">Revisar (guardarraíl):</p>
                                        <ul className="space-y-0.5">{sugerencia.guardarrail.map((w, i) => <li key={i} className="text-[#B91C1C] text-xs">· {w}</li>)}</ul>
                                    </div>
                                )}
                                <div className="flex items-center gap-2 pt-1">
                                    <Button size="sm" className="bg-[#FF671F] hover:bg-[#FF671F]/90 text-white text-xs" onClick={usarSugerencia} data-testid="use-suggestion-btn"><CheckCircle2 className="w-3 h-3 mr-1" />Usar esta propuesta</Button>
                                    <Button size="sm" variant="ghost" className="text-[#57544E] hover:text-[#1C1B19] text-xs" onClick={() => setSugerencia(null)}>Descartar</Button>
                                </div>
                                </div>
                        )}
                        <TablaMacrosNuevos form={macrosForm} actuales={macrosActuales} onCambio={setMacroCampo} />
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                <div>
                                    <Label className="text-[#57544E] text-xs">Fecha de asignación de macros</Label>
                                    {/* POR DEFECTO MAÑANA (21-10): los macros nuevos empiezan al día
                                        siguiente de guardarlos, y ese día al cliente le llega «Ya tienes
                                        tus macros nuevos» (`core/avisos_cliente.aviso_de_macros_que_empiezan`).
                                        Se avisa solo si se sale de mañana, hacia delante o hacia atrás. */}
                                    <Input type="date" value={macrosForm.effective_date} onChange={e => setMacrosForm({ ...macrosForm, effective_date: e.target.value })} className={`h-9 bg-white text-[#1C1B19] mt-1 ${macrosForm.effective_date > mananaISO() ? 'border-[#FCA5A5]' : macrosForm.effective_date !== mananaISO() ? 'border-[#FF671F]' : 'border-[#D6D2C9]'}`} data-testid="macro-effective-date" />
                                    {macrosForm.effective_date !== mananaISO() && (
                                        // Más allá de mañana va en el rojo de los avisos de la ficha (Macros,
                                        // prueba 12): esos macros tardan en regir. Hoy o hacia atrás, en naranja.
                                        <p className={`text-[10px] mt-1 leading-relaxed ${macrosForm.effective_date > mananaISO() ? 'text-[#B91C1C]' : 'text-[#B43E06]'}`} data-testid="macro-aviso-fecha">
                                            {!macrosForm.effective_date
                                                ? 'Falta la fecha desde la que valen. '
                                                : macrosForm.effective_date > mananaISO()
                                                ? `No se aplican hasta el ${_fechaLarga(macrosForm.effective_date)}: hasta ese día sigue con los actuales. `
                                                : macrosForm.effective_date === hoyISO()
                                                ? 'Se aplican desde hoy. '
                                                : `Se aplican hacia atrás, desde el ${_fechaLarga(macrosForm.effective_date)}. `}
                                            <button type="button" className="underline font-bold"
                                                onClick={() => setMacrosForm({ ...macrosForm, effective_date: mananaISO() })}>Poner mañana</button>
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <Label className="text-[#57544E] text-xs">Guardar asociado a peso</Label>
                                    <Input type="number" step="0.1" min="25" max="300" value={macrosForm.peso}
                                        onChange={e => setMacrosForm({ ...macrosForm, peso: e.target.value,
                                            // Un peso cambiado a mano ya no es el del reporte (REP-10).
                                            peso_fecha: fechaDelPesaje(e.target.value, pesoDelEditor?.peso, pesoFechaActual) })}
                                        placeholder="-" className="h-9 bg-white border-[#D6D2C9] text-[#1C1B19] mt-1" data-testid="macro-peso" />
                                    {/* Debajo, la diferencia con el peso del ajuste anterior, que es lo
                                        primero que mira el coach. */}
                                    {(() => {
                                        const p = parseFloat(macrosForm.peso);
                                        if (isNaN(p) || pesoUltimoAjuste == null) return null;
                                        const d = Math.round((p - pesoUltimoAjuste) * 10) / 10;
                                        return (
                                            <p className="text-[10px] mt-0.5 text-[#6F6B64]">
                                                Últimos macros: {kilos(pesoUltimoAjuste)} kg ·{' '}
                                                <b className={d > 0 ? 'text-[#B91C1C]' : d < 0 ? 'text-[#15803D]' : 'text-[#57544E]'}>
                                                    {d > 0 ? `ha ganado ${kilos(d)}` : d < 0 ? `ha perdido ${kilos(Math.abs(d))}` : 'sin cambios'}{d !== 0 ? ' kg' : ''}
                                                </b>
                                            </p>
                                        );
                                    })()}
                                </div>
                                <div>
                                    <Label className="text-[#57544E] text-xs">Guardar asociado a % graso</Label>
                                    <Input type="number" step="0.1" min="3" max="60" value={macrosForm.porcentaje_graso}
                                        onChange={e => setMacrosForm({ ...macrosForm, porcentaje_graso: e.target.value })}
                                        placeholder="-" className="h-9 bg-white border-[#D6D2C9] text-[#1C1B19] mt-1" data-testid="macro-body-fat" />
                                </div>
                                {/* LA RUTINA, Y NADA MÁS DE ENTRENAMIENTO (vídeo de Jesús, 20-10:
                                    «¿Ajustes de entrenamiento por qué? Si esto va a otro sitio. Es
                                    simplemente poner una rutina y el cardio pautado no hay que
                                    ponerlo aquí»). Llega con la que lleva (de momento no pasa sola,
                                    `core/rutina_que_pasa_sola`) y se guarda en su fila del historial.
                                    El cardio pautado ya no se pone desde la ficha (21-10). */}
                                <div>
                                    <Label className="text-[#57544E] text-xs">Rutina</Label>
                                    <Input type="text" inputMode="numeric" maxLength={5} value={macrosForm.rutina_numero ?? ''}
                                        onChange={e => setMacrosForm({ ...macrosForm, rutina_numero: e.target.value })}
                                        placeholder="-" className="h-9 bg-white border-[#D6D2C9] text-[#1C1B19] mt-1 tabular-nums" data-testid="macro-rutina" />
                                </div>
                        </div>
                                <div>
                                    <Label className="text-[#57544E] text-xs">Feedback para el cliente (obligatorio)</Label>
                                    <Textarea ref={feedbackMacrosRef} value={macrosForm.note} onChange={e => setMacrosForm({ ...macrosForm, note: e.target.value })} placeholder="Ej: Bajamos un poco los hidratos por la pérdida de peso, sigue así..." className="bg-white border-[#D6D2C9] text-[#1C1B19] mt-1 min-h-[38px]" data-testid="macro-note" />
                                    {/* Aquí es donde más falta hacen: el feedback es obligatorio
                                        para guardar un ajuste, así que se escribe uno por cada
                                        cliente y cada quincena (punto 4.1). */}
                                    <PlantillasFeedback actual={macrosForm.note} testid="plantillas-macros" claro
                                        onInsertar={t => setMacrosForm({ ...macrosForm, note: t })} />
                                </div>
                                <div>
                                    <Label className="text-[#57544E] text-xs">Criterio del ajuste (interno)</Label>
                                    <Textarea value={macrosForm.criterio} onChange={e => setMacrosForm({ ...macrosForm, criterio: e.target.value })} placeholder="Ej: estancado dos meses cumpliendo, recorto un escalón de hidratos..." className="bg-white border-[#D6D2C9] text-[#1C1B19] mt-1 min-h-[38px]" data-testid="macro-criterio" />
                                </div>
                        {/* Punto 28: que se vea que ha guardado. El aviso flotante se va solo; esto
                            se queda mientras el coach siga en la ficha, y dice QUE quedo guardado. */}
                        {ultimoGuardado && !macrosTocados && (
                            <div className="rounded-lg border border-[#86EFAC] bg-[#DCFCE7] p-3 flex items-start gap-2" data-testid="macros-guardado">
                                <CheckCircle2 className="w-4 h-4 text-[#15803D] shrink-0 mt-0.5" />
                                <p className="text-xs text-[#15803D]">
                                    <b>Guardado a las {ultimoGuardado.hora}.</b>{' '}
                                    Macros para el {_fechaLarga(ultimoGuardado.desde)}
                                    {ultimoGuardado.peso ? ` · peso ${kilos(ultimoGuardado.peso)} kg${ultimoGuardado.pesoFecha ? ` del ${_fechaCorta(ultimoGuardado.pesoFecha)}` : ''}` : ''}.
                                    {' '}{ultimoGuardado.desde > hoyISO()
                                        ? 'El cliente ya tiene el aviso, y ese día le llega otro cuando empiecen.'
                                        : 'El cliente ya lo tiene.'}
                                </p>
                            </div>
                        )}
                        <div className="flex flex-wrap items-center gap-2">
                            <Button onClick={handleSaveMacros} disabled={savingMacros || (!macrosTocados && !macrosForm.note.trim())} className="bg-[#FF671F] hover:bg-[#FF671F]/90 text-white disabled:opacity-40" data-testid="save-macros-btn">{savingMacros ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" />{macrosForm.effective_date ? `Guardar macros para el ${_fechaLarga(macrosForm.effective_date)}` : 'Guardar macros'}</>}</Button>
                            {macrosTocados && <Button size="sm" variant="ghost" className="text-[#57544E] hover:text-[#1C1B19] text-xs" onClick={descartarCambiosMacros}>Descartar cambios</Button>}
                        </div>
                    </CardContent></Card>

                    {/* LAS NOTAS DEL ENTRENADOR, DEBAJO DE LOS MACROS (hoja del 22-10, fila 9):
                        el cliente no las ve; las cinco últimas y «Mostrar todas». */}
                    <NotasDelEntrenador api={api} clientId={clientId} />

                    {/* Evaluacion de la fase que abrio un ajuste (modelo predictivo, paso 1) */}
                    <Dialog open={!!evalEntry} onOpenChange={(o) => !o && setEvalEntry(null)}>
                        {evalEntry && (
                            <DialogContent className="tema-claro [color-scheme:light] bg-white border-[#D6D2C9] max-w-md text-[#1C1B19]" data-testid="eval-dialog">
                                <DialogHeader><DialogTitle className="uppercase tracking-wider">Cómo salió la fase</DialogTitle></DialogHeader>
                                <p className="text-[#57544E] text-xs -mt-2">
                                    Macros del {_fechaCorta(evalEntry.effective_date || (evalEntry.created_at || '').slice(0, 10))}
                                </p>
                                <div className="space-y-3">
                                    <div>
                                        <Label className="text-[#57544E] text-xs">Resultado</Label>
                                        <div className="grid grid-cols-2 gap-2 mt-1">
                                            {[['buena', 'Buena'], ['mala', 'Mala']].map(([v, l]) => (
                                                <button key={v} onClick={() => setEvalForm(f => ({ ...f, resultado: v }))}
                                                    className={`py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all ${evalForm.resultado === v ? (v === 'buena' ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white') : 'bg-[#F6F5F2] text-[#6F6B64] hover:text-[#1C1B19]'}`}
                                                    data-testid={`eval-resultado-${v}`}>{l}</button>
                                            ))}
                                        </div>
                                    </div>
                                    {evalForm.resultado === 'mala' && (
                                        <div>
                                            <Label className="text-[#57544E] text-xs">¿De quién fue?</Label>
                                            <div className="grid grid-cols-3 gap-2 mt-1">
                                                {[['ajuste', 'Del ajuste'], ['cliente', 'No cumplió'], ['otro', 'Otro']].map(([v, l]) => (
                                                    <button key={v} onClick={() => setEvalForm(f => ({ ...f, causa: v }))}
                                                        className={`py-2 rounded-lg text-[11px] font-bold uppercase tracking-wider transition-all ${evalForm.causa === v ? 'bg-[#FF671F] text-white' : 'bg-[#F6F5F2] text-[#6F6B64] hover:text-[#1C1B19]'}`}>{l}</button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    <div>
                                        <Label className="text-[#57544E] text-xs">Nota (opcional)</Label>
                                        <Textarea value={evalForm.nota} onChange={e => setEvalForm(f => ({ ...f, nota: e.target.value }))}
                                            placeholder="Ej: bajó 2 kg pero se quedó sin fuerza la última semana" className="bg-white border-[#D6D2C9] text-[#1C1B19] mt-1" />
                                    </div>
                                </div>
                                <DialogFooter>
                                    <Button variant="outline" onClick={() => setEvalEntry(null)} className="bg-transparent border-[#D6D2C9] text-[#1C1B19]">Cancelar</Button>
                                    <Button onClick={guardarEvaluacion} disabled={savingEval} className="bg-[#FF671F] text-white" data-testid="save-eval-btn">
                                        {savingEval ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" />Guardar</>}
                                    </Button>
                                </DialogFooter>
                            </DialogContent>
                        )}
                    </Dialog>

                    {/* Modal del historial: editar una entrada pasada o reaplicarla como macros actuales */}
                    <Dialog open={entryModalOpen} onOpenChange={(o) => { setEntryModalOpen(o); if (!o) setEditingEntryId(null); }}>
                        <DialogContent className="tema-claro [color-scheme:light] bg-white border-[#D6D2C9] max-w-lg text-[#1C1B19]" data-testid="macros-modal">
                            <DialogHeader><DialogTitle className="text-[#1C1B19] uppercase tracking-wider">{editingEntryId ? 'Editar entrada del historial' : 'Repetir estos macros'}</DialogTitle></DialogHeader>
                            <div className="space-y-4">
                                <div>
                                    <p className="text-xs text-[#6F6B64] uppercase tracking-wider mb-2">Entrenamiento</p>
                                    <div className="grid grid-cols-3 gap-2">
                                        <div><Label className="text-[#57544E] text-xs">Proteína</Label><Input type="number" value={entryForm.training.protein} onChange={e => setEntryForm({...entryForm, training: {...entryForm.training, protein: e.target.value}})} className="bg-white border-[#D6D2C9] text-[#1C1B19]" data-testid="macro-input-tp" /></div>
                                        <div><Label className="text-[#57544E] text-xs">Hidratos</Label><Input type="number" value={entryForm.training.carbs} onChange={e => setEntryForm({...entryForm, training: {...entryForm.training, carbs: e.target.value}})} className="bg-white border-[#D6D2C9] text-[#1C1B19]" /></div>
                                        <div><Label className="text-[#57544E] text-xs">Grasa</Label><Input type="number" value={entryForm.training.fat} onChange={e => setEntryForm({...entryForm, training: {...entryForm.training, fat: e.target.value}})} className="bg-white border-[#D6D2C9] text-[#1C1B19]" /></div>
                                    </div>
                                </div>
                                <div>
                                    <p className="text-xs text-[#6F6B64] uppercase tracking-wider mb-2">Descanso</p>
                                    <div className="grid grid-cols-3 gap-2">
                                        <div><Label className="text-[#57544E] text-xs">Proteína</Label><Input type="number" value={entryForm.rest.protein} onChange={e => setEntryForm({...entryForm, rest: {...entryForm.rest, protein: e.target.value}})} className="bg-white border-[#D6D2C9] text-[#1C1B19]" /></div>
                                        <div><Label className="text-[#57544E] text-xs">Hidratos</Label><Input type="number" value={entryForm.rest.carbs} onChange={e => setEntryForm({...entryForm, rest: {...entryForm.rest, carbs: e.target.value}})} className="bg-white border-[#D6D2C9] text-[#1C1B19]" /></div>
                                        <div><Label className="text-[#57544E] text-xs">Grasa</Label><Input type="number" value={entryForm.rest.fat} onChange={e => setEntryForm({...entryForm, rest: {...entryForm.rest, fat: e.target.value}})} className="bg-white border-[#D6D2C9] text-[#1C1B19]" /></div>
                                    </div>
                                </div>
                                <div>
                                    <p className="text-xs text-[#6F6B64] uppercase tracking-wider mb-2">Perientreno</p>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div><Label className="text-[#57544E] text-xs">Proteína</Label><Input type="number" value={entryForm.peri.protein} onChange={e => setEntryForm({...entryForm, peri: {...entryForm.peri, protein: e.target.value}})} className="bg-white border-[#D6D2C9] text-[#1C1B19]" /></div>
                                        <div><Label className="text-[#57544E] text-xs">Hidratos</Label><Input type="number" value={entryForm.peri.carbs} onChange={e => setEntryForm({...entryForm, peri: {...entryForm.peri, carbs: e.target.value}})} className="bg-white border-[#D6D2C9] text-[#1C1B19]" /></div>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <Label className="text-[#57544E] text-xs">Fecha de asignación de macros</Label>
                                        <Input type="date" value={entryForm.effective_date} onChange={e => setEntryForm({...entryForm, effective_date: e.target.value})} className="bg-white border-[#D6D2C9] text-[#1C1B19] mt-1" data-testid="macro-effective-date" />
                                    </div>
                                    <div>
                                        <Label className="text-[#57544E] text-xs">% graso</Label>
                                        <Input type="number" step="0.1" min="3" max="60" value={entryForm.porcentaje_graso} onChange={e => setEntryForm({...entryForm, porcentaje_graso: e.target.value})} placeholder="-" className="bg-white border-[#D6D2C9] text-[#1C1B19] mt-1" />
                                    </div>
                                </div>
                                <div>
                                    <Label className="text-[#57544E] text-xs">Criterio del ajuste (interno)</Label>
                                    <Textarea value={entryForm.criterio} onChange={e => setEntryForm({...entryForm, criterio: e.target.value})} placeholder="Por qué se hizo este ajuste" className="bg-white border-[#D6D2C9] text-[#1C1B19] mt-1 min-h-[38px]" />
                                </div>
                                <div>
                                    <Label className="text-[#57544E] text-xs">{editingEntryId ? 'Feedback para el cliente (opcional)' : 'Motivo: el feedback que le llega al cliente (obligatorio)'}</Label>
                                    <Textarea value={entryForm.note} onChange={e => setEntryForm({...entryForm, note: e.target.value})} placeholder="Ej: Bajamos un poco los hidratos por la pérdida de peso, sigue así..." className="bg-white border-[#D6D2C9] text-[#1C1B19] mt-1" data-testid="macro-note" />
                                    <PlantillasFeedback actual={entryForm.note} testid="plantillas-entrada" claro
                                        onInsertar={t => setEntryForm({ ...entryForm, note: t })} />
                                </div>
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setEntryModalOpen(false)} className="bg-transparent border-[#D6D2C9] text-[#1C1B19]">Cancelar</Button>
                                <Button onClick={handleSaveEntry} disabled={savingEntry} className="bg-[#FF671F] text-white" data-testid="save-entry-btn">{savingEntry ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" />Guardar</>}</Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </TabsContent>

                {/* ========== TAB 3: MEMBRESÍA ========== */}
                <TabsContent value="membresia" className="space-y-4">
                    {/* GESTIÓN DE USUARIO: SOLO EL ADMIN (rol, plan de cortesía y baja lógica).
                        Los tres controles llaman a endpoints que van con `get_admin_only_user`,
                        así que al entrenador -- que ve la ficha de TODOS los clientes desde el
                        13-08 -- se le pintaban y al pulsarlos solo se llevaba un 403. Un botón
                        que siempre falla es peor que no tenerlo, y es el mismo candado que ya
                        lleva la sección Usuarios del menú.
                        La baja se dice aparte y para todos: es el motivo de que su cliente no
                        pueda entrar y no se lee en ningún otro sitio de la ficha. UNA sola
                        copia del aviso -- estuvo escrito dos veces, aquí para el entrenador y
                        dentro de la tarjeta para el admin, y dos copias del mismo texto acaban
                        diciendo cosas distintas el día que alguien retoca una. Al admin le sale
                        justo encima de la tarjeta, que es donde lo tenía. */}
                    {user?.deleted_at && (
                        <div className="text-xs text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] rounded-lg px-3 py-2">Usuario dado de baja: no puede entrar en la app.</div>
                    )}
                    {adminUser?.role === 'admin' && (
                    <Card className="bg-white border-[#E5E2DB]"><CardHeader className="pb-2"><CardTitle className="text-sm text-[#6F6B64] uppercase tracking-wider flex items-center gap-2"><Shield className="w-4 h-4" />Gestión de usuario</CardTitle></CardHeader>
                        <CardContent className="space-y-3">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div><Label className="text-[#57544E] text-xs">Rol</Label>
                                    <select value={user?.role || 'client'} onChange={e => changeUserRole(e.target.value)} disabled={savingMgmt} className="w-full bg-white border border-[#D6D2C9] text-[#1C1B19] text-sm rounded-lg px-2 py-2 mt-1">
                                        {USER_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                                    </select>
                                </div>
                                <div><Label className="text-[#57544E] text-xs">Plan</Label>
                                    <select value={profile?.plan || ''} onChange={e => setUserPlan(e.target.value, !!profile?.comp_plan)} className="w-full bg-white border border-[#D6D2C9] text-[#1C1B19] text-sm rounded-lg px-2 py-2 mt-1">
                                        <option value="">Sin plan</option>
                                        <OpcionesDePlan catalogo={planCatalog} planActual={profile?.plan} />
                                    </select>
                                </div>
                            </div>
                            <label className="flex items-center gap-2 text-sm text-[#57544E] cursor-pointer select-none">
                                <input type="checkbox" checked={!!profile?.comp_plan} onChange={e => setUserPlan(profile?.plan || '', e.target.checked)} className="accent-[#FF671F] w-4 h-4" />
                                Plan de cortesía (sin pago)
                            </label>
                            {/* DAR ACCESO SIN SALIR DE LA FICHA (punto 48). El aviso del hash de
                                Firebase va en el diálogo, antes de tocar nada. */}
                            {pausaDeLaFicha.aviso && (
                                <p className="text-sm text-[#1E3A8A]" data-testid={pausaDeLaFicha.avisoTestid}>{pausaDeLaFicha.aviso}</p>
                            )}
                            <div className="pt-1 flex flex-wrap items-center gap-2">
                                {user?.deleted_at
                                    ? <Button onClick={toggleUserBaja} disabled={savingMgmt} data-testid="reactivar-usuario" className="bg-green-600 hover:bg-green-700 text-white text-sm"><RotateCcw className="w-4 h-4 mr-1" /> {savingMgmt ? 'Reactivando...' : 'Reactivar usuario'}</Button>
                                    : <Button onClick={toggleUserBaja} variant="outline" className="bg-transparent border-[#FCA5A5] text-[#B91C1C] hover:bg-[#FEE2E2] text-sm"><Trash2 className="w-4 h-4 mr-1" /> Dar de baja (lógica)</Button>}
                                {!user?.deleted_at && profile?.id && (
                                    <Button onClick={alternarPausa} variant="outline" data-testid={pausaDeLaFicha.testid}
                                        className={`bg-transparent text-sm ${pausaDeLaFicha.marcado
                                            ? 'border-[#93C5FD] text-[#1E3A8A] hover:bg-[#DBEAFE]'
                                            : 'border-[#D6D2C9] text-[#1C1B19] hover:bg-[#F6F5F2]'}`}>
                                        <PauseCircle className="w-4 h-4 mr-1" /> {pausaDeLaFicha.boton}
                                    </Button>
                                )}
                                {!user?.deleted_at && profile?.id && (
                                    profile.status === 'activo'
                                        ? <Button onClick={alternarActivo} variant="outline" data-testid="desactivar-cliente"
                                            className="bg-transparent border-[#FCD34D] text-[#854D0E] hover:bg-[#FEF3C7] text-sm">
                                            <Power className="w-4 h-4 mr-1" /> Desactivar cliente
                                          </Button>
                                        : <Button onClick={alternarActivo} data-testid="activar-cliente"
                                            className="bg-green-600 hover:bg-green-700 text-white text-sm">
                                            <Power className="w-4 h-4 mr-1" /> Activar cliente
                                          </Button>
                                )}
                                <Button onClick={restablecerClave} variant="outline" data-testid="restablecer-clave"
                                    className="bg-transparent border-[#D6D2C9] text-[#1C1B19] hover:bg-[#F6F5F2] text-sm">
                                    <Shield className="w-4 h-4 mr-1" /> Restablecer contraseña
                                </Button>
                                {!user?.deleted_at && <ActivarFechaDePrueba usuario={client?.user} alActivarla={fetchClient} />}
                            </div>
                            {claveTemporal && (
                                <div className="rounded-lg border border-[#FF671F]/40 bg-[#FDEBDF] p-3 space-y-1" data-testid="clave-temporal">
                                    <p className="text-sm text-[#1C1B19]">
                                        Clave temporal: <b className="font-mono tracking-wide">{claveTemporal}</b>
                                    </p>
                                    <button type="button" onClick={() => { navigator.clipboard?.writeText(claveTemporal); toast.success('Clave copiada'); }}
                                        className="text-xs text-[#B43E06] hover:underline">Copiar</button>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                    )}
                    <Card className="bg-white border-[#E5E2DB]"><CardContent className="p-5">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <InfoItem icon={Shield} label="Plan" value={<PlanBadge plan={profile?.plan} />} />
                            {/* Del catálogo cuando el perfil no trae precio propio (punto 2.4c).
                                Lo resuelve el servidor: aquí salía «0€/ciclo» en la ficha del
                                mismo cliente al que la lista le pone bien su precio. */}
                            {/* «/mes» en los planes mensuales (P51 del 23-08): el periodo
                                lo dice el servidor por el catálogo, no un if por nombre. */}
                            <InfoItem icon={CreditCard} label="Precio"
                                value={profile?.precio_cortesia ? 'Cortesía' : `${profile?.precio_ciclo ?? profile?.price ?? 0}€/${profile?.precio_periodo || 'ciclo'}`} />
                            {/* «ALTA» Y «INICIO DEL CICLO» SON DOS COSAS (2-09). Esto ponía
                                «Inicio» y pintaba `created_at`, justo al lado de un «Semana
                                4/12» que se cuenta desde `cycle_start`. En la ficha de
                                Montalvo se leía «INICIO 5/7/2026 · SEMANA 4/12», y del 5 de
                                julio a hoy van nueve semanas, no cuatro: la tarjeta se
                                contradecía sola porque enseñaba un anclaje y contaba con
                                otro. Ahora se dicen los dos, con su nombre. */}
                            <InfoItem icon={Calendar} label="Alta" value={pintarDia(profile?.created_at, undefined, '-')} />
                            <InfoItem icon={Calendar} label="Inicio del ciclo"
                                value={pintarDia(profile?.cycle_start, undefined, '-')} />
                            <InfoItem icon={Calendar} label="Próx. cobro" value={pintarDia(profile?.next_payment, undefined, '-')} />
                        </div>
                        {adminUser?.role === 'admin' && (
                            <FechasDelPlan api={api} clientId={clientId} onGuardado={fetchClient} />
                        )}
                        {/* SI HA ENTRADO ALGUNA VEZ (la otra mitad del punto 48: «no hay
                            ninguna pantalla que diga quién ha entrado alguna vez»). El dato
                            lo escribe la campanita y ya viajaba a la lista de Clientes; en la
                            ficha, que es donde se mira antes de llamar a alguien, no estaba.
                            Sin él, «no le llega nada» y «nunca ha abierto la app» son el
                            mismo silencio y se tratan igual. */}
                        <p className="mt-3 text-xs text-[#6F6B64]" data-testid="ultima-entrada">
                            {profile?.ultima_entrada
                                // Del día, a mediodía: la campanita lo guarda como día suelto
                                // («2026-08-24») y el navegador lee eso como medianoche UTC,
                                // que en España es el día anterior por la tarde. Los `slice`
                                // son por si algún registro viejo trae la hora pegada.
                                ? `Última entrada en la app: ${new Date(String(profile.ultima_entrada).slice(0, 10) + 'T12:00:00').toLocaleDateString('es-ES')}`
                                : 'No consta que haya entrado nunca en la app'}
                        </p>
                        {/* Punto 39: aquí es donde se mira antes de cobrar, y aquí es donde la
                            excepción tiene que estar delante. El caso que costó dinero fue
                            exactamente este: cobrarle la renovación a quien tenía un mes
                            perdonado. */}
                        {profile?.excepcion && (
                            <div className="mt-4 flex items-start gap-2 rounded-lg border border-[#FF671F]/40 bg-[#FDEBDF] p-3" data-testid="excepcion-en-cobro">
                                <AlertCircle className="w-4 h-4 text-[#B43E06] shrink-0 mt-0.5" />
                                <p className="text-sm text-[#1C1B19]"><b className="text-[#B43E06]">Antes de cobrarle:</b> {profile.excepcion}</p>
                            </div>
                        )}

                        {/* LO QUE DE VERDAD SE LE COBRA, AL LADO DEL PRECIO DE LA FICHA
                            (punto 43 del doc del 24-08). La ficha de Montalvo dice 1.500 € y
                            Stripe le cobra 250: hasta hoy no había forma de ver la diferencia
                            sin salirse de la pantalla. Manda el cobro real.

                            Y OJO: el precio de la ficha NO se corrige solo. Los clientes que
                            vienen del sistema anterior conservan su plan y su precio congelado
                            (Jesús, 24-08); las tarifas nuevas son para los clientes nuevos. */}
                        <div className="mt-4 border-t border-[#E5E2DB] pt-3 space-y-2" data-testid="cobro-real">
                            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                                <span className="text-xs text-[#6F6B64] uppercase tracking-wider">Último cobro</span>
                                {profile?.ultimo_cobro ? (
                                    <span className="text-sm text-[#1C1B19]">
                                        <b>{Number(profile.ultimo_cobro.importe).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}</b>
                                        {' el '}{pintarDia(profile.ultimo_cobro.fecha)}
                                        {profile.ultimo_cobro.origen ? ` · ${profile.ultimo_cobro.origen}` : ''}
                                        {profile.cadencia?.real ? ` · ${profile.cadencia.real}` : ''}
                                    </span>
                                ) : (
                                    <span className="text-sm text-[#6F6B64]">no consta ninguno</span>
                                )}
                                {/* LO QUE ESTE CLIENTE DEJA AL MES, y es el mismo número que
                                    suma la «Factura al mes» de Dirección y el MRR del Inicio
                                    (punto 46): el servidor ya lo mandaba y la ficha no lo
                                    enseñaba, así que para saber por cuánto contaba un cliente
                                    había que hacer la división a mano y salía distinta. */}
                                {profile?.euros_al_mes != null && (
                                    <span className="text-xs text-[#6F6B64]" data-testid="euros-al-mes">
                                        {Number(profile.euros_al_mes).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}/mes
                                        {' '}para el negocio
                                        {/* El 0 de la cortesía se explica DONDE se lee. Sin esto se leía
                                            «0,00 €/mes» a secas debajo de un cobro de 291,01 € y parecía
                                            un fallo de la cuenta. */}
                                        {profile?.precio_cortesia && ' · plan de cortesía'}
                                    </span>
                                )}
                            </div>
                            {/* EL AVISO TIENE QUE RESPETAR LA CORTESÍA (fallo 11 de la verificación
                                del 24-08). Este aviso nació en el punto 43 mirando solo la
                                diferencia entre el precio de la ficha y el último cobro, y por eso
                                en las fichas de cortesía decía lo contrario que el propio código:
                                arriba «Cortesía» y «0,00 €/mes para el negocio», y justo debajo «la
                                ficha dice 0,00 € y se le cobraron 291,01 €». No hay tal
                                descuadre: `importe_de_ciclo` (backend/routes/admin.py) devuelve 0 y
                                «cortesia» ANTES de mirar nada más, o sea que el 0 es una decisión
                                tomada, no un dato que falte. En producción eran 7 fichas con un
                                cobro antiguo a sus espaldas (Andrés Cano, 291,01 €; Francisco
                                Javier Marcos, 625,57 €; Ricardo Rodríguez, 87,00 €...).
                                Si se vuelve a tocar este bloque: cualquier aviso de dinero se
                                pregunta primero si hay cortesía, porque la cortesía gana. */}
                            {profile?.precio_cortesia ? (
                                /* Lo único que SÍ hay que avisar de una cortesía: que Stripe le
                                   siga cobrando por detrás. Ahí el 0 del panel es mentira y hay que
                                   decidir cuál de las dos cosas sobra. Hoy en producción no le pasa
                                   a ninguno, y por eso mismo conviene que salte el día que pase. */
                                profile?.renueva_solo?.via === 'stripe' ? (
                                    <p className="text-xs text-[#854D0E]" data-testid="cortesia-con-stripe">
                                        Plan de cortesía con una suscripción viva en Stripe.
                                    </p>
                                ) : null
                            ) : profile?.ultimo_cobro && profile?.precio_ciclo != null
                                && Math.abs(Number(profile.precio_ciclo) - Number(profile.ultimo_cobro.importe)) >= 1 ? (
                                <p className="text-xs text-[#854D0E]" data-testid="cobro-no-cuadra">
                                    La ficha dice {Number(profile.precio_ciclo).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })} por
                                    ciclo y lo último que se le cobró fueron {Number(profile.ultimo_cobro.importe).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}.
                                </p>
                            ) : null}
                            {/* La cadencia real contra la que dice su plan (punto 44): Montalvo
                                cuenta «semana 3 de 12» y Stripe le cobra del 8 de agosto al 8 de
                                septiembre. */}
                            {profile?.cadencia?.discrepan && (
                                <p className="text-xs text-[#854D0E]" data-testid="cadencia-no-cuadra">
                                    Se le cobra {profile.cadencia.real} y su plan cuenta ciclos
                                    de {profile.cadencia.plan}.
                                </p>
                            )}
                            {/* QUIÉN RENUEVA SOLO (punto 45): no había ni una palabra en toda la
                                ficha, y es lo primero que se pregunta antes de llamar a nadie. */}
                            <p className="text-sm" data-testid="renovacion-ficha">
                                <span className="text-xs text-[#6F6B64] uppercase tracking-wider mr-2">Renovación</span>
                                {profile?.renueva_solo?.automatica && profile?.renueva_solo?.via === 'stripe' ? (
                                    <span className="text-[#15803D]">
                                        {profile.renueva_solo.cobro
                                            ? `Se renovará automáticamente el día ${pintarDia(profile.renueva_solo.cobro)}`
                                            : 'Se renovará automáticamente'}
                                    </span>
                                ) : profile?.precio_cortesia ? (
                                    /* Misma raíz que el fallo 11: sin esto, a un cliente de
                                       cortesía la ficha le decía «la renovación la confirma él» o
                                       «hoy no se le cobra solo», que es exactamente lo que se lee
                                       antes de llamar a alguien para pedirle el pago. No hay pago
                                       que pedir. */
                                    <span className="text-[#57544E]" data-testid="renovacion-cortesia">
                                        Plan de cortesía: cuando le toque renovar no hay nada que cobrarle
                                    </span>
                                ) : profile?.renueva_solo?.automatica ? (
                                    <span className="text-[#854D0E]">
                                        Su plan dice que renueva automático, pero no tiene suscripción viva
                                        en Stripe: hoy no se le cobra solo
                                    </span>
                                ) : (
                                    <span className="text-[#57544E]">La renovación la confirma él: no se cobra sola</span>
                                )}
                            </p>
                        </div>

                        {/* El ciclo por cliente (punto 44). Es contrato, así que solo el admin. */}
                        {adminUser?.role === 'admin' && (
                            <CicloDelCliente api={api} clientId={clientId} profile={profile}
                                onGuardado={fetchClient} />
                        )}
                        <FechaDeRutina api={api} clientId={clientId} esAdmin={adminUser?.role === 'admin'} />
                    </CardContent></Card>
                    <Card className="bg-white border-[#E5E2DB]"><CardHeader className="pb-2"><CardTitle className="text-sm text-[#6F6B64] uppercase tracking-wider">Historial de pagos</CardTitle></CardHeader>
                        {/* Los cobros llegan de dos sitios con dos formas (punto 5 del 17-08):
                            los de Cobros (`pagos_historicos`: importe/fecha/concepto/origen) y
                            los que escribió el checkout de la app (amount/created_at/status).
                            Se normalizan aquí para que la tarjeta no tenga que saberlo. */}
                        <CardContent>{payments?.length > 0 ? (
                            <div className="space-y-2">{payments.map((p, i) => {
                                // El importe con la coma y sus dos decimales, igual que en
                                // Cobros: «60,50 €», no «60.5€».
                                const bruto = Number(p.importe ?? p.amount);
                                const importe = Number.isFinite(bruto)
                                    ? bruto.toLocaleString('es-ES', { style: 'currency', currency: p.moneda || 'EUR' })
                                    : '-';
                                const cuando = p.fecha || p.created_at;
                                const fallido = p.status ? p.status !== 'success' : p.es_dinero === false;
                                return (
                                <div key={p.id || p.referencia || i} className="flex items-center justify-between p-3 bg-[#F6F5F2] rounded-lg border border-[#E5E2DB] gap-3">
                                    <div className="min-w-0">
                                        <p className="text-[#1C1B19] text-sm font-medium">{importe}</p>
                                        <p className="text-[#6F6B64] text-xs">
                                            {pintarDia(cuando, undefined, '-')}
                                            {p.concepto ? ` · ${p.concepto}` : ''}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                        {p.origen && <span className="text-[#6F6B64] text-[11px] uppercase tracking-wider">{p.origen}</span>}
                                        <Badge className={fallido ? 'bg-[#FEE2E2] text-[#B91C1C] border-0' : 'bg-[#DCFCE7] text-[#15803D] border-0'}>
                                            {fallido ? 'Fallido' : 'Cobrado'}
                                        </Badge>
                                    </div>
                                </div>
                            );})}</div>
                        ) : <p className="text-[#6F6B64] text-sm text-center py-4">Sin pagos registrados</p>}</CardContent>
                    </Card>
                    {calma_raw?.membresia?.length > 0 && <CalmaMembresias membresia={calma_raw.membresia} />}
                </TabsContent>

                {/* ========== TAB 4: REPORTES ========== */}

                {/* ========== TAB 5: CUESTIONARIO ========== */}
                <TabsContent value="cuestionario" className="space-y-4">
                    {(profile?.goal || profile?.weight || profile?.equipment?.length) ? (
                        <Card className="bg-white border-[#E5E2DB]"><CardContent className="p-5">
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                <InfoItem icon={Scale} label="Peso inicial" value={profile?.weight ? `${kilos(profile.weight)} kg` : '-'} />
                                <InfoItem icon={User} label="Sexo" value={sexoLabel(profile?.sex)} />
                                <InfoItem icon={Activity} label="% Graso" value={profile?.body_fat ? `${profile.body_fat}%` : '-'} />
                                <InfoItem icon={Calendar} label="Edad" value={profile?.age || '-'} />
                                <InfoItem icon={Scale} label="Altura" value={profile?.height ? `${profile.height} cm` : '-'} />
                            </div>
                            {/* El objetivo ya no es una respuesta del cuestionario: lo pone el
                                entrenador (fase 2 del 2-09). Es el mismo bloque que en Resumen. */}
                            <ObjetivoDelCliente api={api} clientId={clientId} profile={profile}
                                ciclo={profile?.ciclo_actual || client?.ciclo_actual} onGuardado={fetchClient} />
                            {Array.isArray(profile?.equipment) && profile.equipment.length > 0 && (
                                <div className="mt-4"><p className="text-xs text-[#6F6B64] uppercase tracking-wider mb-2">Equipamiento</p>
                                    <div className="flex flex-wrap gap-1.5">{profile.equipment.map((e, i) => <Badge key={i} className="bg-[#FDEBDF] text-[#B43E06] border-0 text-xs">{equipamientoLabel(e)}</Badge>)}</div>
                                </div>
                            )}
                            {Array.isArray(profile?.injuries) && profile.injuries.length > 0 && (
                                <div className="mt-4"><p className="text-xs text-[#6F6B64] uppercase tracking-wider mb-2">Lesiones</p>
                                    <div className="flex flex-wrap gap-1.5">{profile.injuries.map((l, i) => <Badge key={i} className="bg-[#FEE2E2] text-[#B91C1C] border-0 text-xs">{l}</Badge>)}</div>
                                </div>
                            )}
                        </CardContent></Card>
                    ) : (!calma_raw?.formulario_inicial && <EmptyState icon={ClipboardList} message="Cuestionario pendiente." />)}
                    {/* Las respuestas que deciden sus macros (punto 17). Van ANTES del
                        cuestionario largo porque son las que se miran para ajustar. */}
                    <AjustesDelCuestionario ajustes={profile?.ajustes_macros} />
                    <PreferenciasDeComida prefs={profile?.preferencias_para_ver} />
                    {/* El cuestionario largo. Se rellenaba entero y NO se veía en ninguna
                        parte de la ficha: treinta preguntas de historia, salud, entreno,
                        suplementación y comida que el cliente contestaba para nadie.

                        Y CON LO QUE VIENE DEL BÁSICO (19-08). Media docena de estas preguntas
                        -- su historia de pesos, las dietas de antes, por qué se apuntó, si ha
                        tenido entrenador -- se le hacen a TODO EL MUNDO en el básico desde el
                        18-08, y el básico las guarda en la raíz del perfil, no dentro de
                        `nivel1`. Así que el que no lleva plan personalizado las contestaba y
                        no las veía nadie, igual que antes. Manda lo de `nivel1` cuando está
                        en los dos sitios: es la respuesta del que hizo el largo de verdad. */}
                    <FotosDelAlta api={api} profile={profile} />
                    <FotosYMedidasDelAlta api={api} clientId={clientId} />
                    <PerfilLargo nivel1={{ ...(profile || {}), ...(profile?.nivel1 || {}) }} />
                    {calma_raw?.formulario_inicial && <CalmaCuestionario fi={calma_raw.formulario_inicial} />}
                </TabsContent>

                {/* ========== TAB 6: ENTRENAMIENTO ========== */}
                <TabsContent value="entrenamiento" className="space-y-4">
                    {/* Maquinaria, lesiones y observaciones: EDITABLES aquí (2.5). Es lo que el
                        coach actualiza al leer el reporte, y el entrenamiento lo lleva aparte
                        de la nutrición, así que no tiene sentido que estuvieran de solo lectura. */}
                    <Card className="bg-white border-[#E5E2DB]"><CardContent className="p-5">
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                            <p className="text-xs font-bold text-[#6F6B64] uppercase tracking-wider">Maquinaria, lesiones y observaciones</p>
                            <div className="flex items-center gap-2">
                                {entrenoTocado && <Badge className="border-0 text-[10px] bg-[#FDEBDF] text-[#B43E06]">sin guardar</Badge>}
                                <Button size="sm" onClick={guardarEntreno} disabled={savingEntreno || !entrenoTocado}
                                    className="bg-[#FF671F] hover:bg-[#FF671F]/90 text-white text-xs disabled:opacity-40" data-testid="save-entreno-btn">
                                    {savingEntreno ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Save className="w-3.5 h-3.5 mr-1" />Guardar</>}
                                </Button>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div>
                                <p className="text-xs text-[#6F6B64] uppercase tracking-wider mb-2">Maquinaria disponible</p>
                                <div className="flex flex-wrap gap-1.5">
                                    {EQUIPAMIENTO_OPCIONES.map(o => {
                                        const activo = entrenoForm.equipment.includes(o.value);
                                        return (
                                            <button key={o.value} type="button" data-testid={`equip-${o.value}`}
                                                onClick={() => setEntrenoForm(f => ({ ...f, equipment: alternarMaterial(f.equipment, o.value) }))}
                                                className={`px-2.5 py-1 rounded-lg text-xs font-semibold border transition-colors ${
                                                    activo ? 'bg-[#FDEBDF] text-[#B43E06] border-[#FF671F]/40' : 'bg-[#F6F5F2] text-[#6F6B64] border-[#E5E2DB] hover:text-[#57544E]'}`}>
                                                {o.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                            <div>
                                <p className="text-xs text-[#6F6B64] uppercase tracking-wider mb-2">Lesiones activas</p>
                                <div className="flex flex-wrap gap-1.5 mb-2">
                                    {entrenoForm.injuries.length === 0 && <span className="text-[#6F6B64] text-sm">Sin lesiones</span>}
                                    {entrenoForm.injuries.map((l, i) => (
                                        <span key={i} className="inline-flex items-center gap-1 bg-[#FEE2E2] text-[#B91C1C] rounded-lg px-2 py-1 text-xs">
                                            {l}
                                            <button type="button" onClick={() => setEntrenoForm(f => ({ ...f, injuries: f.injuries.filter((_, j) => j !== i) }))}
                                                className="hover:text-[#1C1B19]"><X className="w-3 h-3" /></button>
                                        </span>
                                    ))}
                                </div>
                                <Input value={nuevaLesion} onChange={e => setNuevaLesion(e.target.value)}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter' && nuevaLesion.trim()) {
                                            e.preventDefault();
                                            if (yaEstaLaLesion(entrenoForm.injuries, nuevaLesion)) {
                                                toast.error('Esa lesión ya está en la lista.');
                                                return;
                                            }
                                            setEntrenoForm(f => ({ ...f, injuries: anadirLesion(f.injuries, nuevaLesion) }));
                                            setNuevaLesion('');
                                        }
                                    }}
                                    placeholder="Escribe una lesión y pulsa Enter" className="bg-white border-[#D6D2C9] text-[#1C1B19] text-sm" data-testid="nueva-lesion" />
                            </div>
                        </div>
                        <div className="mt-4">
                            <Label className="text-[#57544E] text-xs">Observaciones del entrenamiento</Label>
                            <Textarea value={entrenoForm.training_notes} onChange={e => setEntrenoForm(f => ({ ...f, training_notes: e.target.value }))}
                                placeholder="Ej: no le va bien la sentadilla libre, la sustituye por hack…"
                                className="bg-white border-[#D6D2C9] text-[#1C1B19] mt-1 min-h-[60px]" data-testid="entreno-notas" />
                        </div>
                    </CardContent></Card>

                    {/* ELEGIR UNA DE LAS GUARDADAS (17-08-2026). Antes solo se podía escribir
                        a mano aquí o pedírsela a la IA; las que el equipo tiene hechas no se
                        podían reutilizar. Al asignarla se copia, así que retocársela luego a
                        este cliente no se la cambia a los demás. */}
                    <RutinasGuardadas api={api} clientId={clientId} onAsignada={fetchClient} />

                    {/* La rutina en PDF, la que el cliente abre desde Entreno (doc 19-08). */}
                    <PdfDeRutina api={api} clientId={clientId} onInfo={setPdfRutina} />

                    {/* Current routine */}
                    {activeRoutine ? (
                        <Card className="bg-white border-[#E5E2DB]"><CardHeader className="pb-2"><CardTitle className="text-sm text-[#6F6B64] uppercase tracking-wider">Rutina actual</CardTitle></CardHeader>
                            <CardContent><div className="space-y-2">{activeRoutine.days?.map((d, i) => (
                                <div key={i} className="flex items-center justify-between p-2.5 bg-[#F6F5F2] rounded-lg border border-[#E5E2DB]">
                                    <span className="text-[#1C1B19] text-sm font-medium capitalize">{d.day}</span>
                                    {d.is_rest ? <Badge className="bg-[#EDE9FE] text-[#6D28D9] border-0 text-xs">Descanso</Badge> : <span className="text-[#6F6B64] text-xs">{plural(d.exercises?.length || 0, 'ejercicio')}</span>}
                                </div>
                            ))}</div>
                            {activeRoutine.trainer_notes && <p className="text-[#6F6B64] text-xs mt-3 italic">{activeRoutine.trainer_notes}</p>}
                            </CardContent>
                        </Card>
                    ) : pdfEntregado ? (
                        /* Sin estructurada pero con PDF: la rutina SÍ está entregada, solo
                           que en papel. Decir «sin rutina asignada» aquí contradecía al
                           «Rutina en PDF subida» de dos tarjetas más arriba. */
                        <EmptyState icon={FileText}
                            message={pdfEntregado.uploaded_at
                                ? `Rutina entregada en PDF el ${new Date(pdfEntregado.uploaded_at).toLocaleDateString('es-ES')}.`
                                : 'Rutina entregada en PDF.'} />
                    ) : rutina_puesta?.estado === 'del_mes' ? (
                        /* La del mes le llega sola por su plan (14-10): ya la ve en su
                           pantalla, así que no está «sin rutina» aunque no tenga un PDF suyo. */
                        <EmptyState icon={FileText} message="Ve la rutina del mes." />
                    ) : <EmptyState icon={Dumbbell} message="Sin rutina asignada." />}

                    {/* EL INTERRUPTOR DE LA RUTINA SUELTA (César, 17-09). Solo en los planes
                        que la venden aparte; en los demás no se pinta. */}
                    <RutinaSuelta api={api} clientId={clientId} profile={profile}
                        esAdmin={adminUser?.role === 'admin'}
                        habilitaciones={planDelCatalogo(planCatalog, profile?.plan)?.habilitaciones}
                        onGuardado={(cambio) => {
                            if (cambio) setClient(prev => (prev ? { ...prev, profile: { ...prev.profile, ...cambio } } : prev));
                            fetchClient();
                        }} />

                    {/* El cardio pautado ya no se pone desde la ficha (21-10). */}

                    {/* Generate routine */}
                    <Card className="bg-white border-[#FF671F]/20"><CardHeader className="pb-2"><CardTitle className="text-sm text-[#1C1B19] uppercase tracking-wider flex items-center gap-2"><Zap className="w-4 h-4 text-[#B43E06]" />Generar rutina con IA</CardTitle></CardHeader>
                        <CardContent className="space-y-3">
                            <Textarea value={routineInstructions} onChange={e => setRoutineInstructions(e.target.value)} placeholder="Instrucciones para la IA..." className="bg-white border-[#D6D2C9] text-[#1C1B19]" rows={2} data-testid="routine-instructions" />
                            <Button onClick={handleGenerateRoutine} disabled={generatingRoutine} className="bg-[#FF671F] text-white" data-testid="generate-routine-btn">
                                {generatingRoutine ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Generando...</> : <><Zap className="w-4 h-4 mr-1" />Generar</>}
                            </Button>
                            {generatedRoutine && (
                                <div className="p-3 bg-[#F6F5F2] rounded-lg border border-[#D6D2C9] mt-3">
                                    <ScrollArea className="h-48">{generatedRoutine.days?.map((d, i) => <div key={i} className="mb-2"><p className="text-[#1C1B19] text-xs font-bold capitalize">{d.day}</p>{d.is_rest ? <p className="text-[#6F6B64] text-xs">Descanso</p> : <ul className="text-[#57544E] text-xs">{d.exercises?.map((ex, j) => <li key={j}>• {ex.name}: {ex.sets}x{ex.reps}</li>)}</ul>}</div>)}</ScrollArea>
                                    <div className="flex gap-2 mt-3"><Button size="sm" onClick={handleSaveRoutine} disabled={savingRoutine} className="bg-[#FF671F] text-white" data-testid="save-routine-btn">{savingRoutine ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Guardando...</> : <><Save className="w-3 h-3 mr-1" />Guardar</>}</Button><Button size="sm" variant="outline" onClick={() => setGeneratedRoutine(null)} className="bg-transparent border-[#D6D2C9] text-[#1C1B19]">Descartar</Button></div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* ========== TAB: SUPLEMENTOS ========== */}
                <TabsContent value="suplementos" className="space-y-4">
                    {/* La pestaña entera vive en components/panel/SuplementosDelCliente (20-10,
                        como la de Calma). El estado y las llamadas siguen aquí. */}
                    <SuplementosDelCliente
                        key={clientId}
                        protocolo={supProtocol} setProtocolo={setSupProtocol}
                        catalogo={supCatalog}
                        guardando={supSaving} onGuardar={supSave}
                        onAnadir={supAdd} onQuitar={supRemove} onEditar={supEdit}
                        onBorrarVersion={supBorrarVersion}
                    />
                    {calma_raw?.suplementacion && <CalmaSuplementos sup={calma_raw.suplementacion} />}
                    {/* La pantalla del cliente tal cual, con lo guardado (hoja del 21-10, fila 32). */}
                    <AsiLoVeElCliente api={api} clientId={clientId} refresco={client} />
                </TabsContent>

                {/* ========== TAB 7: NUTRICIÓN ========== */}
                <TabsContent value="nutricion" className="space-y-4">
                    {/* ENTRAR EN SU CALCULADORA (punto 4.11). Esta pestaña era de solo
                        lectura: se veían sus dietas, sus alimentos y sus gramos, y no había
                        forma de añadir nada ni de montarle una comida. Desde aquí se abre el
                        MISMO editor que usa él, con una barra naranja arriba que no deja
                        olvidarse de en qué cuenta estás. */}
                    {/* TAMBIÉN EN LA FICHA DE UN ENTRENADOR (Francisco, 29-08): «que tenga el
                        botón de entrar a su calculadora para yo poder ver cómo cargan las cosas
                        cuando reportan un error». El equipo usa la app como cliente y reporta
                        fallos de SUS pantallas; hasta hoy la única forma de verlas era pedirles
                        la clave. El candado que lo impedía se abrió en `core/security.py` solo
                        para admin -> entrenador, así que aquí se pregunta lo mismo que allí: si
                        el backend va a decir que no, no se pinta el botón.
                        Sigue cerrado entrar en un ADMIN, y que un entrenador entre en nadie del
                        equipo -- por eso la condición mira los DOS roles, no solo el de la
                        ficha. */}
                    <Card className="bg-white border-[#E5E2DB]">
                        <CardContent className="p-4 flex items-center gap-3 flex-wrap">
                            <UserCog className="w-5 h-5 text-[#B43E06] shrink-0" />
                            {(user?.role === 'client'
                                || adminUser?.role === 'admin'
                                // Y LA SUYA PROPIA. Cada uno del equipo se encuentra en la lista
                                // de clientes desde el 13-08 (la excepción de una fila de
                                // `_fuera_el_equipo`), y en su ficha el botón abre su propia
                                // calculadora: el backend ni siquiera lo cuenta como suplantar
                                // (`security.py`, «actuar como uno mismo no es actuar»).
                                || profile?.user_id === adminUser?.id) ? (<>
                                <p className="min-w-0 flex-1 text-[#1C1B19] text-sm font-semibold">Crearle el día tú mismo</p>
                                <Button onClick={() => actuarComo({
                                    userId: profile?.user_id,
                                    clientId: clientId,
                                    nombre: user?.name || profile?.name || 'tu cliente',
                                })} data-testid="entrar-en-su-calculadora"
                                    className="bg-[#FF671F] hover:bg-[#FF671F]/90 text-white">
                                    Entrar en su calculadora
                                </Button>
                            </>) : (
                                <p className="min-w-0 flex-1 text-[#1C1B19] text-sm font-semibold">Su ficha sí, su cuenta no</p>
                            )}
                        </CardContent>
                    </Card>
                    {nutrition_stats?.total_diets > 0 ? (<>
                        <Card className="bg-white border-[#E5E2DB]"><CardHeader className="pb-2"><CardTitle className="text-sm text-[#6F6B64] uppercase tracking-wider">Top 5 alimentos</CardTitle></CardHeader>
                            <CardContent><div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">{nutrition_stats.top_foods?.map((f, i) => (
                                <div key={i} className="flex items-center justify-between p-2.5 bg-[#F6F5F2] rounded-lg">
                                    <span className="text-[#1C1B19] text-sm truncate flex-1">{f.nombre}</span>
                                    <Badge className="bg-[#FDEBDF] text-[#B43E06] border-0 text-xs ml-2">{f.count}x</Badge>
                                </div>
                            ))}</div></CardContent>
                        </Card>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <Card className="bg-white border-[#E5E2DB] md:col-span-1"><CardHeader className="pb-2"><CardTitle className="text-sm text-[#6F6B64] uppercase tracking-wider">Dietas ({nutrition_stats.total_diets})</CardTitle></CardHeader>
                                <CardContent>
                                    <ScrollArea className="h-[28rem] pr-2">
                                        <div className="space-y-1">{nutrition_stats.diet_dates?.map((d, i) => (
                                            <button key={i} onClick={() => openDiet(d.fecha)}
                                                className={`w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-left transition-colors ${selectedDietDate === d.fecha ? 'bg-[#FDEBDF] border border-[#FF671F]/40' : 'bg-[#F6F5F2] border border-transparent hover:bg-[#EFECE6]'}`}>
                                                <span className="text-[#1C1B19] text-sm">{_fechaDieta(d.fecha)}</span>
                                                <Badge className={d.tipo_dia === 'entrenamiento' ? 'bg-[#FDEBDF] text-[#B43E06] border-0 text-[10px]' : 'bg-[#DCFCE7] text-[#15803D] border-0 text-[10px]'}>{d.tipo_dia}</Badge>
                                            </button>
                                        ))}</div>
                                    </ScrollArea>
                                </CardContent>
                            </Card>
                            <Card className="bg-white border-[#E5E2DB] md:col-span-2"><CardHeader className="pb-2"><CardTitle className="text-sm text-[#6F6B64] uppercase tracking-wider">{selectedDietDate ? `Dieta del ${_fechaDieta(selectedDietDate, { day: 'numeric', month: 'long', year: 'numeric' })}` : 'Dieta'}</CardTitle></CardHeader>
                                <CardContent>
                                    {dietLoading ? <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#B43E06]" /></div>
                                        : selectedDiet ? <DietDetail diet={selectedDiet} />
                                            : <p className="text-[#6F6B64] text-sm text-center py-10">Elige una fecha.</p>}
                                </CardContent>
                            </Card>
                        </div>
                    </>) : <EmptyState icon={Utensils} message="Sin datos de nutrición aún." />}
                </TabsContent>

                {/* ========== TAB MENÚS (buscador biblioteca + recetario) ========== */}
                <TabsContent value="menus" className="space-y-4">
                    <MenuFinder api={api} clientId={clientId} clientUserId={client?.user_id} clientName={client?.name} />
                </TabsContent>

                {/* ========== TAB: SEGUIMIENTO (evolución de peso + check-ins + reportes) ========== */}
                {/* EN FONDO BLANCO (20-10), con la paleta de Clientes. */}
                <TabsContent value="seguimiento" style={TEMA_CLARO}
                    className="space-y-4 bg-white [color-scheme:light] text-[#1C1B19] rounded-xl p-3 md:p-4">
                    {/* CUATRO PESTAÑAS (21-10). Fuera la Comparativa, las Fotos de los mensuales,
                        la Evolución del cliente y el semáforo de salud («En riesgo»). Cada
                        pestaña solo se monta al abrirla: la del peso y la de los check-ins
                        piden sus datos entonces. */}
                    <Tabs defaultValue="mensuales" className="space-y-4">
                        <div className="overflow-x-auto -mx-1 px-1">
                            <TabsList className="inline-flex w-auto bg-[#F6F5F2] border border-[#E5E2DB] p-1 rounded-xl gap-0.5">
                                {[['mensuales', 'Reportes mensuales'], ['peso', 'Evolución del peso'],
                                  ['medidas', 'Evolución de las medidas'], ['checkins', 'Histórico de Check-ins']].map(([id, nombre]) => (
                                    <TabsTrigger key={id} value={id} data-testid={`seguimiento-${id}`}
                                        className="text-xs px-3 py-2 min-h-[40px] data-[state=active]:bg-[#FF671F] data-[state=active]:text-white data-[state=active]:shadow-none text-[#57544E] hover:text-[#1C1B19] rounded-lg whitespace-nowrap">
                                        {nombre}
                                    </TabsTrigger>
                                ))}
                            </TabsList>
                        </div>

                        {/* Los mensuales en tabla, el feedback del último y los quincenales
                            plegados (todo dentro de ReportsFeedbackList, que es quien tiene los
                            reportes, el feedback y el modal). */}
                        <TabsContent value="mensuales" className="space-y-4 mt-0">
                            <ReportsFeedbackList initialReports={reports} clientId={clientId} profile={profile} onObjetivoAplicado={fetchClient}
                                hojaDeCalma={calma_raw?.ruta_ficha_drive} />
                            {/* SU SEMANA, CONTADA POR ÉL (ficha 16 del artefacto «Mi semana»):
                                «cuando el lunes te sientas a ajustar macros, tienes su semana
                                contada por él». */}
                            <SemanasContadasPorEl api={api} clientId={clientId} />
                            {/* Meter el reporte de un cliente que lo mandó por WhatsApp (punto 45) */}
                            <ReportePorElCliente api={api} clientId={clientId} onHecho={fetchClient} />
                        </TabsContent>

                        {/* Arriba la sección «Peso» y debajo la curva (`PesoDeSeguimiento`). */}
                        <TabsContent value="peso" className="mt-0">
                            <PesoDeSeguimiento api={api} clientId={clientId} reports={reports}
                                macroHistory={macro_history} perfil={profile} />
                        </TabsContent>

                        {/* Las diez medidas comparadas en el tiempo (punto 35), con las que el
                            cliente apuntó por su cuenta y las del alta. */}
                        <TabsContent value="medidas" className="mt-0">
                            <EvolucionMedidas reports={reports} perfil={profile} tono="admin" />
                        </TabsContent>

                        <TabsContent value="checkins" className="mt-0">
                            <CoachCheckins clientId={clientId} />
                        </TabsContent>
                    </Tabs>
                </TabsContent>
            </Tabs>
        </div>
    );
};

// ========== SUB-COMPONENTS ==========

/** «Su semana, contada por él» (ficha 16 del artefacto «Mi semana», Jesús 6-09-2026).
 *
 *  Lo que el cliente valoró el domingo: su estrella, su frase y los números de esa semana.
 *  Los números se guardaron CON la valoración, así que esto es una consulta y no rehace la
 *  semana de nadie.
 *
 *  Si no ha valorado ninguna, el bloque no sale: no es obligatorio rellenarla (ficha 17) y
 *  un recuadro vacío diciendo «sin valoraciones» sería contarle al entrenador lo que el
 *  cliente no hizo, que es justo lo que él pide no hacer.
 */
const SemanasContadasPorEl = ({ api, clientId }) => {
    const [semanas, setSemanas] = useState(null);

    useEffect(() => {
        let vivo = true;
        api.get(`/diets/admin/clients/${clientId}/semanas`)
            .then(r => { if (vivo) setSemanas(r.data?.semanas || []); })
            .catch(() => { if (vivo) setSemanas([]); });
        return () => { vivo = false; };
    }, [api, clientId]);

    if (!semanas || !semanas.length) return null;

    const dia = (iso) => {
        try {
            return new Date(`${iso}T12:00:00`).toLocaleDateString('es-ES',
                { day: 'numeric', month: 'short' });
        } catch { return iso; }
    };

    return (
        <Card className="bg-card border-border" data-testid="semanas-del-cliente">
            <CardHeader>
                <CardTitle className="text-foreground text-base">Sus semanas, contadas por él</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
                {semanas.map(s => {
                    const n = s.numeros || {};
                    const cifras = [
                        n.creados != null ? `${n.creados} de ${n.dias || 7} creados` : null,
                        /* En singular cuando es uno: «1 cerrados» es de las cosas que
                           delatan que lo ha escrito una máquina. */
                        n.cuadrados != null ? `${n.cuadrados} ${n.cuadrados === 1 ? 'cuadrado' : 'cuadrados'}` : null,
                        n.cerrados != null ? `${n.cerrados} ${n.cerrados === 1 ? 'cerrado' : 'cerrados'}` : null,
                        n.kg != null ? `${n.kg > 0 ? '+' : '−'}${Math.abs(n.kg).toString().replace('.', ',')} kg` : null,
                    ].filter(Boolean).join(' · ');
                    return (
                        <div key={s.semana} className="border-b border-border last:border-0 pb-3 last:pb-0"
                            data-testid={`semana-valorada-${s.semana}`}>
                            <div className="flex items-center justify-between gap-3">
                                <p className="text-sm font-semibold text-foreground">
                                    Semana del {dia(s.semana)}
                                </p>
                                <span className="text-sm text-brand whitespace-nowrap">
                                    {'★'.repeat(s.estrellas || 0)}{'☆'.repeat(5 - (s.estrellas || 0))}
                                </span>
                            </div>
                            {cifras && <p className="text-xs text-muted-foreground mt-0.5">{cifras}</p>}
                            {/* Su frase, entera y en cursiva: es lo único de aquí que ha
                                escrito él, y es lo que más vale el lunes. */}
                            {s.nota && (
                                <p className="text-sm text-foreground/80 italic mt-1.5">«{s.nota}»</p>
                            )}
                        </div>
                    );
                })}
            </CardContent>
        </Card>
    );
};


// Un número, o null si no lo es. Acepta la coma decimal, que es como se escribe aquí:
// `Number('25,5')` es NaN, y ese NaN acababa pintado en pantalla (punto 4.18).
const _numeroOno = (v) => {
    if (v === '' || v === null || v === undefined) return null;
    const n = Number(String(v).replace(',', '.'));
    return Number.isFinite(n) ? n : null;
};

const InfoItem = ({ icon: Icon, label, value }) => (
    <div className="flex items-start gap-2 min-w-0">
        <Icon className="w-4 h-4 text-[#B43E06] mt-0.5 flex-shrink-0" />
        {/* `min-w-0` y partir la palabra: un correo largo se salía de su columna en el móvil
            y se cortaba por la derecha. */}
        <div className="min-w-0"><p className="text-[10px] text-[#6F6B64] uppercase tracking-wider">{label}</p><div className="text-[#1C1B19] text-sm font-medium [overflow-wrap:anywhere]">{value}</div></div>
    </div>
);

// QUÉ LLEVA ESTE CLIENTE, DE UN VISTAZO (Francisco, 25-08).
//
// Tres estados y no dos, porque no es lo mismo «no lo lleva» que «lo lleva del montón»:
//   fuerte  -> lo que alguien tiene que HACER a mano (rutina personalizada, protocolo,
//              llamadas). Naranja, que es lo que se busca al abrir la ficha.
//   lleva   -> incluido y automático. Blanco sobre gris.
//   no      -> no lo lleva. Apagado y tachado: se lee sin tener que compararlo con nada.
const LoQueLleva = ({ plan }) => {
    if (!plan) return null;
    const filas = loQueLlevaElPlan(plan);
    return (
        <div className="mt-5 pt-4 border-t border-[#E5E2DB]" data-testid="lo-que-lleva">
            <p className="text-[10px] text-[#6F6B64] uppercase tracking-wider mb-2">Lo que lleva su plan</p>
            <div className="flex flex-wrap gap-1.5">
                {filas.map(f => (
                    <span key={f.clave} data-testid={`lleva-${f.clave}`}
                        title={f.lleva ? 'Incluido en su plan' : 'No lo lleva'}
                        className={`text-xs px-2 py-1 rounded-lg border ${
                            !f.lleva ? 'bg-transparent border-[#E5E2DB] text-[#6F6B64] line-through'
                                : f.fuerte ? 'bg-[#FDEBDF] border-[#FF671F]/40 text-[#B43E06] font-semibold'
                                    : 'bg-white border-[#D6D2C9] text-[#1C1B19]'}`}>
                        {f.etiqueta}
                    </span>
                ))}
            </div>
        </div>
    );
};

// LO QUE HA COMPRADO APARTE (Francisco, 25-08: «si compra cosas por aparte... se podría
// perder que un cliente compró un adicional, su entrenador no lo sabría, y una
// notificación no basta»).
//
// Los tres complementos se apuntaban en la ficha de la base y NO se enseñaban en ninguna
// de las nueve pestañas del panel. El cobro sí acaba saliendo en «Historial de pagos»,
// pero ahí llega cuando pasa la sincronización de Stripe, y además un pago no dice si la
// cosa está entregada. Aquí sale al momento y con su estado, que es lo que necesita el
// que abre la ficha para trabajar.
//
// PENDIENTE se pinta en naranja a propósito: es trabajo sin hacer que alguien ha pagado.
/**
 * EL INTERRUPTOR DEL AJUSTE A MEDIDA (Francisco, 8-09).
 *
 * Al que dijo que sí a los 87 € y no llegó a pagar hay que poder recuperarlo, pero la app no
 * le vuelve a poner la oferta delante sola: la enciende una persona desde aquí, y entonces
 * al cliente le sale su línea en Inicio con el pago. Se apaga solo en cuanto paga.
 */
const OfrecerElAjuste = ({ profile, onCambio }) => {
    const { api } = useAuth();
    const aj = profile?.ajuste_a_medida || {};
    const [puesto, setPuesto] = React.useState(!!aj.ofrecer_en_inicio);
    const [yendo, setYendo] = React.useState(false);
    if (!aj.quiere || aj.cobrado) return null;

    const cambiar = async () => {
        setYendo(true);
        try {
            const r = await api.post(`/admin/clients/${profile.id}/ajuste-a-medida/ofrecer`,
                                     { ofrecer: !puesto });
            setPuesto(!!r.data?.ofrecer_en_inicio);
            toast.success(r.data?.ofrecer_en_inicio
                ? 'Le sale en su Inicio, con el pago.'
                : 'Se lo hemos quitado de Inicio.');
            if (onCambio) onCambio();
        } catch (e) {
            toast.error(mensajeDeError(e, 'No se ha podido cambiar'));
        } finally { setYendo(false); }
    };

    return (
        <div className="flex items-center justify-between gap-3 bg-[#F6F5F2] border border-[#FF671F]/40 rounded-lg px-3 py-2"
            data-testid="aparte-ajuste-sin-pagar">
            <p className="min-w-0 text-[#1C1B19] text-sm font-medium">Quiso el ajuste y no llegó a pagar</p>
            <button type="button" onClick={cambiar} disabled={yendo}
                data-testid="ofrecer-ajuste"
                className={`text-xs px-2.5 py-1.5 rounded-lg border flex-shrink-0 disabled:opacity-40 ${
                    puesto ? 'border-[#D6D2C9] text-[#57544E]'
                           : 'border-[#FF671F]/40 bg-[#FDEBDF] text-[#B43E06] font-semibold'}`}>
                {puesto ? 'Quitarlo de su Inicio' : 'Dejárselo en su Inicio'}
            </button>
        </div>
    );
};

const CompradoAparte = ({ profile }) => {
    const eur = (v) => (v == null ? null : `${Number(v).toFixed(2).replace(/\.00$/, '')} €`);
    const dia = (v) => pintarDia(v, { day: 'numeric', month: 'short' }, null);

    const filas = [];
    const rutina = profile?.rutina_mes_pedida;
    if (rutina && (rutina.cobrado || rutina.cobrado === false)) {
        const modalidad = rutina.modalidad && rutina.modalidad !== 'basica'
            ? ` · ${rutina.modalidad}` : '';
        filas.push({
            clave: 'rutina-mes',
            que: `Rutina del mes${modalidad}`,
            importe: eur(rutina.importe_eur), cuando: dia(rutina.cuando || rutina.fecha),
            // Que se le haya cobrado y que se le haya entregado son dos cosas distintas.
            estado: !rutina.cobrado ? 'Cobro pendiente'
                : rutina.rutina_puesta ? 'Entregada' : 'Pendiente de entregar',
            urge: !rutina.cobrado || !rutina.rutina_puesta,
        });
    }
    const rev = profile?.revision_suelta;
    if (rev?.pagada_at) {
        filas.push({
            clave: 'revision-suelta',
            que: 'Revisión de macros suelta',
            importe: eur(rev.importe_eur), cuando: dia(rev.pagada_at),
            estado: rev.estado === 'entregado' ? 'Entregada'
                : rev.estado === 'cancelado' ? 'Cancelada' : 'Pendiente',
            urge: !['entregado', 'cancelado'].includes(rev.estado),
        });
    }
    const aj = profile?.ajuste_a_medida;
    if (aj?.cobrado) {
        filas.push({
            clave: 'ajuste-a-medida',
            que: `Ajuste a medida${aj.veces > 1 ? ` · ${aj.veces}ª vez` : ''}`,
            importe: eur(aj.importe_eur), cuando: dia(aj.pagado_at),
            estado: aj.estado === 'entregado' ? 'Entregado'
                : aj.estado === 'cancelado' ? 'Cancelado'
                    : aj.reporte?.entrega_antes ? `Antes del ${dia(aj.reporte.entrega_antes)}`
                        : aj.para_el_lunes ? `Para el lunes ${dia(aj.para_el_lunes)}` : 'Pendiente',
            urge: !['entregado', 'cancelado'].includes(aj.estado),
        });
    }
    // El que quiso y no pagó no tiene fila de compra, pero sí tiene que salir el bloque:
    // es justo el que hay que recuperar.
    const quisoSinPagar = !!(aj?.quiere && !aj?.cobrado);
    if (!filas.length && !quisoSinPagar) return null;

    return (
        <div className="mt-4 pt-4 border-t border-[#E5E2DB]" data-testid="comprado-aparte">
            <p className="text-[10px] text-[#6F6B64] uppercase tracking-wider mb-2">
                Comprado aparte
            </p>
            <div className="space-y-1.5">
                <OfrecerElAjuste profile={profile} />
                {filas.map(f => (
                    <div key={f.clave} data-testid={`aparte-${f.clave}`}
                        className="flex items-center justify-between gap-3 bg-[#F6F5F2] border border-[#E5E2DB] rounded-lg px-3 py-2">
                        <div className="min-w-0">
                            <p className="text-[#1C1B19] text-sm font-medium truncate">{f.que}</p>
                            <p className="text-[11px] text-[#6F6B64]">
                                {[f.importe, f.cuando].filter(Boolean).join(' · ')}
                            </p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-lg border flex-shrink-0 ${
                            f.urge ? 'bg-[#FDEBDF] border-[#FF671F]/40 text-[#B43E06] font-semibold'
                                : 'bg-transparent border-[#D6D2C9] text-[#57544E]'}`}>
                            {f.estado}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

// Bloque de macros editable (entrenamiento / perientreno / descanso). Cuando el
// coach cambia un valor, debajo de la etiqueta queda el que hay guardado ahora.
// «MACROS NUEVOS» COMO UNA TABLA (lienzo del 19-10): una fila por tipo de día y una columna
// por macro, y debajo de cada casilla lo que cambia respecto a los actuales. El peri no
// lleva grasa.
const _COLUMNAS_MACROS = [['protein', 'Proteína', 'text-orange-400'], ['carbs', 'Hidratos', 'text-blue-400'], ['fat', 'Grasa', 'text-yellow-400']];
const _FILAS_MACROS = [['training', 'Entrenamiento', 'Entreno'], ['rest', 'Descanso', 'Descanso'], ['peri', 'Perientreno', 'Peri']];
const TablaMacrosNuevos = ({ form, actuales, onCambio }) => (
    <div className="overflow-x-auto">
        <table className="w-full max-w-[640px] min-w-[290px] border-separate border-spacing-y-1" data-testid="tabla-macros-nuevos">
            <thead>
                <tr>
                    <th scope="col"><span className="sr-only">Tipo de día</span></th>
                    {_COLUMNAS_MACROS.map(([k, t, c]) => (
                        <th key={k} scope="col" className={`text-[10px] font-semibold uppercase tracking-wider ${c}`}>{t}</th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {_FILAS_MACROS.map(([b, tipo, corto]) => (
                    <tr key={b}>
                        <th scope="row" className="text-left text-xs font-bold text-[#1C1B19] pr-2 sm:pr-3 whitespace-nowrap"><span className="sm:hidden">{corto}</span><span className="hidden sm:inline">{tipo}</span></th>
                        {_COLUMNAS_MACROS.map(([k, t]) => {
                            if (!(k in (actuales[b] || {}))) return <td key={k} className="px-1 text-center text-[#6F6B64]">-</td>;
                            const valor = form[b]?.[k] ?? '';
                            const actual = actuales[b][k];
                            const d = valor !== '' && actual !== '' ? Number(valor) - Number(actual) : 0;
                            return (
                                <td key={k} className="px-0.5 sm:px-1 align-top">
                                    <div className="relative">
                                        <Input type="number" min="0" value={valor} aria-label={`${t} en ${tipo.toLowerCase()}`}
                                            onChange={e => onCambio(b, k, e.target.value)}
                                            data-testid={b === 'training' && k === 'protein' ? 'macro-input-tp' : undefined}
                                            className={`h-9 pl-2 pr-5 sm:pr-6 text-right text-sm font-bold tabular-nums bg-white text-[#1C1B19] [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${String(valor) !== String(actual) ? 'border-[#FF671F]/60' : 'border-[#D6D2C9]'}`} />
                                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[#6F6B64] text-xs pointer-events-none">g</span>
                                    </div>
                                    <span className={`block text-right text-[10px] min-h-[13px] tabular-nums ${d > 0 ? 'text-[#15803D]' : 'text-[#B91C1C]'}`}>
                                        {d && !isNaN(d) ? `${d > 0 ? '+' : '-'}${Math.abs(d)} g` : ''}
                                    </span>
                                </td>
                            );
                        })}
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

const _mv = (m, keys) => { for (const k of keys) if (m && m[k] != null) return Math.round(m[k]); return 0; };

// Fecha por la que se ordena y filtra: manda effective_date (desde cuando aplican
// esos macros); si la entrada es antigua y no la trae, se usa el created_at.
const _fechaEntrada = fechaDeLaFila;
const _fechaCorta = (f) => f ? f.split('-').reverse().join('/') : '-';

// Celdas P/H/G de un bloque de macros dentro de la tabla del historial.
// EN ROJO LO QUE CAMBIÓ respecto al ajuste anterior, como en Calma: el coach no lee
// números sueltos, lee la escalera. Con todo del mismo color hay que comparar fila a
// fila a ojo; en rojo, el cambio salta solo (vídeo del 05-08, minuto 1:47).
//
// Desde el punto 31 (07-08) el cambio viene GUARDADO en la entrada (`cambios`), calculado
// al guardar contra los macros que el cliente tenía en ese momento. Se usa ese cuando está,
// porque es el que dice la verdad; comparar con la fila de arriba solo acierta si la tabla
// está entera y en orden. Para las entradas viejas y para la fila sin guardar se sigue
// comparando, que es lo que había.
const _CAMPOS = [['protein', 'proteinas'], ['carbs', 'hidratos'], ['fat', 'grasas']];
const _NOMBRE_CAMPO = ['proteina', 'hidratos', 'grasa'];
const MacroCeldas = ({ m, prev, showG = true, apagado = false, cambios = null }) => (
    <>
        {(showG ? _CAMPOS : _CAMPOS.slice(0, 2)).map((keys, i) => {
            const v = m ? _mv(m, keys) : null;
            const p = prev ? _mv(prev, keys) : null;
            const cambio = cambios
                ? !!cambios[_NOMBRE_CAMPO[i]]
                : (v != null && p != null && v !== p);
            return (
                // Lo que cambió, en rojo y en negrita, como en Calma (20-10); lo demás, normal.
                <td key={i} className={`px-1.5 py-1 text-right tabular-nums ${
                    apagado ? 'text-[#6F6B64]' : cambio ? 'text-[#C8102E] font-bold' : 'text-[#57544E]'}`}
                    title={cambio && p != null ? `antes ${p} g (${v > p ? '+' : ''}${v - p})` : undefined}>
                    {v ?? '-'}
                </td>
            );
        })}
    </>
);

// LA EVOLUCIÓN DE CADA MEDIDA (punto 35 del doc del 07-08) vive ahora en
// `components/EvolucionMedidas.jsx`, compartida con la pantalla de Evolución del cliente
// (T6 del doc 16-08): es la misma tabla y tiene que decir lo mismo en los dos sitios. Aquí
// se pinta con `tono="admin"`, que es lo único que cambiaba.

// LA EXCEPCIÓN DEL CLIENTE (punto 39 del doc del 07-08).
//
// Hay 17 clientes con una excepción apuntada a mano en una hoja, y no se parecen entre sí:
// uno cuya membresía paga su marido, uno que paga en efectivo, uno al que no se le genera
// rutina, uno que no paga nada y aun así se le hace, uno con ciclo de 4 semanas en vez de
// 12, otro al que se le manda el reporte por WhatsApp. Por eso es texto libre y no un juego
// de casillas: modelarlas sería inventarse las categorías antes de conocerlas, y la de la
// número 18 no entraría en ninguna.
//
// Cuando hay excepción se ve SIEMPRE, en naranja y arriba del todo. Cuando no la hay, un
// enlace pequeño que no molesta: la mayoría de los clientes no tienen ninguna.
// LAS TAREAS SOBRE ESTE CLIENTE (doc 19-08, apartado 05): las pendientes, el registro de
// lo hecho, y el botón de asignar con «sobre quién» ya rellenado.
const TareasDelCliente = ({ api, clientId, nombre }) => {
    const [datos, setDatos] = useState(null);
    const [modal, setModal] = useState(false);
    const [verHechas, setVerHechas] = useState(false);

    const cargar = useCallback(() => {
        api.get(`/admin/tareas/de-cliente/${clientId}`)
            .then(r => setDatos(r.data)).catch(() => setDatos({ pendientes: [], hechas: [] }));
    }, [api, clientId]);
    useEffect(() => { cargar(); }, [cargar]);

    const marcar = async (t) => {
        try {
            await api.put(`/admin/tareas/${t.id}/hecha`, { hecha: !t.hecha });
            cargar();
        } catch (e) { toast.error(mensajeDeError(e, 'No se pudo marcar')); }
    };

    const Fila = ({ t }) => (
        <div className="flex items-start gap-2.5 py-2 border-b border-[#E5E2DB] last:border-0">
            <button onClick={() => marcar(t)} title={t.hecha ? 'Deshacer' : 'Marcar hecha'}
                className={t.hecha ? 'text-[#15803D] mt-0.5' : 'text-[#6F6B64] hover:text-[#B43E06] mt-0.5'}>
                <CheckCircle2 className="w-5 h-5" />
            </button>
            <div className="min-w-0">
                <p className={`text-sm [overflow-wrap:anywhere] ${t.hecha ? 'text-[#6F6B64] line-through' : 'text-[#1C1B19]'}`}>{t.que}</p>
                <p className="text-[11px] text-[#6F6B64]">
                    {t.a_quien_nombre && `para ${t.a_quien_nombre}`}
                    {/* Las fechas, dichas (16 oct) y no como vienen de la base (12-10). */}
                    {t.para_cuando && ` · para el ${pintarDia(t.para_cuando, { day: 'numeric', month: 'short' }, t.para_cuando)}`}
                    {t.hecha && t.hecha_at && ` · hecha el ${pintarDia(t.hecha_at, { day: 'numeric', month: 'short' }, String(t.hecha_at).slice(0, 10))}${t.hecha_por_nombre ? ` por ${t.hecha_por_nombre}` : ''}`}
                    {!t.hecha && t.origen !== 'manual' && ' · automática'}
                </p>
            </div>
        </div>
    );

    return (
        <Card className="bg-white border-[#E5E2DB] mt-4" data-testid="tareas-del-cliente">
            <CardContent className="p-5">
                <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-[#6F6B64] uppercase tracking-wider font-bold">Tareas</p>
                    <Button size="sm" onClick={() => setModal(true)} data-testid="asignar-tarea-ficha"
                        className="bg-[#FF671F] hover:bg-[#FF671F]/90 text-white text-xs h-7 px-2">
                        Asignar tarea
                    </Button>
                </div>
                {!datos ? <p className="text-[#6F6B64] text-sm">Cargando…</p> : (
                    <>
                        {datos.pendientes.length === 0 && (
                            <p className="text-[#6F6B64] text-sm">Nada pendiente sobre este cliente.</p>
                        )}
                        {datos.pendientes.map(t => <Fila key={t.id} t={t} />)}
                        {datos.hechas.length > 0 && (
                            <button onClick={() => setVerHechas(v => !v)}
                                className="text-xs text-[#6F6B64] hover:text-[#1C1B19] mt-2">
                                {verHechas ? 'Ocultar el registro' : `Registro (${datos.hechas.length} hechas)`}
                            </button>
                        )}
                        {verHechas && datos.hechas.map(t => <Fila key={t.id} t={t} />)}
                    </>
                )}
            </CardContent>
            <AsignarTarea abierto={modal} onClose={() => setModal(false)}
                sobre={[{ id: clientId, nombre }]} onCreada={cargar} />
        </Card>
    );
};


const ExcepcionDelCliente = ({ excepcion, guardando, onGuardar }) => {
    const [editando, setEditando] = useState(false);
    const [texto, setTexto] = useState(excepcion || '');
    useEffect(() => { setTexto(excepcion || ''); }, [excepcion]);

    if (!excepcion && !editando) {
        return (
            <button onClick={() => setEditando(true)} data-testid="anadir-excepcion"
                className="text-xs text-[#6F6B64] hover:text-[#B43E06] flex items-center gap-1.5">
                <AlertCircle className="w-3.5 h-3.5" /> Añadir una excepción
            </button>
        );
    }

    return (
        <div className="rounded-xl border border-[#FF671F]/50 bg-[#FDEBDF] p-4" data-testid="excepcion-cliente">
            <div className="flex items-start gap-2.5">
                <AlertCircle className="w-5 h-5 text-[#B43E06] shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-bold text-[#B43E06] uppercase tracking-wider mb-1">Excepción de este cliente</p>
                    {editando ? (
                        <>
                            <Textarea value={texto} onChange={e => setTexto(e.target.value)} autoFocus
                                placeholder="Ej: la membresía se la paga su marido · paga en efectivo · no se le genera rutina · ciclo de 4 semanas · el reporte se lo manda por WhatsApp"
                                data-testid="excepcion-texto"
                                className="bg-white border-[#D6D2C9] text-[#1C1B19] text-sm" rows={2} />
                            <div className="flex items-center gap-2 mt-2">
                                <Button size="sm" onClick={() => { onGuardar(texto); setEditando(false); }} disabled={guardando}
                                    className="bg-[#FF671F] hover:bg-[#FF671F]/90 text-white text-xs" data-testid="guardar-excepcion">
                                    {guardando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Save className="w-3.5 h-3.5 mr-1" />Guardar</>}
                                </Button>
                                <button onClick={() => { setTexto(excepcion || ''); setEditando(false); }}
                                    className="text-xs text-[#6F6B64] hover:text-[#1C1B19]">Cancelar</button>
                            </div>
                        </>
                    ) : (
                        <div className="flex items-start justify-between gap-3">
                            <p className="text-[#1C1B19] text-sm whitespace-pre-wrap">{excepcion}</p>
                            <button onClick={() => setEditando(true)} className="text-[#6F6B64] hover:text-[#1C1B19] shrink-0" title="Editar la excepción">
                                <Pencil className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// METER EL REPORTE DE UN CLIENTE QUE LO MANDÓ POR OTRA VÍA (punto 45 del doc del 07-08).
//
// Los Premium no rellenan el formulario: mandan el reporte y las fotos por WhatsApp y alguien
// del equipo se lo pasa a la app. Hasta ahora eso solo se podía hacer entrando con la cuenta
// del cliente - "subiendo las fotos con el correo del cliente para que se enlacen a su ficha",
// dice el punto -, y lo que no se metía se perdía: ni curva de peso, ni comparativa, ni modelo.
//
// Va plegado porque no es lo normal: se abre cuando toca.
const ReportePorElCliente = ({ api, clientId, onHecho }) => {
    const [abierto, setAbierto] = useState(false);
    const [guardando, setGuardando] = useState(false);
    const [subiendo, setSubiendo] = useState(false);
    const [datos, setDatos] = useState({ weight: '', notes: '', proximo_objetivo: '', measurements: {} });
    const [fotos, setFotos] = useState([]);

    const guardar = async () => {
        const peso = parseFloat(datos.weight);
        if (isNaN(peso) || peso < 25 || peso > 300) { toast.error('Pon su peso (25-300 kg)'); return; }
        setGuardando(true);
        try {
            const medidas = Object.fromEntries(
                Object.entries(datos.measurements).filter(([, v]) => v !== '' && v != null)
                    .map(([k, v]) => [k, parseFloat(v)]));
            await api.post(`/admin/clients/${clientId}/reporte`, {
                weight: peso,
                measurements: Object.keys(medidas).length ? medidas : null,
                notes: datos.notes || null,
                proximo_objetivo: datos.proximo_objetivo || null,
            });
            toast.success('Reporte guardado', { description: 'Queda anotado que lo metiste tú.' });
            setDatos({ weight: '', notes: '', proximo_objetivo: '', measurements: {} });
            setFotos([]);
            setAbierto(false);
            onHecho?.();
        } catch (e) { toast.error(mensajeDeError(e, 'No se pudo guardar el reporte')); }
        finally { setGuardando(false); }
    };

    const subirFoto = async (archivo, pose) => {
        if (!archivo) return;
        setSubiendo(true);
        try {
            const fd = new FormData();
            fd.append('file', archivo);
            const r = await api.post(
                `/admin/clients/${clientId}/reports/photos?pose=${encodeURIComponent(pose || '')}`,
                fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            setFotos(f => [...f, { ...r.data, pose }]);
            toast.success(`Foto subida${pose ? ` (${pose})` : ''}`);
        } catch (e) { toast.error(mensajeDeError(e, 'No se pudo subir la foto')); }
        finally { setSubiendo(false); }
    };

    if (!abierto) {
        return (
            <button onClick={() => setAbierto(true)} data-testid="abrir-reporte-por-el"
                className="text-xs text-[#57544E] hover:text-[#B43E06] flex items-center gap-1.5">
                <FileText className="w-3.5 h-3.5" /> Meter un reporte por él (llegó por WhatsApp)
            </button>
        );
    }

    return (
        <Card className="bg-white border-[#FF671F]/30" data-testid="reporte-por-el-cliente">
            <CardContent className="p-5 space-y-3">
                <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-bold text-[#57544E] uppercase tracking-wider">Reporte en su nombre</p>
                    <button onClick={() => setAbierto(false)} className="text-[#57544E]/70 hover:text-[#1C1B19]"><X className="w-4 h-4" /></button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                        <Label className="text-[#57544E] text-xs">Peso (kg)</Label>
                        <Input type="number" step="0.1" min="25" max="300" value={datos.weight}
                            onChange={e => setDatos(d => ({ ...d, weight: e.target.value }))}
                            data-testid="reporte-peso" className="bg-[#F6F5F2] border-[#E5E2DB] text-[#1C1B19] mt-1" />
                    </div>
                    <div>
                        <Label className="text-[#57544E] text-xs">Objetivo que marca</Label>
                        <select value={datos.proximo_objetivo}
                            onChange={e => setDatos(d => ({ ...d, proximo_objetivo: e.target.value }))}
                            className="w-full bg-[#F6F5F2] border border-[#E5E2DB] text-[#1C1B19] text-sm rounded-lg px-2 py-2 mt-1">
                            {/* Las seis de la lista cerrada (fase 2 del 2-09), no las tres de
                                antes: es la misma pregunta que contesta el cliente en su
                                reporte, y las dos listas tienen que ser la misma. */}
                            <option value="">Sin cambio</option>
                            {OBJETIVOS.map(o => <option key={o.clave} value={o.clave}>{o.nombre}</option>)}
                        </select>
                    </div>
                </div>

                {/* Las diez medidas, opcionales: en WhatsApp no siempre llegan todas. */}
                <details className="rounded-lg border border-[#E5E2DB] bg-[#F6F5F2] p-3">
                    <summary className="text-xs text-[#57544E] cursor-pointer">Medidas (opcionales)</summary>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3">
                        {MEDIDAS.map(({ key, label }) => (
                            <div key={key}>
                                <Label className="text-[#57544E] text-[10px]">{label}</Label>
                                <Input type="number" step="0.1" value={datos.measurements[key] ?? ''}
                                    onChange={e => setDatos(d => ({ ...d, measurements: { ...d.measurements, [key]: e.target.value } }))}
                                    className="bg-white border-[#E5E2DB] text-[#1C1B19] text-xs h-8 mt-0.5" />
                            </div>
                        ))}
                    </div>
                </details>

                {/* Las fotos, con su pose: sin pose son tres fotos sueltas, con ella son la
                    misma foto de tres meses distintos y entran en la comparativa. */}
                <div className="rounded-lg border border-[#E5E2DB] bg-[#F6F5F2] p-3">
                    <p className="text-xs text-[#57544E] mb-2">Fotos {subiendo && <Loader2 className="w-3 h-3 animate-spin inline ml-1" />}</p>
                    <div className="flex flex-wrap gap-2">
                        {['frente', 'espalda', 'perfil'].map(pose => (
                            <label key={pose} className="px-3 py-1.5 rounded-lg bg-white border border-[#E5E2DB] text-xs text-[#57544E] cursor-pointer hover:border-[#FF671F]/50">
                                <Camera className="w-3.5 h-3.5 inline mr-1" />{pose}
                                <input type="file" accept="image/*" className="hidden" data-testid={`foto-${pose}`}
                                    onChange={e => { subirFoto(e.target.files?.[0], pose); e.target.value = ''; }} />
                            </label>
                        ))}
                    </div>
                    {fotos.length > 0 && (
                        <p className="text-[11px] text-emerald-700 mt-2">
                            {fotos.length} {fotos.length === 1 ? 'foto subida' : 'fotos subidas'}: {fotos.map(f => f.pose).join(', ')}
                        </p>
                    )}
                </div>

                <div>
                    <Label className="text-[#57544E] text-xs">Lo que ha contado</Label>
                    <Textarea value={datos.notes} onChange={e => setDatos(d => ({ ...d, notes: e.target.value }))}
                        placeholder="Pega aquí lo que te ha escrito por WhatsApp…" rows={3}
                        data-testid="reporte-notas" className="bg-[#F6F5F2] border-[#E5E2DB] text-[#1C1B19] mt-1" />
                </div>

                <div className="flex justify-end">
                    <Button onClick={guardar} disabled={guardando} data-testid="guardar-reporte-por-el"
                        className="bg-[#FF671F] hover:bg-[#FF671F]/90 text-white">
                        {guardando ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" />Guardar el reporte</>}
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
};

const CAUSA_LABEL = { ajuste: 'fallo del ajuste', cliente: 'no cumplió', otro: 'otros motivos' };

// Respuestas de las tres preguntas del reporte (punto 5 del 05-08), en legible.
/**
 * «CRITERIO INTERNO» Y «FEEDBACK» VACÍOS SE QUEDAN VACÍOS (vídeo de Jesús del 20-10: «esto
 * de importado de Calma, de verdad, elimínalo. Todo el ruido de texto hay que eliminarlo»).
 * Desde el doc del 09-08 la celda vacía decía por qué («importado de Calma», «lo calculó el
 * cuestionario de alta»...) en cada fila; gana lo de Jesús, que es lo más reciente. De dónde
 * viene la fila (`lib/historialDeMacros.nombreDelOrigen`) se lee al pasar por su fecha. Lo
 * que escribió el entrenador sí sale.
 */
const CeldaHistorial = ({ texto, testid }) => (
    <td className="px-2 py-1 text-[#57544E] text-xs max-w-[200px]" data-testid={testid}>
        {texto && <span className="block truncate" title={texto}>{texto}</span>}
    </td>
);

// Lo que el cliente marcó en el reporte, con su nombre de la lista (fase 2 del 2-09): los
// reportes viejos traen «definicion» o «volumen» y los nuevos las seis claves nuevas. Se lee
// con el nombre que tendría al aplicarse, para que lo que se ve sea lo que se aplica.
const nombreDeLaPropuesta = (clave) => nombreDelObjetivo(clave) || objetivoLabel(clave);
const VIABILIDAD_REPORTE = {
    me_adapto: 'se adapta a lo que le pongas',
    necesito_mas: 'necesita comer MÁS para cumplir',
    necesito_menos: 'necesita comer MENOS para cumplir',
};
const ENTRENO_REPORTE = {
    todos: 'todos', casi_todos: 'casi todos', la_mitad: 'la mitad', pocos: 'pocos', ninguno: 'ninguno',
};

const EvaluacionBadge = ({ ev }) => (
    <span className={`text-xs font-semibold ${ev.resultado === 'buena' ? 'text-[#15803D]' : 'text-[#B91C1C]'}`}
        title={[ev.resultado === 'buena' ? 'Fase buena' : 'Fase mala', CAUSA_LABEL[ev.causa], ev.nota].filter(Boolean).join(' · ')}>
        {ev.resultado === 'buena' ? 'Buena' : 'Mala'}
        {ev.causa && <span className="text-[#6F6B64] font-normal"> · {CAUSA_LABEL[ev.causa]}</span>}
    </span>
);

// «24 SEPT 2026» en la etiqueta azul del histórico (la mayúscula la pone la clase).
const FECHA_DEL_HISTORICO = { day: 'numeric', month: 'short', year: 'numeric' };

// Historial de macros en tabla, con filtro por rango de fechas.
// Las versiones que una fila sustituyó ese mismo día (`versiones_del_dia`, F2 del 12-10) se
// pintan como filas propias debajo de la vigente: qué filas salen y contra cuál se compara
// cada una lo decide `lib/historialDeMacros`.
const MacroHistoryTable = ({ items, programados, cambios, onEdit, onRepeat, onDelete, onEvaluar, borrador }) => {
    const [desde, setDesde] = useState('');
    const [hasta, setHasta] = useState('');
    // El ajuste que se está escribiendo abajo entra como una fila más, en gris, en el sitio
    // que le toca por fecha: así se ve cómo queda la escalera ANTES de guardar.
    const todas = useMemo(
        // Los programados (todavía no rigen) van arriba, marcados, y no cuentan como cambio.
        () => [...(items || []), ...(programados || []).map(p => ({ ...p, _programado: true })),
            ...(borrador ? [borrador] : [])]
            .sort((a, b) => _fechaEntrada(b).localeCompare(_fechaEntrada(a))),
        [items, programados, borrador]);
    // Los guardados, no las filas: tres guardados el mismo día son una fila y tres cambios.
    const totalCambios = cambios ?? (items || []).reduce((n, h) => n + 1 + versionesDelDia(h).length, 0);
    // Peso máximo y mínimo del recorrido. Se marca UNA sola fila de cada: con 170 ajustes
    // hay empates de sobra y pintar quince "máximos" no señala nada.
    const { filaMax, filaMin } = useMemo(() => {
        let max = null, min = null;
        for (const h of (items || [])) {
            const p = h.peso ?? h.client_weight;
            if (typeof p !== 'number') continue;
            if (!max || p > (max.peso ?? max.client_weight)) max = h;
            if (!min || p < (min.peso ?? min.client_weight)) min = h;
        }
        return { filaMax: max, filaMin: min };
    }, [items]);
    const filas = useMemo(() => todas.filter(h => {
        const f = _fechaEntrada(h);
        return !((desde && f < desde) || (hasta && f > hasta));
    }), [todas, desde, hasta]);
    // La tabla va arriba para poder comparar antes de ajustar, pero un cliente con 170
    // ajustes dejaría el editor a media pantalla de scroll. Por defecto se ven los últimos
    // (que es con los que se compara) y el resto se despliega, como el "ocultar macros" de Calma.
    const [verTodo, setVerTodo] = useState(false);
    const RECIENTES = 12;
    const visibles = useMemo(() => {
        if (verTodo) return filas;
        const corte = filas.slice(0, RECIENTES);
        // El ajuste en curso se ve SIEMPRE, aunque su fecha lo mande fuera del recorte:
        // si el coach lo pone vigente desde una fecha vieja, la fila no puede desaparecer.
        const b = filas.find(h => h._borrador);
        return (!b || corte.includes(b)) ? corte
            : [...corte, b].sort((x, y) => _fechaEntrada(y).localeCompare(_fechaEntrada(x)));
    }, [filas, verTodo]);
    const filtrando = !!(desde || hasta);
    // La que rige hoy lleva «ACTUAL» (vídeo de Jesús, 20-10). De la lista entera: el filtro
    // de fechas no cambia cuál rige.
    const rige = useMemo(() => laQueRige(todas), [todas]);
    const inputFecha = "bg-white border border-[#D6D2C9] text-[#1C1B19] text-sm rounded-lg px-2 py-1.5 focus:outline-none focus:border-[#FF671F]";

    return (
        <Card className="bg-white border-[#E5E2DB] text-[#1C1B19]">
            <CardHeader className="pb-2">
                <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
                    <CardTitle className="text-sm text-[#6F6B64] uppercase tracking-wider flex items-center gap-2">
                        <History className="w-4 h-4" />Historial de macros
                    </CardTitle>
                    <div className="flex flex-wrap items-center gap-2">
                        {/* El ajuste en curso no cuenta como cambio: todavía no está guardado */}
                        <span className="text-[#6F6B64] text-xs tabular-nums">{filtrando ? `${filas.length} de ${(items || []).length}` : `${totalCambios} ${totalCambios === 1 ? 'cambio' : 'cambios'}`}</span>
                        <input type="date" value={desde} max={hasta || undefined} onChange={e => setDesde(e.target.value)} title="Desde" className={inputFecha} data-testid="macro-hist-desde" />
                        <span className="text-[#6F6B64] text-sm">a</span>
                        <input type="date" value={hasta} min={desde || undefined} onChange={e => setHasta(e.target.value)} title="Hasta" className={inputFecha} data-testid="macro-hist-hasta" />
                        {filtrando && (
                            <button onClick={() => { setDesde(''); setHasta(''); }} title="Quitar filtro"
                                className="p-1 rounded text-[#6F6B64] hover:text-[#1C1B19] hover:bg-[#EFECE6]"><X className="w-3.5 h-3.5" /></button>
                        )}
                    </div>
                </div>
            </CardHeader>
            <CardContent>
                {todas.length === 0 ? (
                    <p className="text-[#6F6B64] text-sm text-center py-4">Sin cambios registrados</p>
                ) : filas.length === 0 ? (
                    <p className="text-[#6F6B64] text-sm text-center py-4">Ningún cambio entre esas fechas</p>
                ) : (
                    <div className="overflow-x-auto -mx-2">
                        <table className="w-full text-sm min-w-[880px]">
                            <thead>
                                <tr className="text-[#6F6B64] text-xs uppercase tracking-wider border-b border-[#E5E2DB]">
                                    <th rowSpan={2} className="px-2 py-1.5 text-left font-medium">Vigente desde</th>
                                    <th rowSpan={2} className="px-2 py-1.5 text-right font-medium">Peso<span className="block normal-case">y % graso</span></th>
                                    <th rowSpan={2} className="px-2 py-1.5 text-right font-medium">Rutina</th>
                                    <th colSpan={3} className="px-2 py-1.5 text-center font-bold border-l border-[#E5E2DB]" style={{ color: '#C2410C' }}>Entrenamiento</th>
                                    <th colSpan={2} className="px-2 py-1.5 text-center font-bold border-l border-[#E5E2DB]" style={{ color: '#A16207' }}>Perientreno</th>
                                    <th colSpan={3} className="px-2 py-1.5 text-center font-bold border-l border-[#E5E2DB]" style={{ color: '#15803D' }}>Descanso</th>
                                    <th rowSpan={2} className="px-2 py-1.5 text-left font-medium border-l border-[#E5E2DB]">Criterio</th>
                                    <th rowSpan={2} className="px-2 py-1.5 text-left font-medium">Feedback</th>
                                    <th rowSpan={2} className="px-2 py-1.5 text-left font-medium border-l border-[#E5E2DB]">Cómo salió</th>
                                    {(onEdit || onRepeat || onDelete) && <th rowSpan={2} className="px-2 py-1.5" />}
                                </tr>
                                <tr className="text-[#6F6B64] text-xs uppercase border-b border-[#E5E2DB]">
                                    <th className="px-2 pb-1.5 text-right font-medium border-l border-[#E5E2DB]">P</th><th className="px-2 pb-1.5 text-right font-medium">H</th><th className="px-2 pb-1.5 text-right font-medium">G</th>
                                    <th className="px-2 pb-1.5 text-right font-medium border-l border-[#E5E2DB]">P</th><th className="px-2 pb-1.5 text-right font-medium">H</th>
                                    <th className="px-2 pb-1.5 text-right font-medium border-l border-[#E5E2DB]">P</th><th className="px-2 pb-1.5 text-right font-medium">H</th><th className="px-2 pb-1.5 text-right font-medium">G</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filasQueSePintan(visibles, filas).map(({ fila: h, anterior: ant, cambios, sustituida, clave }) => {
                                    const peso = h.peso ?? h.client_weight;
                                    const peri = h.peri || h.macros_periworkout;
                                    const nota = h.note && h.note !== 'Importado de Calma' ? h.note : '';
                                    // `ant` es la fila inmediatamente ANTERIOR en el tiempo (la de debajo), que
                                    // puede ser una versión del mismo día. Sale de la lista entera, no de las
                                    // visibles: si no, la última fila a la vista no tendría con qué compararse.
                                    const antPeri = ant && (ant.peri || ant.macros_periworkout);
                                    const esBorrador = !!h._borrador;
                                    const origen = nombreDelOrigen(h);
                                    const esMax = h === filaMax, esMin = h === filaMin;
                                    const tonoPeso = esMax ? 'bg-[#FEE2E2] text-[#B91C1C]'
                                        : esMin ? 'bg-[#DCFCE7] text-[#15803D]'
                                        : 'text-[#B43E06]';
                                    return (
                                        <tr key={clave} data-testid={esBorrador ? 'macro-fila-borrador' : sustituida ? 'macro-fila-sustituida' : undefined}
                                            className={`border-b border-[#1a1a1a] last:border-0 ${
                                                esBorrador ? 'bg-[#EFECE6] border-dashed border-[#FF671F]/40'
                                                : sustituida ? 'bg-[#FAF9F7]' : 'hover:bg-[#FAF9F7]'}`}>
                                            <td className="px-2 py-1 whitespace-nowrap font-medium tabular-nums">
                                                {/* La fecha en la etiqueta azul de Calma (20-10): «24 SEPT 2026». */}
                                                {/* El origen, al pasar (prueba 14; sin texto en la fila desde el 20-10). */}
                                                <span className={`inline-block rounded px-1.5 py-0.5 text-xs font-bold uppercase tracking-wide text-white ${esBorrador || sustituida ? 'bg-[#7FA9E6]' : 'bg-[#1D6FD8]'}`}
                                                    title={origen && !esBorrador ? `Origen: ${origen}` : undefined}
                                                    data-testid="macro-fila-fecha">{pintarDia(_fechaEntrada(h), FECHA_DEL_HISTORICO, '-')}</span>
                                                {h === rige && (
                                                    <span className="ml-1.5 rounded px-1.5 py-0.5 text-xs font-bold uppercase bg-[#DCFCE7] text-[#15803D]"
                                                        data-testid="macro-fila-actual">Actual</span>
                                                )}
                                                {esBorrador && <span className="ml-1.5 text-[11px] uppercase text-[#B43E06]">sin guardar</span>}
                                                {h._programado && <span className="ml-1.5 text-[11px] uppercase text-[#1E3A8A]" data-testid="macro-fila-programada" title="Todavía no rige: empieza ese día">programado</span>}
                                                {sustituida && (
                                                    <span className="ml-1.5 text-[11px] uppercase text-[#6F6B64]"
                                                        title="Se guardó ese día y otro guardado del mismo día la sustituyó. No es una fila aparte del historial: no se edita ni se repite.">
                                                        sustituida ese día{h.created_at ? ` · ${horaEnEspana(new Date(h.created_at))}` : ''}
                                                    </span>
                                                )}
                                                {h.origen === 'ia' && <span className="ml-1.5 text-[11px] uppercase text-[#B43E06]" title="Propuesta de la IA aceptada tal cual">IA</span>}
                                                {h.origen === 'ia_corregida' && <span className="ml-1.5 text-[11px] uppercase text-[#854D0E]" title={`Propuesta de la IA corregida por el entrenador: ${JSON.stringify(h.correccion_coach || {})}`}>IA·corr</span>}
                                            </td>
                                            <td className="px-2 py-1 text-right whitespace-nowrap tabular-nums">
                                                <span className={`font-bold rounded px-1 ${esBorrador || sustituida ? 'text-[#6F6B64]' : tonoPeso}`}
                                                    title={esMax ? 'Peso máximo del recorrido' : esMin ? 'Peso mínimo del recorrido' : undefined}>
                                                    {peso != null ? `${kilos(peso)} kg` : '-'}
                                                </span>
                                                {h.body_fat != null && <span className="text-[#6F6B64] text-sm"> · {h.body_fat}%</span>}
                                            </td>
                                            <td className={`px-2 py-1 text-right whitespace-nowrap tabular-nums ${esBorrador || sustituida ? 'text-[#6F6B64]' : 'text-[#57544E]'}`}
                                                data-testid="macro-fila-rutina">{h.rutina_numero || '-'}</td>
                                            <MacroCeldas m={h.training} prev={ant?.training} apagado={esBorrador} cambios={cambios?.entreno} />
                                            <MacroCeldas m={peri} prev={antPeri} showG={false} apagado={esBorrador} cambios={cambios?.perientreno} />
                                            <MacroCeldas m={h.rest} prev={ant?.rest} apagado={esBorrador} cambios={cambios?.descanso} />
                                            <CeldaHistorial texto={h.criterio} testid="hist-criterio" />
                                            <CeldaHistorial texto={nota} testid="hist-feedback" />
                                            <td className="px-2 py-1 whitespace-nowrap">
                                                {onEvaluar && h.id && !sustituida ? (
                                                    <button onClick={() => onEvaluar(h)} title="Cómo salió la fase que abrió este ajuste"
                                                        className="text-sm rounded px-1.5 py-0.5 transition-colors hover:bg-[#EFECE6]">
                                                        {h.evaluacion?.resultado ? <EvaluacionBadge ev={h.evaluacion} /> : <span className="text-[#6F6B64]">Evaluar</span>}
                                                    </button>
                                                ) : (h.evaluacion?.resultado ? <EvaluacionBadge ev={h.evaluacion} /> : <span className="text-[#6F6B64] text-sm">-</span>)}
                                            </td>
                                            {(onEdit || onRepeat || onDelete) && (
                                                <td className="px-2 py-1">
                                                    {/* La fila en curso todavía no existe, y la sustituida ya no es una fila
                                                        del historial: no se pueden repetir, editar ni borrar */}
                                                    {!esBorrador && !sustituida && (
                                                        <div className="flex items-center justify-end gap-0.5">
                                                            {onRepeat && <button onClick={() => onRepeat(h)} title="Repetir estos macros (aplicar hoy)" className="p-1 rounded text-[#6F6B64] hover:text-[#B43E06] hover:bg-[#FDEBDF]"><RotateCcw className="w-3.5 h-3.5" /></button>}
                                                            {onEdit && <button onClick={() => onEdit(h)} title="Editar esta entrada" className="p-1 rounded text-[#6F6B64] hover:text-[#1C1B19] hover:bg-[#EFECE6]"><Pencil className="w-3.5 h-3.5" /></button>}
                                                            {onDelete && <button onClick={() => onDelete(h)} title="Eliminar esta entrada" className="p-1 rounded text-[#B91C1C] hover:text-[#991B1B] hover:bg-[#FEE2E2]"><Trash2 className="w-3.5 h-3.5" /></button>}
                                                        </div>
                                                    )}
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        {filas.length > RECIENTES && (
                            <button onClick={() => setVerTodo(!verTodo)} data-testid="macro-hist-ver-todo"
                                className="w-full mt-2 py-2 text-sm font-semibold text-[#57544E] hover:text-[#B43E06] transition-colors">
                                {verTodo ? `Ver solo los ${RECIENTES} últimos` : `Ver el historial entero (${filas.length} ajustes)`}
                            </button>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

// ========== CALMA (datos importados, solo lectura) ==========

// Los campos de texto de Calma a veces acaban con un código interno del formulario
// (p. ej. "Descanso OK|A", "...semanales|I"): lo quitamos para mostrarlo limpio.
const _limpiaCodigo = (s) => typeof s === 'string' ? s.replace(/\s*\|[A-Za-z0-9]{1,2}\s*$/, '').trim() : s;

// UN «1» NO ES UNA RESPUESTA (punto 66 del doc del 24-08). «El cuestionario de Montalvo
// está lleno de unos: edad 5, estatura 1 cm, peso inicial 1 kg y las diez medidas a 1 -- y
// en la misma pestaña, arriba, peso inicial 96 kg».
//
// Comprobado en producción el 30-08: su `formulario_inicial` de Calma trae VEINTE campos
// con el valor 1, y ahí dentro van `estatura`, `peso`, `pesoMaximo`, las mediciones y hasta
// el Instagram y la profesión. No son datos: es lo que dejó el formulario de Calma cuando
// no se contestó. Pintados tal cual, la ficha decía «Peso inicial 1 kg» tres centímetros
// debajo de sus 96 kg de verdad, y de las dos la que se lee primero es la de arriba.
//
// Se esconde el campo en vez de pintar un aviso, que es lo que ya hace esta misma función
// con lo vacío: un cuestionario que nadie rellenó tiene que verse como lo que es, vacío.
// Y quien tenga que arreglarlo lo tiene en el panel, en Operaciones > «datos imposibles»,
// que es la pantalla que existe para eso.
//
// El crudo NO se toca: `calma_raw` es el archivo de lo que había en Calma y reescribirlo
// sería perder la única copia de lo que de verdad se importó.
const _esRelleno = (v) => Number(v) === 1 && String(v).trim().replace(',', '.') === '1';

const CalmaField = ({ label, value }) => {
    if (value == null || value === '' || (Array.isArray(value) && !value.length)) return null;
    if (_esRelleno(value)) return null;
    // Un «XX» o un «-» del volcado no es lo que escribió nadie (punto 104, 4-09).
    if (typeof value === 'string' && !sinElRelleno(_limpiaCodigo(value))) return null;
    const text = Array.isArray(value) ? value.join(', ') : _limpiaCodigo(String(value));
    return (
        <div>
            <p className="text-[10px] text-[#6F6B64] uppercase tracking-wider mb-0.5">{label}</p>
            <p className="text-[#1C1B19] text-sm whitespace-pre-wrap break-words">{text}</p>
        </div>
    );
};

/**
 * El cuestionario largo del cliente (perfil.nivel1), agrupado como en el documento.
 *
 * Existe porque no existía: el cliente contestaba las treinta preguntas -- su historia,
 * su salud, con qué entrena, qué suplementos toma -- y no se pintaban en ninguna parte de
 * la ficha. Se le vendía "tu coach usará todo esto para tu estrategia" y el coach no
 * tenía dónde leerlo.
 *
 * Se pinta lo que HAY: si un bloque está vacío no se enseña, para que no parezca que el
 * cliente dejó cosas sin contestar cuando lo que pasa es que no se le preguntaron.
 */
const ETIQUETAS_NIVEL1 = {
    // Tu historia
    peso_maximo: 'Peso máximo', peso_maximo_cuando: '¿Cuándo?',
    peso_minimo: 'Peso mínimo', peso_habitual: 'Peso habitual',
    peso_mejor_momento: 'Peso en su mejor momento',
    mejor_definicion_cuando: 'Mejor punto de definición',
    hasta_donde: 'Hasta dónde quiere llegar',
    vario_peso_3m: '¿Ha variado su peso en 3 meses?',
    tiempo_intentandolo: 'Tiempo intentándolo',
    // La fundida, para las fichas que la contestaron antes del 8-09.
    motivo_apuntarse: 'Por qué se apuntó',
    // Y las dos en que se partió, que es como las quiere el documento.
    por_que_a_mi: 'Por qué le eligió a él y no a otro',
    por_que_ahora: 'Por qué ahora y no antes',
    // LAS DOS QUE SE PREGUNTABAN Y NO LEÍA NADIE (documento «El cuestionario», 7-09). «Este
    // dato es el que dice de dónde viene la gente», y hasta hoy se guardaba en la ficha y no
    // salía en ninguna pantalla: el panel solo ponía de dónde venía el alta («Ghl 26 ·
    // Renovación 1»). La profesión igual: se pregunta en el alta y se quedaba dentro.
    como_me_conociste: 'Cómo le conoció',
    // Las del 24-10: el «Otro» de cómo le conoció, desde cuándo le conoce y el botón
    // «Estoy en mi peor momento de forma».
    como_me_conociste_otro: 'Cómo le conoció (otro)',
    tiempo_siguiendome: 'Desde cuándo le conoce',
    peor_forma_ahora: 'Su peso máximo es el de ahora',
    profesion: 'A qué se dedica',
    dietas_previas: 'Dietas que ha hecho',
    dieta_que_funciona: 'La que mejor le funcionó',
    por_que_fallaron: 'Por qué fallaron',
    entrenador_anterior: '¿Ha tenido entrenador o plan antes?',
    entrenador_anterior_que_tal: 'Qué tal le fue',
    // Tu entrenamiento
    training_experience: 'Experiencia entrenando',
    entrena_ahora: '¿Entrena ahora?',
    material: 'Material',
    maquinas_que_faltan: 'Máquinas que le faltan',
    ejercicios_imposibles: 'Ejercicios que no puede hacer',
    cardio: 'Cardio',
    dias_entreno: 'Días de entreno',
    // Tu salud
    trt: 'TRT', farmacologia_uso: 'Farmacología',
    // Lo que contestó al aviso del dopaje: si pidió el soporte médico o no.
    aviso_dopaje: '¿Quiere orientación médica?',
    patologia: 'Patología o medicación', patologia_detalle: 'Qué tiene',
    medicacion: 'Medicación (de antes)', medicacion_detalle: 'Cuál (de antes)',
    zona_grasa: 'Dónde acumula grasa',
    // Tu descanso: las tres del cuestionario largo desde el 20-10 (rediseño del
    // cuestionario), cada una en su propio campo.
    horas_sueno: 'Horas que duerme',
    sueno_suficiente: '¿Le basta?',
    para_dormir: 'Para el sueño (suplementos o medicación)',
    // Tu suplementación
    suplementos_ahora: 'Toma ahora',
    suplementos_antes: 'Ha tomado antes',
    quiere_pauta_suplementos: '¿Quiere que le pauten?',
    // Tu comida
    cocina_o_rapido: '¿Cocina o quiere rápido?',
    conserva_o_fresco: '¿Conserva o fresco?',
    come_fuera: '¿Come fuera?',
    que_le_apetece: 'Qué le apetece en cada comida',
    favoritos_y_no_gustos: 'Favoritos y no-gustos',
    plato_imprescindible: 'Plato imprescindible',
    // Su día tipo, del cuestionario largo (25-10).
    dia_tipo: 'Su día tipo',
    lactosa: 'Lactosa',
    gluten: 'Gluten',
    alergias: 'Otras alergias',
    num_comidas: 'Comidas al día',
    // LO QUE COME, no solo lo que rechaza (Jesús, 9-09). Sin estas cuatro filas las
    // respuestas se guardan y no las ve nadie, que es como no haberlas preguntado.
    proteinas_habituales: 'Proteínas que come',
    hidratos_habituales: 'Hidratos que come',
    grasas_habituales: 'Grasas que come',
    desayuno_habitual: 'Un desayuno que le gusta',
    // Otros
    biotype: 'Biotipo', height: 'Altura', birthdate: 'Fecha de nacimiento',
};

const BLOQUES_NIVEL1 = [
    ['Su historia', ['peso_maximo', 'peso_maximo_cuando', 'peso_minimo', 'peso_habitual',
                     'peso_mejor_momento', 'mejor_definicion_cuando', 'hasta_donde',
                     'vario_peso_3m', 'tiempo_intentandolo', 'motivo_apuntarse',
                     'por_que_a_mi', 'por_que_ahora',
                     'como_me_conociste', 'como_me_conociste_otro', 'tiempo_siguiendome',
                     'peor_forma_ahora', 'profesion',
                     'dietas_previas', 'dieta_que_funciona', 'por_que_fallaron',
                     'entrenador_anterior', 'entrenador_anterior_que_tal']],
    ['Su entrenamiento', ['training_experience', 'entrena_ahora', 'material',
                          'maquinas_que_faltan', 'ejercicios_imposibles', 'cardio',
                          'dias_entreno']],
    ['Su salud', ['patologia', 'patologia_detalle', 'medicacion', 'medicacion_detalle',
                  'trt', 'farmacologia_uso', 'aviso_dopaje', 'zona_grasa']],
    ['Su descanso', ['horas_sueno', 'sueno_suficiente', 'para_dormir']],
    ['Su suplementación', ['suplementos_ahora', 'suplementos_antes', 'quiere_pauta_suplementos']],
    ['Su comida', ['dia_tipo', 'proteinas_habituales', 'hidratos_habituales', 'grasas_habituales',
                   'desayuno_habitual',
                   'cocina_o_rapido', 'conserva_o_fresco', 'come_fuera', 'que_le_apetece',
                   'favoritos_y_no_gustos', 'plato_imprescindible', 'lactosa', 'gluten',
                   'alergias', 'num_comidas']],
];

// Los valores se guardan en clave ('3_10', 'irregular'); el coach lee castellano.
const VALORES_NIVEL1 = {
    menos_1: 'Menos de 1 año', '1_3': 'Entre 1 y 3 años', '3_10': 'Entre 3 y 10 años',
    mas_10: 'Más de 10 años', parado: 'Entrenó antes, lleva tiempo parado',
    si: 'Sí', no: 'No', antes: 'Antes sí, ahora no',
    irregular: 'Va, pero de forma irregular',
    uso: 'Sí, ahora mismo', use: 'Ha usado antes, ahora no',
    intencion: 'No, pero tiene intención', nunca: 'No, ni se lo plantea',
    lo_justo: 'Solo lo imprescindible',
    // Su comida
    cocinar: 'Le gusta cocinar y tiene tiempo', normal: 'Cocina lo justo',
    rapido: 'Cuanto más rápido, mejor',
    conserva: 'La conserva le facilita la vida', ambos: 'Le da igual', fresco: 'Lo prefiere fresco',
    '1_2': '1 o 2 días por semana', '3_4': '3 o 4 días', casi_todos: 'Casi todos los días',
    // Intolerancias
    bien: 'Sin problema', tolera_algo: 'Tolera yogur y queso curado', nada: 'Nada de lactosa',
    sensibilidad: 'Sensibilidad, no celiaquía', celiaquia: 'Celiaquía diagnosticada',
    // Cómo le conoció: las siete del alta, con las mismas palabras que leyó el cliente.
    newsletter: 'Por la newsletter', instagram: 'Por Instagram',
    ex_cliente: 'Fue cliente de las asesorías', ex_alumno: 'Fue alumno de una formación',
    recomendacion: 'Por recomendación', medios: 'Por artículos o noticias en medios',
    otro: 'Otro', youtube: 'Por el canal de Youtube',
    // Desde cuándo le conoce: los tramos del 24-10 y los de antes, que siguen en sus fichas.
    menos_1_semana: 'Le acaba de conocer (menos de una semana)', '1_2m': 'Un mes o dos',
    '2_6m': 'Entre 2 y 6 meses', '6m_1a': 'Más de 6 meses y hasta un año',
    anos: 'Desde hace años',
    menos_1_5m: 'Menos de un mes o mes y medio', '1_5_3m': 'Entre un mes y medio y 3',
    // Su descanso: con las mismas palabras que leyó el cliente.
    min_7_30: 'Mínimo 7 y media', '6_7_30': 'Entre 6 y 7 horas y media',
    '5_6': 'Entre 5 y 6, no más', menos_5: 'Menos de 5',
    mas_que_suficiente: 'Más que suficiente', poco_pero_vale: 'Es poco, pero le vale',
    necesito_mas: 'Necesita más, sin duda alguna',
    // Las del cuestionario rediseñado (PR 8 de Enrique, 20-10), que salían en clave.
    ahora: 'Empieza ahora', menos_6m: 'Menos de 6 meses', '6m_2a': 'Entre 6 meses y 2 años',
    mas_2a: 'Más de 2 años',
    cero: 'Ninguna, parte de cero', principiante: 'Menos de 1 año (principiante)',
    intermedio: 'Más de un año, no siempre en serio (intermedio)',
    avanzado: 'Años entrenando en serio (avanzado)',
    otro_entrenamiento: 'Va, pero hace otro tipo de entrenamiento',
    parada_puntual: 'Ahora no, fue un parón puntual',
    si_me_gusta: 'Sí, y le gusta: que se le ponga lo que mejor le vaya',
    si_lo_odio: 'Sí, pero lo odia: cuanto menos, mejor',
    no_pero_abierto: 'No, pero no le importa empezar',
    no_jamas: 'No, y no lo hará en ningún caso (sabe que aprieta la dieta)',
};

// LAS QUE SE LEEN POR CAMPO: en «¿Quiere que le pauten?», `no` es «No me pongas nada» y
// no el «No» del diccionario general (lo mismo que `VALORES_BOOLEANOS` con `sigue_dieta`).
const VALORES_NIVEL1_POR_CAMPO = {
    quiere_pauta_suplementos: {
        libertad: 'Sí, suele tomarlos: libertad total',
        abierto: 'No suele tomarlos, pero no le importa empezar',
        lo_justo: 'Uno o dos como mucho',
        no: 'Nada: descarta la suplementación por completo',
    },
};

/**
 * LO QUE CONTESTÓ Y MUEVE SUS MACROS (punto 17 del doc del 07-08).
 *
 * La pestaña Cuestionario enseñaba seis campos -- objetivo, peso, sexo, % graso, edad y
 * altura -- y el cuestionario largo. Faltaba justo lo que decide los números: cuánto se
 * mueve al día, si hace otro deporte, si engorda con facilidad, qué dieta trae y cómo le
 * está funcionando. Eso vive en `ajustes_macros`, se guarda desde el 06-08 y no se pintaba
 * en ningún sitio: el coach ajustaba a ciegas sobre unas respuestas que no podía leer.
 *
 * `como_va` es la que más pesa -- sitúa lo que come respecto a su mantenimiento -- y va la
 * primera a propósito.
 */
const ETIQUETAS_AJUSTES = {
    como_va: 'Cómo le va',
    sigue_dieta: '¿Sigue una dieta?',
    tiempo_dieta: 'Cuánto lleva con ella',
    hambre_saturacion: 'Hambre / saturación',
    actividad_diaria: 'Actividad diaria',
    deporte_extra: 'Otro deporte además de pesas',
    facilidad_engordar: 'Engorda al pasarse',
    dieta_hc_entreno: 'HC del día de entreno (g)',
    dieta_grasa_entreno: 'Grasa aprox. (g)',
    dieta_texto: 'Su día tipo, tal cual lo escribió',
};

// Los valores se guardan en clave; el coach lee castellano. Los textos son los mismos que
// ve el cliente al contestar, para que no haya dos vocabularios para lo mismo.
// LOS BOOLEANOS SE LEEN POR CAMPO, y aquí estaba el fallo: `sigue_dieta` tiene sus propias
// palabras para el sí y el no, y vivían en el diccionario general bajo las claves `true` y
// `false`. Como ese diccionario se consulta POR VALOR y no por campo, cualquier otro campo
// booleano cogía esas mismas frases: en la ficha del cliente, «Otro deporte además de pesas»
// le decía al entrenador «Estricta, mide todo lo que come», que es la respuesta de otra
// pregunta. Un dato mal pintado en la pantalla con la que ajusta los macros.
const VALORES_BOOLEANOS = {
    sigue_dieta: { true: 'Estricta, mide todo lo que come',
                   false: 'Sin control, pero no come mal' },
};

const VALORES_AJUSTES = {
    // sigue_dieta -- las otras dos del punto 19; el sí y el no van arriba, por campo
    parecido: 'No pesa, pero se cuida bastante', desorganizado: 'Come mal y desorganizado',
    // como_va
    bien: 'Bien, va a buen ritmo', lento: 'Va, pero muy lento',
    mucha_grasa: 'Sube, pero coge más grasa de la cuenta',
    mantengo: 'Se mantiene igual', bajando: 'Mal: en vez de subir, baja',
    cogiendo_peso: 'Mal: está cogiendo peso',
    // actividad_diaria / facilidad_engordar / hambre / tiempo
    sedentario: 'Sedentario', normal: 'Normal', muy_activo: 'Muy activo',
    enseguida: 'Enseguida', casi_no: 'Casi no',
    mucho: 'Mucha hambre', aguanto_mas: 'Ninguna: aguanta más',
    no_puedo_mas: 'No saturado, pero no puede comer más', puedo_mas: 'Puede comer más sin problema',
    menos_1m: 'Menos de un mes', '1_3m': 'Entre 1 y 3 meses',
    '3_6m': 'Entre 3 y 6 meses', mas_6m: 'Más de 6 meses',
};

/**
 * LO QUE NO QUIERE COMER Y LO QUE MÁS LE GUSTA (29-10, incidencia de Jesús: «tampoco aparece
 * en el reporte inicial»). Lo marca en el alta (`components/alta/PreferenciasDelAlta`) o en
 * Preferencias de Nutrición, y se guardaba sin enseñarse aquí. Llega ya en nombres
 * (`core.preferencias.para_ver`): los de Calma lo tienen en códigos.
 */
const PreferenciasDeComida = ({ prefs }) => {
    if (!prefs) return null;
    const nombre = (id) => PREFERENCE_CATEGORIES.find(c => c.id === id)?.label || id;
    const evitadas = prefs.avoided_categories || [];
    const palabras = prefs.avoided_keywords || [];
    const preferidas = prefs.food_preferences || [];
    const sueltos = (prefs.preferred_foods || []).map(f => f?.nombre).filter(Boolean);
    if (!evitadas.length && !palabras.length && !preferidas.length && !sueltos.length) return null;

    const Fila = ({ etiqueta, children }) => (
        <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,13rem)_1fr] gap-1.5 sm:gap-3 text-sm">
            <span className="text-[#6F6B64]">{etiqueta}</span>
            <div className="flex flex-wrap gap-1.5">{children}</div>
        </div>
    );
    const rojo = 'bg-[#FEE2E2] text-[#B91C1C] border-0 text-xs';
    const naranja = 'bg-[#FDEBDF] text-[#B43E06] border-0 text-xs';
    const nada = <span className="text-[#6F6B64]">Nada marcado</span>;

    return (
        <Card className="bg-white border-[#E5E2DB]" data-testid="preferencias-de-comida">
            <CardHeader className="pb-2">
                <CardTitle className="text-sm text-[#6F6B64] uppercase tracking-wider flex items-center gap-2">
                    <Utensils className="w-4 h-4" />Lo que come y lo que no
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
                <Fila etiqueta="No quiere comer">
                    {evitadas.length || palabras.length ? (<>
                        {evitadas.map(id => <Badge key={`c-${id}`} className={rojo}>{nombre(id)} (toda la categoría)</Badge>)}
                        {palabras.map(kw => <Badge key={`p-${kw}`} className={rojo}>{kw}</Badge>)}
                    </>) : nada}
                </Fila>
                <Fila etiqueta="Lo que más le gusta">
                    {preferidas.length || sueltos.length ? (<>
                        {preferidas.map(id => <Badge key={`c-${id}`} className={naranja}>{nombre(id)}</Badge>)}
                        {sueltos.map(n => <Badge key={`s-${n}`} className={naranja}>{n}</Badge>)}
                    </>) : nada}
                </Fila>
            </CardContent>
        </Card>
    );
};

const AjustesDelCuestionario = ({ ajustes }) => {
    const filas = Object.keys(ETIQUETAS_AJUSTES)
        .map(k => {
            const v = ajustes?.[k];
            if (v == null || v === '') return null;
            const texto = typeof v === 'boolean'
                ? ((VALORES_BOOLEANOS[k] || {})[String(v)] || (v ? 'Sí' : 'No'))
                : (VALORES_AJUSTES[v] || String(v));
            return [k, ETIQUETAS_AJUSTES[k], texto];
        })
        .filter(Boolean);
    if (!filas.length) return null;

    return (
        <Card className="bg-white border-[#E5E2DB]" data-testid="ajustes-del-cuestionario">
            <CardHeader className="pb-2">
                <CardTitle className="text-sm text-[#6F6B64] uppercase tracking-wider flex items-center gap-2">
                    <SlidersHorizontal className="w-4 h-4" />Lo que contestó y mueve sus macros
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
                {filas.map(([k, etiqueta, texto]) => (
                    <div key={k} className="grid grid-cols-[minmax(0,13rem)_1fr] gap-3 text-sm">
                        <span className="text-[#6F6B64]">{etiqueta}</span>
                        <span className="text-[#1C1B19] whitespace-pre-wrap break-words">{texto}</span>
                    </div>
                ))}
            </CardContent>
        </Card>
    );
};

/**
 * LAS FOTOS QUE SUBIÓ EN EL ALTA (Francisco, 13-10): la de su mejor forma y la que puso al
 * lado de los modelos para elegir su % graso.
 *
 * Se guardaban y no las veía nadie: en la ficha queda solo su id y ninguna pantalla lo leía.
 * No son fotos de progreso, así que no van con las de Seguimiento: van aquí, con el resto de
 * lo que contestó. Qué fotos hay y de dónde sale cada una lo decide `lib/fotosDelAlta`; las
 * guardadas se pintan con `AppFoto`, la misma miniatura (y el mismo abrir en grande) que usa
 * la ficha para las de progreso. Sin fotos, el bloque no existe.
 */
const FotosDelAlta = ({ api, profile }) => {
    const fotos = fotosDelAlta(profile);
    if (!fotos.length) return null;
    return (
        <Card className="bg-white border-[#E5E2DB]" data-testid="fotos-del-alta">
            <CardHeader className="pb-2">
                <CardTitle className="text-sm text-[#6F6B64] uppercase tracking-wider flex items-center gap-2">
                    <Camera className="w-4 h-4" />Fotos del alta
                </CardTitle>
            </CardHeader>
            <CardContent>
                <div className="flex flex-wrap gap-5">
                    {fotos.map(f => (
                        <div key={f.clave} className="w-32 max-w-full" data-testid={`foto-del-alta-${f.clave}`}>
                            <p className="text-xs text-[#57544E] mb-1.5 leading-snug">{f.titulo}</p>
                            {f.id
                                ? <AppFoto api={api} foto={{ id: f.id, fecha: f.detalle }} />
                                : (
                                    // Ficha de antes del 23-08: la foto viene en base64 dentro
                                    // del propio perfil. Se abre en otra pestaña, como las demás.
                                    <a href="#foto" className="block group"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            fetch(f.dataUrl).then(r => r.blob())
                                                .then(b => window.open(URL.createObjectURL(b), '_blank', 'noopener'))
                                                .catch(() => {});
                                        }}>
                                        <img src={f.dataUrl} alt={f.titulo}
                                            className="w-full aspect-[3/4] object-cover rounded-lg border border-[#E5E2DB] group-hover:border-[#FF671F]/50 bg-[#F6F5F2]" />
                                        <p className="text-[9px] text-[#6F6B64] mt-0.5 truncate">{f.detalle}</p>
                                    </a>
                                )}
                        </div>
                    ))}
                </div>
            </CardContent>
        </Card>
    );
};

/**
 * LAS FOTOS Y LAS MEDIDAS DEL DÍA 1 (Francisco, 28-10: «no aparecen los datos volcados en la
 * vista de administrador»). La ficha solo las enseñaba dentro de Seguimiento cuando ya había
 * mensuales. Salen del Punto 0 del alta de `GET /admin/clients/{id}/puntos`, el mismo que
 * pinta Seguimiento (`core/puntos.py`): las fotos del cuestionario y las cercanas al inicio
 * del ciclo del alta, y las medidas del inicio si se tomaron entonces. Sin nada, no hay bloque.
 */
const FotosYMedidasDelAlta = ({ api, clientId }) => {
    const [alta, setAlta] = useState(null);
    useEffect(() => {
        let vivo = true;
        api.get(`/admin/clients/${clientId}/puntos`)
            .then(r => {
                if (vivo) setAlta((r.data?.puntos || []).find(p => p.tipo === 'punto0' && p.nombre_secundario === 'el alta') || null);
            })
            .catch(e => console.error('No se pudieron cargar las fotos del alta:', e));
        return () => { vivo = false; };
    }, [api, clientId]);
    // Solo las tres posturas: la de su mejor forma y la de la grasa (sin postura) ya salen
    // en «Fotos del alta», justo encima.
    const fotos = (alta?.fotos || []).filter(f => f.pose);
    const medidas = filasDeMedidas(alta?.medidas, { antiguas: false });
    if (!fotos.length && !medidas.length) return null;
    return (
        <Card className="bg-white border-[#E5E2DB]" data-testid="fotos-y-medidas-del-alta">
            <CardHeader className="pb-2">
                <CardTitle className="text-sm text-[#6F6B64] uppercase tracking-wider flex items-center gap-2">
                    <Camera className="w-4 h-4" />Fotos y medidas del día 1
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                {fotos.length > 0 && (
                    <div className="grid grid-cols-3 gap-3 max-w-md">
                        {fotos.map(f => (
                            <div key={f.id} className="min-w-0" data-testid={`foto-inicial-${f.pose || f.id}`}>
                                <p className="text-xs text-[#57544E] mb-1.5 first-letter:uppercase">{NOMBRE_POSE[f.pose] || f.pose}</p>
                                <AppFoto api={api} foto={{ id: f.id, fecha: f.fecha }} />
                            </div>
                        ))}
                    </div>
                )}
                {medidas.length > 0
                    ? <div className="max-w-md"><TablaDeMedidas filas={medidas} /></div>
                    : <p className="text-sm text-[#57544E] m-0">No apuntó medidas en el alta.</p>}
            </CardContent>
        </Card>
    );
};

const PerfilLargo = ({ nivel1 }) => {
    if (!nivel1 || !Object.keys(nivel1).length) return null;

    const valor = (v, campo) => {
        if (v == null || v === '') return null;
        if (typeof v === 'boolean') return v ? 'Sí' : 'No';
        // Las listas, con las palabras de cada opción (cómo le conoció es una lista desde el
        // 24-10); el equipamiento sigue saliendo con las suyas.
        if (Array.isArray(v)) return v.length ? v.map(x => VALORES_NIVEL1[x] || equipamientoLabel(x)).join(' · ') : null;
        return VALORES_NIVEL1_POR_CAMPO[campo]?.[v] || VALORES_NIVEL1[v] || String(v);
    };

    const salud = nivel1.salud || {};
    const hayAlgoDeSalud = Object.values(salud).some(v => v);

    // LOS BLOQUES SE CALCULAN ANTES DE PINTAR, no dentro del map. Aquí llega ahora el perfil
    // entero (el básico guarda sus respuestas en la raíz, no en `nivel1`), así que
    // «¿tiene claves?» ya no sirve para saber si hay algo que enseñar: lo tiene siempre.
    // Sin esto, la ficha de quien no ha contestado ninguna de estas preguntas pintaba una
    // tarjeta con el título y nada debajo.
    const bloques = BLOQUES_NIVEL1
        .map(([titulo, claves]) => [titulo, claves
            .map(k => [ETIQUETAS_NIVEL1[k] || k, valor(nivel1[k], k)])
            .filter(([, v]) => v)])
        .filter(([, filas]) => filas.length);
    if (!bloques.length && !hayAlgoDeSalud) return null;

    return (
        <Card className="bg-white border-[#E5E2DB]" data-testid="perfil-largo">
            <CardHeader className="pb-2">
                <CardTitle className="text-sm text-[#6F6B64] uppercase tracking-wider flex items-center gap-2">
                    <ClipboardList className="w-4 h-4" />Su cuestionario
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
                {bloques.map(([titulo, filas]) => (
                    <div key={titulo}>
                        <p className="text-xs text-[#B43E06] uppercase tracking-wider font-bold mb-2">{titulo}</p>
                        <div className="space-y-2">
                            {filas.map(([etiqueta, v]) => (
                                <div key={etiqueta} className="grid grid-cols-[minmax(0,11rem)_1fr] gap-3 text-sm">
                                    <span className="text-[#6F6B64]">{etiqueta}</span>
                                    <span className="text-[#1C1B19] whitespace-pre-wrap break-words">{v}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}

                {hayAlgoDeSalud && (
                    <div>
                        <p className="text-xs text-[#B43E06] uppercase tracking-wider font-bold mb-2">Sueño, estrés y lesiones</p>
                        <div className="space-y-2">
                            {[['sueno', 'Sueño'], ['estres', 'Estrés'], ['medicacion', 'Medicación'],
                              ['hormonal', 'Hormonal'], ['lesiones', 'Lesiones']]
                                .filter(([k]) => salud[k])
                                .map(([k, etiqueta]) => (
                                    <div key={k} className="grid grid-cols-[minmax(0,11rem)_1fr] gap-3 text-sm">
                                        <span className="text-[#6F6B64]">{etiqueta}</span>
                                        <span className="text-[#1C1B19] whitespace-pre-wrap break-words">{String(salud[k])}</span>
                                    </div>
                                ))}
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

//: Un número de Calma con su unidad, salvo que sea el relleno del punto 66.
const _utilCalma = (v, unidad) => (v && !_esRelleno(v) ? `${v} ${unidad}` : null);

//: Las medidas cuando solo hay la cadena cruda («82|95|101|...»): se parte y se le quita el
//  relleno, igual que al array. Si no queda ni una medida de verdad, no hay nada que enseñar.
const _medidasRaw = (raw) => {
    if (!raw) return null;
    const piezas = String(raw).split(/[|;,·]/).map(s => s.trim()).filter(s => s && !_esRelleno(s));
    return piezas.length ? piezas.join(' · ') : null;
};

const CalmaCuestionario = ({ fi }) => {
    if (!fi) return null;
    // Las medidas, sin los unos: «las diez medidas a 1» del punto 66. Una medida corporal
    // de 1 cm no existe, así que no es un dato que se pueda enseñar ni promediar.
    const med = fi.mediciones?.valores?.filter(v => v != null && !_esRelleno(v));
    // Y LA FECHA DE NACIMIENTO, IGUAL (punto 66). Del mismo relleno salen fechas del año 1:
    // en la ficha de Juan Chamero se leía «Fecha nacimiento 31/12/1». No se filtra por el
    // valor crudo sino por el año que sale, que es lo que se ve; nadie nacido antes de 1900
    // está entrenando hoy, así que ahí no hay dato que enseñar.
    // Y es un día, no un instante: se pinta sin convertir de huso (`pintarDia`).
    const _fnac = diaDeCalendario(fi.fechaNacimiento);
    const fnac = _fnac && Number(_fnac.slice(0, 4)) >= 1900 ? pintarDia(_fnac) : null;
    return (
        <Card className="bg-white border-[#E5E2DB]">
            <CardHeader className="pb-2"><CardTitle className="text-sm text-[#6F6B64] uppercase tracking-wider flex items-center gap-2"><ClipboardList className="w-4 h-4" />Cuestionario inicial</CardTitle></CardHeader>
            <CardContent className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-3">
                    <CalmaField label="Nombre" value={fi.nombre} />
                    <CalmaField label="Fecha nacimiento" value={fnac} />
                    <CalmaField label="Teléfono" value={fi.telefono} />
                    <CalmaField label="Instagram" value={fi.instagram} />
                    <CalmaField label="Dirección" value={fi.direccion} />
                    <CalmaField label="Profesión" value={fi.profesion} />
                    <CalmaField label="Cómo nos conoció" value={fi.fuenteCliente} />
                    {/* Los tres llegan ya compuestos («1 cm»), así que el filtro del relleno
                        del punto 66 no los vería: se preguntan por el valor de dentro. */}
                    <CalmaField label="Estatura" value={_utilCalma(fi.estatura, 'cm')} />
                    <CalmaField label="Peso inicial" value={_utilCalma(fi.peso, 'kg')} />
                    <CalmaField label="Peso máximo" value={_utilCalma(fi.pesoMaximo, 'kg')} />
                    <CalmaField label="Objetivo" value={fi.objetivo} />
                    <CalmaField label="Estilo de vida" value={fi.estiloVida} />
                    <CalmaField label="Experiencia con preparadores" value={fi.preparadores} />
                    <CalmaField label="Estado de forma anterior" value={fi.estadoFormaAnterior} />
                    <CalmaField label="Entrena actualmente" value={fi.entrenamientoActual} />
                    <CalmaField label="Rutina inicial" value={fi.rutinaInicial} />
                    <CalmaField label="Descanso" value={fi.descanso} />
                    <CalmaField label="Interés en suplementación" value={fi.interesEnSuplementacion} />
                </div>
                <div className="space-y-3 border-t border-[#E5E2DB] pt-3">
                    {/* Y la cadena de reserva, que es por donde se colaban: filtrado el array
                        de valores, esto caía en `mediciones.raw` y pintaba «1|1|1|1|1|1|1|1|1»
                        tal cual. Se mira pieza a pieza, que es lo mismo que se hace arriba. */}
                    <CalmaField label="Medidas (cm)" value={med?.length ? med.join(' · ') : _medidasRaw(fi.mediciones?.raw)} />
                    <CalmaField label="Medicación" value={fi.medicacion} />
                    <CalmaField label="Fármacos actuales" value={fi.farmacosActuales} />
                    <CalmaField label="Maquinaria disponible" value={fi.maquinaria} />
                    <CalmaField label="Lesiones" value={fi.lesiones} />
                    <CalmaField label="Dieta de ejemplo" value={fi.ejemploDieta} />
                    <CalmaField label="Interés en otros servicios" value={fi.informacionSobreServicios} />
                    <CalmaField label="Comentario del cliente" value={fi.comentarioCliente} />
                </div>
            </CardContent>
        </Card>
    );
};

// Cache de miniaturas por foto. La misma foto la piden ahora varios sitios de la ficha
// (la comparativa, el mural de la pestaña de macros, la línea temporal y el comparador),
// y sin esto se pedían 108 veces para 49 fotos: el navegador encolaba y no se pintaba
// ninguna. Con la caché, cada foto se descarga UNA vez y la comparten todos.
const _thumbs = new Map();   // clave -> Promise<objectURL>

// Y con la cola de descargas limitada. El navegador solo abre 6 conexiones por host: al
// abrir la ficha se piden decenas de fotos de golpe y CUALQUIER otra llamada (guardar el
// % graso, los macros...) se queda en cola detrás de todas ellas, sin salir ni fallar.
// Con tres a la vez, las fotos siguen entrando rápido y el resto de la ficha responde.
const _MAX_EN_VUELO = 3;
let _enVuelo = 0;
const _cola = [];
const _tirarDeLaCola = () => {
    if (_enVuelo >= _MAX_EN_VUELO || !_cola.length) return;
    const { fn, ok, ko } = _cola.shift();
    _enVuelo++;
    fn().then(ok, ko).finally(() => { _enVuelo--; _tirarDeLaCola(); });
};
const _encolar = (fn) => new Promise((ok, ko) => { _cola.push({ fn, ok, ko }); _tirarDeLaCola(); });

const _thumb = (clave, descargar) => {
    if (!_thumbs.has(clave)) {
        _thumbs.set(clave, _encolar(descargar)
            .then(blob => URL.createObjectURL(blob))
            .catch(e => { _thumbs.delete(clave); throw e; }));   // si falla, se puede reintentar
    }
    return _thumbs.get(clave);
};

// Foto subida desde la app (client_photos) como miniatura; abre la original al hacer clic.
const AppFoto = ({ api, foto, sinPie = false }) => {
    const [thumb, setThumb] = useState(null);
    const [fallo, setFallo] = useState(false);
    useEffect(() => {
        let alive = true;
        setFallo(false);
        _thumb(`app:${foto.id}`, () => api.get(`/reports/photos/${foto.id}`, { responseType: 'blob' }).then(r => r.data))
            .then(url => { if (alive) setThumb(url); })
            // Sin la imagen (en local, las fotos están en R2 y sin credenciales no se bajan),
            // se dice: un recuadro gris parpadeando para siempre parece que aún carga.
            .catch(() => { if (alive) setFallo(true); });
        // El objectURL lo comparte la caché: no se revoca al desmontar, o la siguiente
        // pantalla que pida esa foto se quedaría con una imagen rota.
        return () => { alive = false; };
    }, [api, foto.id]);
    const openFull = async (e) => {
        e.preventDefault();
        try {
            const r = await api.get(`/reports/photos/${foto.id}`, { responseType: 'blob' });
            window.open(URL.createObjectURL(r.data), '_blank', 'noopener');
        } catch { /* noop */ }
    };
    return (
        <a href="#foto" onClick={openFull} className="block group">
            {thumb
                ? <img src={thumb} alt={foto.fecha || ''} className="w-full aspect-[3/4] object-cover rounded-lg border border-border group-hover:border-[#FF671F]/50 bg-muted" />
                : fallo
                    ? <div className="w-full aspect-[3/4] rounded-lg border border-border bg-muted flex items-center justify-center text-center px-2 text-[11px] text-muted-foreground">Foto no disponible</div>
                    : <div className="w-full aspect-[3/4] rounded-lg border border-border bg-muted animate-pulse" />}
            {/* Con los colores del tema: sale también en el cuestionario, que va en otro tono. */}
            {!sinPie && <p className="text-[9px] text-muted-foreground mt-0.5 truncate">{_fechaCorta(foto.fecha)}</p>}
        </a>
    );
};

// Una foto importada de Calma, por fetch autenticado (blob) para no llevar el token en la URL.
// `ancho`: la miniatura que se pide (las Fotos de los mensuales las pintan grandes y piden
// más); `sinPie` quita la línea de debajo cuando quien la pinta ya pone la fecha.
const CalmaFoto = ({ api, clientId, foto, ancho = 300, sinPie = false }) => {
    const [thumb, setThumb] = useState(null);
    const [fallo, setFallo] = useState(false);
    useEffect(() => {
        let alive = true;
        setFallo(false);
        // Por la caché compartida: la misma foto sale en varios sitios de la ficha.
        _thumb(`calma:${clientId}:${foto.file}${ancho === 300 ? '' : `:${ancho}`}`,
            () => api.get(`/admin/clients/${clientId}/calma-foto`, { params: { file: foto.file, w: ancho }, responseType: 'blob' }).then(r => r.data))
            .then(url => { if (alive) setThumb(url); })
            .catch(() => { if (alive) setFallo(true); });
        return () => { alive = false; };
    }, [api, clientId, foto.file, ancho]);
    const openFull = async (e) => {
        e.preventDefault();
        try {
            const r = await api.get(`/admin/clients/${clientId}/calma-foto`, { params: { file: foto.file }, responseType: 'blob' });
            window.open(URL.createObjectURL(r.data), '_blank', 'noopener');
        } catch { /* noop */ }
    };
    return (
        <a href="#foto" onClick={openFull} className="block group">
            {thumb
                ? <img src={thumb} alt={`${foto.kind || ''} ${foto.fecha || ''}`}
                    className="w-full aspect-[3/4] object-cover rounded-lg border border-[#E5E2DB] group-hover:border-[#FF671F]/50 bg-[#F6F5F2]" />
                : fallo
                    ? <div className="w-full aspect-[3/4] rounded-lg border border-[#E5E2DB] bg-[#F6F5F2] flex items-center justify-center text-center px-2 text-[11px] text-[#6F6B64]">Foto no disponible</div>
                    : <div className="w-full aspect-[3/4] rounded-lg border border-[#E5E2DB] bg-[#F6F5F2] animate-pulse" />}
            {!sinPie && <p className="text-[9px] text-[#57544E] mt-0.5 truncate">{foto.fecha} {foto.kind}</p>}
        </a>
    );
};

const CalmaMembresias = ({ membresia }) => {
    if (!membresia?.length) return null;
    const fmt = (d) => pintarDia(d, undefined, '?');
    const ordenadas = [...membresia].sort((a, b) => (b.inicio || '').localeCompare(a.inicio || ''));
    return (
        <Card className="bg-white border-[#E5E2DB]">
            <CardHeader className="pb-2"><CardTitle className="text-sm text-[#6F6B64] uppercase tracking-wider flex items-center gap-2">Historial de membresías</CardTitle></CardHeader>
            <CardContent><div className="space-y-2">{ordenadas.map((m, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-[#F6F5F2] rounded-lg border border-[#E5E2DB]">
                    <span className="text-[#1C1B19] text-sm font-medium">{m.nombre || 'Plan'}</span>
                    <span className="text-[#6F6B64] text-xs">{fmt(m.inicio)} a {fmt(m.fin)}</span>
                </div>
            ))}</div></CardContent>
        </Card>
    );
};

const CalmaSuplementos = ({ sup }) => {
    if (!sup || (!sup.protocolos?.length && !sup.observaciones)) return null;
    return (
        <Card className="bg-white border-[#E5E2DB]">
            <CardHeader className="pb-2"><CardTitle className="text-sm text-[#6F6B64] uppercase tracking-wider flex items-center gap-2">Suplementación</CardTitle></CardHeader>
            <CardContent className="space-y-3">
                {sup.protocolos?.length > 0 && (
                    <div className="space-y-1">
                        {sup.protocolos.map((p, i) => (
                            <div key={i} className="flex items-center justify-between p-2.5 bg-[#F6F5F2] rounded-lg border border-[#E5E2DB]">
                                <span className="text-[#57544E] text-xs">{p.fecha}</span>
                                <span className="text-[#57544E] text-xs">códigos: {p.raw || (p.codigos || []).join('|')}</span>
                            </div>
                        ))}
                    </div>
                )}
                {sup.observaciones && (
                    <div>
                        <p className="text-[10px] text-[#6F6B64] uppercase tracking-wider mb-1">Observaciones del entrenador</p>
                        <p className="text-[#1C1B19] text-sm whitespace-pre-wrap">{sup.observaciones}</p>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

// La MISMA gráfica que ve el cliente (punto 4.13). Antes eran dos, con los mismos fallos
// escritos dos veces: el eje por categorías juntando todos los «9 ago» de cuatro años y los
// colores a pelo. Ahora hay una sola y las dos pantallas enseñan lo mismo.
// LA SERIE QUE NO PUEDE SER, DICHA AQUÍ (Francisco, 5-09). Siete fichas de producción traen
// pesos imposibles del import de Calma -- Jesús va de 50 a 145 kg en la suya, y otras dos son
// de entrenadores que usaron la app como cuaderno --, y esa gráfica no se puede leer como la
// de nadie más. Francisco decidió no tocar los datos, que son reales y borrarlos no tiene
// vuelta: se avisa y ya. Un salto de más de 20 kg entre dos pesajes seguidos, o un recorrido
// de 30 kg o más de punta a punta, no es una persona adelgazando.
const serieImposible = (puntos) => {
    if ((puntos || []).length < 2) return null;
    const pesos = puntos.map(p => p.peso).filter(w => typeof w === 'number');
    if (pesos.length < 2) return null;
    const min = Math.min(...pesos), max = Math.max(...pesos);
    const orden = [...puntos].sort((a, b) => new Date(a.fecha) - new Date(b.fecha));
    let salto = 0;
    for (let i = 1; i < orden.length; i++) {
        salto = Math.max(salto, Math.abs((orden[i].peso || 0) - (orden[i - 1].peso || 0)));
    }
    if (max - min < 30 && salto <= 20) return null;
    return { min, max, salto: Math.round(salto) };
};

// LA CURVA DE LA FICHA SALE DE DONDE SALE LA DEL CLIENTE (13-09, ficha 66 · Francisco).
//
// Esto se dibujaba con `reports[].weight`: un punto por reporte, o sea uno al mes. Faltaban
// los pesajes semanales de la serie del perfil y los que viajaron dentro de los ajustes de
// macros (los importados de Calma van de 2022 en adelante), y sobre todo faltaba el reparto
// por semanas del 8-09, que es el que convierte tres puntos sueltos en doce y deja ver una
// tendencia. Con eso, el entrenador miraba una curva y el cliente otra del mismo día.
//
// Ahora pide `GET /admin/clients/{id}/evolucion`, que por dentro es la misma función que le
// sirve la suya al cliente. Si esa llamada falla, se sigue pintando con los reportes: es la
// curva que había hasta hoy y es mejor que dejar la tarjeta en blanco.
// LA SECCIÓN «PESO» DE SEGUIMIENTO (Francisco, 21-10). Los números los decide
// `lib/resumenDelPeso`; aquí solo se pintan. Lo que no hay dice «sin dato».
const PesoDeLaSeccion = ({ etiqueta, punto, testid }) => (
    <div className="rounded-xl bg-[#F6F5F2] border border-[#E5E2DB] px-4 py-3" data-testid={testid}>
        <p className="text-[11px] font-bold uppercase tracking-wider text-[#57544E]">{etiqueta}</p>
        {punto ? (
            <p className="text-[#1C1B19]">
                <span className="text-xl font-bold tabular-nums">{kilos(punto.peso)} kg</span>
                {punto.fecha && <span className="text-xs text-[#6F6B64] ml-2">{_fechaCorta(punto.fecha)}</span>}
            </p>
        ) : (
            <p className="text-sm text-[#6F6B64] italic py-1">sin dato</p>
        )}
    </div>
);

const SeccionDelPeso = ({ evolucion, macroHistory, perfil }) => {
    const r = useMemo(() => resumenDelPeso(evolucion?.weight, macroHistory, perfil), [evolucion, macroHistory, perfil]);
    return (
        <Card className="bg-white border-[#E5E2DB] text-[#1C1B19]" data-testid="seccion-peso">
            <CardContent className="p-4 md:p-5 space-y-3">
                <h2 className="m-0 font-heading text-xl font-bold uppercase tracking-wide text-[#1C1B19]">Peso</h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <PesoDeLaSeccion etiqueta="Peso actual" punto={r.actual} testid="peso-actual" />
                    <PesoDeLaSeccion etiqueta="Peso últimos macros" punto={r.ultimosMacros} testid="peso-ultimos-macros" />
                    <PesoDeLaSeccion etiqueta="Peso inicial" punto={r.inicial} testid="peso-inicial" />
                </div>
                {r.frase && (
                    <p className="text-sm text-[#3D3A35]" data-testid="peso-cambio">{r.frase}</p>
                )}
            </CardContent>
        </Card>
    );
};

// LA PESTAÑA «EVOLUCIÓN DEL PESO» (21-10): arriba la sección «Peso» y debajo la curva. Las
// dos leen la misma curva (`/admin/clients/{id}/evolucion`), que se pide UNA vez al abrir la
// pestaña (solo se monta entonces) y otra vez si la ficha se recarga con reportes nuevos.
// Si esa llamada falla, la gráfica sigue pintando con los reportes.
const PesoDeSeguimiento = ({ api, clientId, reports, macroHistory, perfil }) => {
    const [evolucion, setEvolucion] = useState(null);
    useEffect(() => {
        if (!api || !clientId) return undefined;
        let vivo = true;
        api.get(`/admin/clients/${clientId}/evolucion`)
            .then(r => { if (vivo) setEvolucion(r.data || null); })
            .catch(e => { console.error('No se pudo cargar la curva de peso del cliente:', e); });
        return () => { vivo = false; };
    }, [api, clientId, reports]);
    return (
        <div className="space-y-4">
            {/* EL PESO DE UN VISTAZO (Francisco, 21-10): el actual, el de sus últimos macros y
                el primero, con lo que ha cambiado. */}
            <SeccionDelPeso evolucion={evolucion} macroHistory={macroHistory} perfil={perfil} />
            {/* LA CURVA, LA MISMA QUE VE ÉL (13-09, ficha 66). Manda `ciclos.inicio`, igual
                que en la pantalla del cliente; el ancla del cobro se queda de reserva para el
                que aún no tiene cuaderno. */}
            <WeightEvolution evolucion={evolucion} reports={reports} objetivo={perfil?.goal}
                desdeElCiclo={perfil?.ciclo_actual?.inicio || perfil?.cycle_start}
                semanasDelCiclo={perfil?.ciclo_actual?.semanas || perfil?.cycle_total_weeks}
                perfil={perfil} />
        </div>
    );
};

// `evolucion`: la respuesta de `/admin/clients/{id}/evolucion`, que pide `PesoDeSeguimiento`
// una vez para esta gráfica y la sección «Peso» (21-10).
const WeightEvolution = ({ evolucion, reports, objetivo, desdeElCiclo = null,
                          semanasDelCiclo = null, perfil = null }) => {

    // Con su marca (12-09): quién se sale de los tres kilos por semana lo dice el servidor
    // y lo aparta la propia gráfica, igual que en la pantalla del cliente. Aquí no se filtra.
    const deLaSerie = (evolucion?.weight || [])
        .filter(w => w.value != null)
        .map(w => ({ fecha: w.date, peso: w.value, fiable: w.fiable }));
    const puntos = deLaSerie.length ? deLaSerie : (reports || [])
        .filter(r => r.weight != null)
        .map(r => ({ fecha: r.created_at, peso: r.weight }));
    if (!puntos.length) return null;
    const rara = serieImposible(puntos);
    return (
        <Card className="bg-white border-[#E5E2DB]">
            <CardHeader className="pb-2"><CardTitle className="text-sm text-[#57544E] uppercase tracking-wider flex items-center gap-2"><Scale className="w-4 h-4" />Evolución del peso ({puntos.length})</CardTitle></CardHeader>
            <CardContent>
                {rara && (
                    <p className="mb-3 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-800"
                       data-testid="peso-serie-rara">
                        Esta curva no es fiable: sus pesajes van de {rara.min} a {rara.max} kg
                        {rara.salto > 20 ? `, con saltos de hasta ${rara.salto} kg de uno a otro` : ''}.
                    </p>
                )}
                {/* El color del cambio, contra el objetivo del cliente (P46 del 23-08):
                    el coach tampoco tiene que leer un +2 en volumen como alarma. Y con la
                    ficha entera (doc de Jesús del 2-09, fase 2): la tarjeta del objetivo
                    enseña el del ciclo, el actual, el foco y «vas por el bloque N», lo
                    mismo que ve el cliente. */}
                <GraficaDePeso puntos={puntos} objetivo={objetivo} desdeElCiclo={desdeElCiclo}
                    semanasDelCiclo={semanasDelCiclo}
                    porSemanas={evolucion?.curva_semanal || null} perfil={perfil} ajeno />
            </CardContent>
        </Card>
    );
};

const MEAL_ORDER = { C1: 1, C2: 2, C3: 3, C4: 4, C5: 5, C6: 6, Intra: 7, Post: 8 };
const MEAL_LABEL = { Intra: 'Intra-entreno', Post: 'Post-entreno', C1: 'Comida 1', C2: 'Comida 2', C3: 'Comida 3', C4: 'Comida 4', C5: 'Comida 5', C6: 'Comida 6' };

// Lo que el cliente contestó en el reporte nuevo, con las palabras del formulario.
// Los reportes viejos no traen nada de esto y el bloque no sale.
const RESPUESTA_LABEL = {
    dieta_dificultad: { nada: 'Nada, la llevo bien', algun_dia: 'Algún día suelto',
                        bastante: 'Bastante', no_he_podido: 'No he podido' },
    // Las tres del mensual de hoy («Tu cardio» del mensual anterior al 25-10) y la vieja «mas».
    cardio_proximo_mes: { mismas: 'Mantenme las mismas: este mes cumplo', mas: 'Puedo con más sesiones',
                          menos: 'Bájamelas y las cumplo', quitar: 'Quítamelo, no lo voy a hacer' },
    // Por qué dejó días sin tomarla («Tu suplementación» del mensual anterior al 25-10).
    suplementacion_motivo: { se_me_olvidaba: 'Se me olvidaba', se_me_acabo: 'Se me acabó',
                             no_me_sentaba_bien: 'No me sentaba bien', no_quiero_seguir: 'No quiere seguir tomándola' },
    suplementacion: { todos: 'Todos', alguno_no: 'Alguno no', ninguno: 'Ninguno' },
    energia_motivo: { duermo_poco: 'Duermo poco', estres_trabajo: 'Estrés del trabajo',
                      como_poco: 'Como poco', no_lo_se: 'No lo sé' },
    regularidad: { a_mi_manera_sigo: 'A su manera, quiere seguir así',
                   a_mi_manera_quiero_rutina: 'A su manera, quiere probar una rutina tuya',
                   con_tu_rutina_sigo: 'Con tu rutina, quiere seguir así' },
    rutina_del_mes: { basica: 'La quiere · básica', avanzada: 'La quiere · avanzada', ahora_no: 'Ahora no' },
    // El compromiso no va aquí: son las cuatro opciones de Calma y su corta y su color los
    // manda el servidor (`calma_cortas` y `calma_letras`, `core/letras_de_calma._DE_LA_APP`).
};

// El texto libre pasa por el mismo filtro que el resto (punto 104, 4-09): un «-» o un
// «XX» en molestias o sugerencias no es una respuesta, es un hueco que se rellenó.
// `tono`: las clases del color de Calma de esa respuesta (`lib/coloresDelReporte`), si lo tiene.
const Fila = ({ que, valor, tono }) => (
    <FilaDeRespuesta pregunta={que} respuesta={typeof valor === 'string' ? sinElRelleno(valor) : valor} tono={tono} />
);

// LO QUE CONTESTÓ EN EL REPORTE MENSUAL DE CALMA. Las ocho preguntas de siempre, con las
// palabras del cliente. Se traen tal cual y no como un porcentaje: la respuesta es «Salvo
// algún día puntual, he cumplido con todo lo que me has marcado», y convertir eso en un
// «85 %» es inventárselo.
//
// El orden es el del formulario, que es el que Jesús lee: primero cómo fue el mes, luego el
// entreno y el cardio, luego la dieta y el esfuerzo que le costó, y al final el descanso, la
// suplementación y hacia dónde quiere ir el mes que viene.
const PREGUNTAS_CALMA = [
    ['compromiso', PREGUNTA_DEL_MENSUAL.compromiso],
    ['cumplimientoEntrenamiento', PREGUNTA_DEL_MENSUAL.cumplimientoEntrenamiento],
    ['problemasParaEntrenar', PREGUNTA_DEL_MENSUAL.problemasParaEntrenar],
    ['cumplimientoCardio', PREGUNTA_DEL_MENSUAL.cumplimientoCardio],
    ['cumplimientoDieta', PREGUNTA_DEL_MENSUAL.cumplimientoDieta],
    ['esfuerzoParaCumplirDieta', PREGUNTA_DEL_MENSUAL.esfuerzoParaCumplirDieta],
    ['descanso', PREGUNTA_DEL_MENSUAL.descanso],
    ['tomaDeSuplementos', PREGUNTA_DEL_MENSUAL.tomaDeSuplementos],
    ['detalleSuplementos', 'Cuáles'],
    ['objetivo', PREGUNTA_DEL_MENSUAL.objetivo],
];

// «SIN CALIFICAR CUMPLIMIENTO» NO ES UNA RESPUESTA, NI LA BASURA DEL IMPORT TAMPOCO: la
// regla (`sinElRelleno`) vivía aquí y el 4-09 se fue a `lib/rellenoDelImport`, con su
// porqué, para que ningún bloque tuviera «otro camino» (punto 104).

// Las respuestas que de verdad son del cliente, ya sin el relleno, y la CORTA donde la hay
// (`lib/coloresDelReporte.respuestaDelReporte`, 21-10).
const filasDeCalma = (reporte) => PREGUNTAS_CALMA
    .map(([k, etiqueta]) => [k, etiqueta, respuestaDelReporte(reporte, k)])
    .filter(([, , r]) => r.valor);

// Con el color de Calma de cada una: la letra que manda el servidor (`calma_letras`); sin
// ella, sin color (`lib/coloresDelReporte.letraDelReporte`).
const RespuestasDeCalma = ({ reporte }) => {
    // Solo los de Calma: un mensual de la app también trae `calma_cortas` (su compromiso,
    // con la corta de Calma) y ya sale en `RespuestasDelReporte`.
    if (!reporte?.calma_respuestas) return null;
    const filas = filasDeCalma(reporte);
    if (!filas.length) return null;
    return (
        <div className="rounded-lg px-3 py-2 bg-white border border-[#E5E2DB]" data-testid="respuestas-calma">
            <p className="m-0 mb-1 text-[#57544E] text-[11px] uppercase tracking-wider">
                Lo que contestó en su reporte mensual
            </p>
            {filas.map(([k, etiqueta, r]) => (
                <FilaDeRespuesta key={k} pregunta={etiqueta} respuesta={r.valor}
                    tono={claseDelTono(letraDelReporte(reporte, k))} />
            ))}
        </div>
    );
};

// ¿Contestó algo en el formulario nuevo?
const hayRespuestasDelReporte = (r) => {
    const e = r.entreno || {};
    const s = r.suplementacion || {};
    return !!(r.tipo || r.molestias || r.sensaciones || r.semana_proxima || r.dieta_dificultad
        || e.regularidad
        || e.estrellas || e.rutina_del_mes || s.respuesta || r.cardio_proximo_mes
        || (r.lesiones || []).length || r.valoracion_resultado || r.motivacion || r.sugerencias
        || r.compromiso || r.expectativas != null || r.suplementacion_motivo
        || (r.ejercicios_molestos || []).length || (r.maquinas_no_disponibles || []).length
        || (r.respuestas_quincenal || []).length || (r.respuestas_mensual || []).length);
};

// Las cinco estrellas de una respuesta de 1 a 5 (sin respuesta, nada). Fuera del componente
// porque la tabla de los mensuales de Seguimiento las pinta igual.
const estrellas = (n) => (n ? '★'.repeat(n) + '☆'.repeat(5 - n) : null);

// LAS MEDIDAS DE ESE REPORTE, con la diferencia contra la toma de antes. Estaban en «Su
// último reporte» de Macros; al irse de allí (Francisco, 19-10) pasan a «Abrir el
// reporte», que es donde se lee el resto de ese reporte. Las tomas son las del servidor,
// las mismas de Evolución: la de antes puede ser la del alta o unas apuntadas sueltas.
const _DE_DONDE_LA_TOMA = { inicio: 'las del alta del', suelta: 'las apuntadas el' };
const MedidasDelReporte = ({ reporte, tomas }) => {
    const { toma, anterior: previo } = tomaDelReporteYAnterior(tomas, reporte.id);
    // Sin la lista (no ha llegado o falló), las del reporte sin diferencia: mejor eso que nada.
    const filas = filasDeMedidas(toma ? toma.measurements : reporte.measurements);
    if (!filas.length) return null;
    const prev = previo?.measurements;
    const conDiferencia = filas.map((m) => {
        const antes = prev ? (valorAnterior(prev, m.key) ?? (prev[m.key] != null ? Number(prev[m.key]) : null)) : null;
        const d = diferencia(m.valor, antes);
        return {
            ...m,
            extra: d && <span className={`ml-1.5 text-xs font-normal ${d.signo > 0 ? 'text-red-600' : d.signo < 0 ? 'text-emerald-700' : 'text-[#57544E]'}`}>{d.texto}</span>,
        };
    });
    const titulo = previo
        ? `Medidas · contra ${_DE_DONDE_LA_TOMA[previo.origen] || 'las del'} ${_fechaCorta(previo.fecha)}`
        : 'Medidas';
    return (
        <div data-testid="medidas-del-reporte">
            <TablaDeMedidas filas={conDiferencia} titulo={titulo} />
        </div>
    );
};

// EL MENSUAL DEL DOC «REPORTE MENSUAL PLAN PERSONALIZADO» (25-10): cada respuesta llega con
// su sección y la pregunta que se le hizo (`core/mensual`), y se lee tal cual, sección a
// sección. Las de 1 a 5, en estrellas; las demás, con el texto de lo que eligió.
const RespuestasDelMensual = ({ reporte: r }) => {
    const porSeccion = [];
    for (const x of r.respuestas_mensual) {
        const ultima = porSeccion[porSeccion.length - 1];
        if (ultima && ultima.seccion === x.seccion) ultima.filas.push(x);
        else porSeccion.push({ seccion: x.seccion, filas: [x] });
    }
    // «Tu peso» (28-10): el del reporte y, si salió de su check-in de la semana, de qué día.
    const peso = r.weight != null ? `${String(r.weight).replace('.', ',')} kg${r.peso_fecha
        ? ` · de su check-in del ${_fechaCorta(r.peso_fecha)}` : ''}` : null;
    return (
        <div className="rounded-lg px-3 py-2 bg-white border border-[#E5E2DB]" data-testid="respuestas-reporte">
            <p className="text-[#57544E] text-[11px] uppercase tracking-wider">Reporte mensual</p>
            <Fila que="Tu peso" valor={peso} />
            {porSeccion.map(({ seccion, filas }) => (
                <div key={seccion} className="mt-1.5" data-testid="respuestas-mensual-seccion">
                    <p className="m-0 text-[#B43E06] text-[11px] font-bold uppercase tracking-wider">{seccion}</p>
                    {filas.map((x) => (
                        <Fila key={x.clave} que={x.pregunta}
                            valor={typeof x.valor === 'number'
                                ? `${estrellas(x.valor)}${x.de_checkin ? ' · de sus check-in' : ''}` : x.texto} />
                    ))}
                    {filas.some((x) => x.clave === 'objetivo_cambio') && r.proximo_objetivo && (
                        <Fila que="El que pide" valor={nombreDeLaPropuesta(r.proximo_objetivo)} />
                    )}
                </div>
            ))}
            <Fila que="Ejercicios que no puede hacer" valor={(r.ejercicios_molestos || []).join(', ') || null} />
            <Fila que="Maquinaria básica que no tiene" valor={(r.maquinas_no_disponibles || []).join(', ') || null} />
            <Fila que="Otra maquinaria (sí la tiene)" valor={(r.maquinas_especiales || []).join(', ') || null} />
        </div>
    );
};

const RespuestasDelReporte = ({ reporte: r }) => {
    const e = r.entreno || {};
    const s = r.suplementacion || {};
    if (!hayRespuestasDelReporte(r)) return null;
    if ((r.respuestas_mensual || []).length) return <RespuestasDelMensual reporte={r} />;

    return (
        <div className="rounded-lg px-3 py-2 bg-white border border-[#E5E2DB]"
            data-testid="respuestas-reporte">
            {r.tipo && (
                <p className="text-[#57544E] text-[11px] uppercase tracking-wider">Reporte {r.tipo}</p>
            )}
            {/* LO QUE CONTESTA EN EL REPORTE DE PASOS (12-10): las cinco estrellas del paso 1
                cuando no hay cierres del día y las dos escalas de 0 a 10 del paso 2. Se
                guardaban y aquí no salían: de un quincenal el equipo solo veía el peso. */}
            <Fila que="Dieta" valor={estrellas(r.dieta_grado)} />
            <Fila que="Entreno" valor={estrellas(r.entreno_grado)} />
            <Fila que="Cardio" valor={estrellas(r.cardio_grado)} />
            <Fila que="Suplementación" valor={estrellas(r.suplementacion_grado)} />
            <Fila que="Descanso" valor={estrellas(r.descanso_grado)} />
            <Fila que="Sensaciones generales (0 a 10)" valor={r.sensaciones_0a10 != null ? String(r.sensaciones_0a10) : null} />
            <Fila que="Esfuerzo y resultados (0 a 10)" valor={r.esfuerzo_resultados != null ? String(r.esfuerzo_resultados) : null} />
            {/* LAS CUATRO DEL QUINCENAL DEL 24-10, con la pregunta que se le hizo (la guarda el
                servidor con la respuesta, `core/quincenal`). La quinta, la libre, es la nota. */}
            {(r.respuestas_quincenal || []).map((x) => (
                <Fila key={x.clave} que={x.pregunta}
                    valor={x.respuesta === 'si' ? `Sí: ${x.detalle || ''}` : 'No'} />
            ))}
            <Fila que="Molestias" valor={r.molestias} />
            <Fila que="Sensaciones" valor={estrellas(r.sensaciones)} />
            {/* La del semanal (doc 21-08): lo que le altera la rutina la semana que
                viene. Es lo que hay que leer ANTES de ajustar: el feedback del domingo
                es para esa semana. */}
            <Fila que="La semana que viene" valor={r.semana_proxima} />
            <Fila que="La dieta" valor={RESPUESTA_LABEL.dieta_dificultad[r.dieta_dificultad]} />
            <Fila que="Entreno del mes" valor={RESPUESTA_LABEL.regularidad[e.regularidad]} />
            <Fila que="Qué tal el entreno" valor={estrellas(e.estrellas)} />
            <Fila que="Del entreno" valor={e.nota} />
            <Fila que="La rutina del mes" valor={RESPUESTA_LABEL.rutina_del_mes[e.rutina_del_mes]} />
            {e.quiere_saber_del_silver && (
                <p className="m-0 py-1.5 text-sm font-semibold text-[#B43E06]">Quiere que le cuentes el plan de arriba</p>
            )}
            {(r.lesiones || []).filter(l => l.estado_mes).map((l, i) => (
                <FilaDeRespuesta key={i} pregunta={`Lesión: ${l.zona}`}
                    respuesta={(l.ejercicios || []).length ? `${l.estado_mes}. No puede: ${l.ejercicios.join(', ')}` : l.estado_mes} />
            ))}
            <Fila que="Lesión nueva" valor={r.lesion_nueva} />
            <Fila que="Cardio el mes que viene" valor={RESPUESTA_LABEL.cardio_proximo_mes[r.cardio_proximo_mes]} />
            <Fila que="Suplementación" valor={RESPUESTA_LABEL.suplementacion[s.respuesta]}
                tono={claseDelTono(letraDelReporte(r, 'tomaDeSuplementos'))} />
            <Fila que="Por qué no la tomó" valor={RESPUESTA_LABEL.suplementacion_motivo[r.suplementacion_motivo]} />
            <Fila que="Cuál y por qué" valor={s.detalle} />
            {/* LAS DOS LISTAS DEL MENSUAL (21-10, Jesús: «le ha puesto cosas de entrenamiento,
                aquí falta»): se guardaban y aquí no salían. */}
            <Fila que="Ejercicios que le molestan" valor={(r.ejercicios_molestos || []).join(', ') || null} />
            <Fila que="Máquinas que no tiene" valor={(r.maquinas_no_disponibles || []).join(', ') || null} />
            <Fila que="Energía" valor={RESPUESTA_LABEL.energia_motivo[r.energia_motivo]} />
            <Fila que="Cómo lo valora" valor={estrellas(r.valoracion_resultado)} />
            <Fila que="Motivación" valor={estrellas(r.motivacion)} />
            {/* EL COMPROMISO Y LAS EXPECTATIVAS (14-10, REP-09): sustituyeron a las dos estrellas
                de arriba en el mensual, el cliente las contesta cada mes y aquí no salían. */}
            {/* La corta y el color, del servidor (`calma_cortas`, `calma_letras`): son las
                cuatro opciones de Calma. */}
            {r.compromiso && (
                <Fila que="Su compromiso" valor={respuestaDelReporte(r, 'compromiso').valor}
                    tono={claseDelTono(letraDelReporte(r, 'compromiso'))} />
            )}
            <Fila que="El programa cumple sus expectativas (0 a 10)" valor={r.expectativas != null ? String(r.expectativas) : null} />
            <Fila que="Sugerencias" valor={r.sugerencias} />
        </div>
    );
};

// ========== SEGUIMIENTO COMO EN EL LIENZO DEL 19-10 ==========
//
// Arriba lo que se mira de un mensual: sus respuestas en una tabla (en el móvil, una tarjeta
// plegable por mensual), sus fotos con la misma pose en la misma columna, el feedback del
// último y, plegados, los quincenales, que son para el entrenador.
//
// QUÉ REPORTE ES UN MENSUAL NO SE DECIDE AQUÍ: son los puntos de Evolución que trae
// `GET /admin/clients/{id}/puntos` (`core/puntos.es_punto`: ni quincenales ni pesajes
// sueltos de Calma), con su día de España, su peso, su % graso y sus fotos, una por pose.
// Las respuestas salen del reporte de la ficha con ese `report_id`.

// Las columnas del lienzo. Cada una con la respuesta del formulario de la app y, para los
// meses de Calma, la misma pregunta de `calma_respuestas` (las de PREGUNTAS_CALMA). El color
// es siempre la letra que manda el servidor (`calma_letras`): la de Calma y, en los de la
// app, la del compromiso y la suplementación, que son las mismas opciones que allí. El
// compromiso de la app va por `calma_cortas` también: su corta es la de Calma.
const sinRespuestaDeLaApp = () => null;
const delMensual = (r, clave) => (r.respuestas_mensual || []).find((x) => x.clave === clave);
const COLUMNAS_DEL_MENSUAL = [
    [PREGUNTA_DEL_MENSUAL.compromiso, sinRespuestaDeLaApp, 'compromiso'],
    [PREGUNTA_DEL_MENSUAL.objetivo, r => (r.proximo_objetivo ? nombreDeLaPropuesta(r.proximo_objetivo) : null), 'objetivo'],
    [PREGUNTA_DEL_MENSUAL.cumplimientoDieta, r => estrellas(r.dieta_grado), 'cumplimientoDieta'],
    // Desde el mensual del 25-10 es «¿Cuánto te ha costado?», de 1 a 5 (`respuestas_mensual`).
    [PREGUNTA_DEL_MENSUAL.esfuerzoParaCumplirDieta, r => RESPUESTA_LABEL.dieta_dificultad[r.dieta_dificultad]
        || estrellas(delMensual(r, 'dieta_coste')?.valor), 'esfuerzoParaCumplirDieta'],
    [PREGUNTA_DEL_MENSUAL.tomaDeSuplementos, r => RESPUESTA_LABEL.suplementacion[r.suplementacion?.respuesta] || estrellas(r.suplementacion_grado), 'tomaDeSuplementos'],
    // El mensual de la app guarda el entreno en `entreno` (estrellas con rutina, regularidad
    // sin ella), igual que lo lee `RespuestasDelReporte`; los viejos, en `entreno_grado`.
    [PREGUNTA_DEL_MENSUAL.cumplimientoEntrenamiento, r => estrellas(r.entreno_grado) || estrellas(r.entreno?.estrellas)
        || RESPUESTA_LABEL.regularidad[r.entreno?.regularidad] || ENTRENO_REPORTE[r.cumplimiento_entreno], 'cumplimientoEntrenamiento'],
    // EL CARDIO Y LO QUE LE IMPIDE ENTRENAR (21-10, Jesús: «faltan más preguntas... le ha
    // puesto cosas de entrenamiento, aquí falta»): en Calma se contestaban cada mes y la
    // tabla no los traía. El mensual de la app no pregunta si cumplió el cardio: lo cuenta
    // la app, y el servidor manda las sesiones hechas de las que tocaban en `cardio_del_mes`
    // (`core/mes_del_reporte`: lo que dijo su informe guardado o, sin él, su cardio pautado
    // contra los «sí» de sus cierres del día, como «Mi semana»). Sin cardio pautado en ese
    // mes, la celda vacía.
    // Lo que pide para el mes que viene (`cardio_proximo_mes`) es otra pregunta: va en el
    // detalle del reporte.
    // Desde el mensual del 25-10, si no tenía check-in con los que contarlo, el cliente
    // contesta cuánto hizo (`cardio_cumplido`): manda lo que dijo, porque sin cierres la
    // cuenta de la app saldría a cero (Francisco, 25-10).
    // Y desde el 28-10 lo contesta en estrellas (`cardio_grado`), solo si no tenía check-in.
    [PREGUNTA_DEL_MENSUAL.cumplimientoCardio, r => delMensual(r, 'cardio_cumplido')?.texto
        || (delMensual(r, 'cardio_grado') && !delMensual(r, 'cardio_grado').de_checkin
            ? estrellas(delMensual(r, 'cardio_grado').valor) : null) || (r.cardio_del_mes
        ? `${r.cardio_del_mes.hechas} de ${r.cardio_del_mes.previstas} sesiones` : estrellas(r.cardio_grado)), 'cumplimientoCardio'],
    [PREGUNTA_DEL_MENSUAL.descanso, r => estrellas(r.descanso_grado) || delMensual(r, 'descanso_resenable')?.texto, 'descanso'],
    [PREGUNTA_DEL_MENSUAL.problemasParaEntrenar, r => (r.ejercicios_molestos || []).join(', ') || r.molestias,
        'problemasParaEntrenar'],
];

// `tono`: las clases del color de Calma de la celda, o `null` si esa respuesta no lo tiene.
// De Calma, la respuesta CORTA (21-10); la larga va en `larga`, para leerla al pasar.
const celdasDelMensual = (r) => COLUMNAS_DEL_MENSUAL.map(([que, deLaApp, deCalma]) => {
    if (!r) return { que, valor: null, tono: null, larga: null };
    // El mensual del formulario nuevo (estrellas y sí o no), con frase corta y color (28-10).
    const nuevo = celdaDelMensualNuevo(r, deCalma, nombreDeLaPropuesta);
    if (nuevo) return { que, valor: nuevo.valor, larga: nuevo.larga, tono: claseDelTono(nuevo.letra) };
    const tono = claseDelTono(letraDelReporte(r, deCalma));
    const app = sinElRelleno(deLaApp(r));
    if (app) return { que, valor: app, larga: null, tono };
    const { valor, larga } = respuestaDelReporte(r, deCalma);
    return { que, valor, larga: larga !== valor ? larga : null, tono };
});

// La ficha trae los 50 reportes más recientes (`GET /admin/clients/{id}`); un mensual más
// antiguo llega como punto, pero sin sus respuestas. Se dice, en vez de «sin respuesta».
const SIN_EL_REPORTE = 'Reporte antiguo: sus respuestas no se cargan aquí';

const _diaDelMensual = (fecha) => pintarDia(fecha, { day: 'numeric', month: 'short', year: 'numeric' }, '-');
// EL % GRASO NO VA EN CADA REPORTE: sale de su serie (`porcentajes_grasos`), que llenan el
// entrenador (bajo cada foto de la Comparativa o al guardar macros) y el cliente cada doce
// semanas, y se pega al reporte si cae a tres días o menos (`core/puntos._valor_cercano`).
// Peso y % graso van en DOS COLUMNAS (Francisco, 20-10: no tienen que ver uno con otro);
// sin ninguno cerca, la celda del % se queda vacía (21-10: sin ruido).
const _peso = (punto) => (punto.peso != null ? `${kilos(punto.peso)} kg` : 'sin peso');
const _grasa = (punto) => (punto.grasa != null ? `${kilos(punto.grasa)} %` : null);

// LA TABLA COMO LA HOJA DE CLIENTE DE CALMA (21-10). Jesús: «muy feo», «veo muchos números
// que ya me estreso», «en Calma lo veo mucho más fácil, mucho más visual». Una fila por mes,
// cada celda con la respuesta CORTA y el color de Calma, el texto en tres líneas como mucho
// (entero al pasar el ratón) y sin «sin respuesta» repetido: una celda vacía ya lo dice.
const CeldaDelMensual = ({ c }) => (
    <td className={`px-2 py-1.5 rounded-md align-middle ${c.tono || (c.valor ? 'bg-[#F6F5F2]' : '')}`}
        title={c.larga || (c.valor && c.valor.length > 40 ? c.valor : undefined)}>
        {c.valor && (
            <span className={`line-clamp-3 leading-snug [hyphens:auto] ${c.tono ? 'font-semibold' : 'text-[#3D3A35]'}`}>{c.valor}</span>
        )}
    </td>
);

// El enlace a la hoja del cliente en Calma (Google Sheets, `calma_raw.ruta_ficha_drive`):
// Jesús quiere «ver aquí el formulario de Google también para poder consultarlo». De los
// formularios no se guardó ningún enlace; la hoja trae sus respuestas. Solo si es de Google.
const _esHojaDeGoogle = (url) => /^https:\/\/docs\.google\.com\/spreadsheets\//.test(String(url || ''));

const TablaDeMensuales = ({ mensuales, onAbrir, hojaDeCalma = null }) => (
    <Card className="bg-white border-[#E5E2DB] text-[#1C1B19]" data-testid="tabla-mensuales">
        <CardContent className="p-4 md:p-5 space-y-3">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="m-0 font-heading text-xl font-bold uppercase tracking-wide text-[#1C1B19]">Reportes mensuales</h2>
                {_esHojaDeGoogle(hojaDeCalma) && (
                    <a href={hojaDeCalma} target="_blank" rel="noopener noreferrer" data-testid="hoja-de-calma"
                        className="text-xs font-semibold text-[#57544E] hover:text-[#B43E06] hover:underline">
                        Su hoja de Calma
                    </a>
                )}
            </div>
            {/* En el ordenador, la tabla; se desplaza dentro de la tarjeta si no cabe. */}
            <div className="hidden md:block overflow-x-auto">
                <table lang="es" className="w-full min-w-[1080px] table-fixed border-separate [border-spacing:3px] text-[12px]">
                    <colgroup>
                        <col className="w-[84px]" /><col className="w-[60px]" /><col className="w-[48px]" />
                        {COLUMNAS_DEL_MENSUAL.map(([que]) => <col key={que} />)}
                    </colgroup>
                    <thead>
                        <tr className="text-left text-[#6F6B64] text-[10px] uppercase align-bottom">
                            <th scope="col" className="sticky left-0 z-10 bg-white px-2 py-1 font-semibold">Fecha</th>
                            <th scope="col" className="px-2 py-1 font-semibold">Peso</th>
                            <th scope="col" className="px-2 py-1 font-semibold">% graso</th>
                            {COLUMNAS_DEL_MENSUAL.map(([que]) => (
                                <th key={que} scope="col" className="px-2 py-1 font-semibold leading-tight break-words">{que}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {mensuales.map(({ punto, reporte }) => (
                            <tr key={punto.id}>
                                <th scope="row" className="sticky left-0 z-10 bg-white px-2 py-1.5 text-left font-bold whitespace-nowrap">
                                    {reporte ? (
                                        <button type="button" onClick={() => onAbrir(reporte.id)} data-testid={`mensual-${reporte.id}`}
                                            className="text-[#1C1B19] hover:text-[#B43E06] underline-offset-2 hover:underline">
                                            {_diaDelMensual(punto.fecha)}
                                        </button>
                                    ) : _diaDelMensual(punto.fecha)}
                                </th>
                                <td className="px-2 py-1.5 whitespace-nowrap tabular-nums text-[#3D3A35]">{_peso(punto)}</td>
                                <td className="px-2 py-1.5 whitespace-nowrap tabular-nums text-[#3D3A35]" data-testid={`grasa-${punto.id}`}>
                                    {_grasa(punto)}
                                </td>
                                {!reporte ? (
                                    <td colSpan={COLUMNAS_DEL_MENSUAL.length} className="px-2 py-1.5 text-[#6F6B64]">
                                        {SIN_EL_REPORTE}
                                    </td>
                                ) : celdasDelMensual(reporte).map(c => <CeldaDelMensual key={c.que} c={c} />)}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {/* En el móvil, una tarjeta plegable por mensual; el más reciente, abierto. Solo
                las preguntas que contestó. */}
            <div className="md:hidden space-y-2">
                {mensuales.map(({ punto, reporte }, i) => (
                    <details key={punto.id} open={i === 0} className="group rounded-xl bg-[#F6F5F2] border border-[#E5E2DB] px-3">
                        <summary className="min-h-[48px] flex items-center justify-between gap-2 cursor-pointer list-none [&::-webkit-details-marker]:hidden">
                            <span className="font-bold text-[#1C1B19]">{_diaDelMensual(punto.fecha)}</span>
                            <span className="flex items-center gap-1.5 text-[13px] text-[#57544E] tabular-nums">
                                {_peso(punto)}{_grasa(punto) ? ` · ${_grasa(punto)}` : ''}
                                <ChevronDown className="w-4 h-4 text-[#57544E] transition-transform group-open:rotate-180" />
                            </span>
                        </summary>
                        <div className="space-y-1.5 pb-3">
                            {!reporte && <p className="text-[#57544E] text-[13px]">{SIN_EL_REPORTE}</p>}
                            {reporte && celdasDelMensual(reporte).filter(c => c.valor).map(c => (
                                <div key={c.que} className="flex justify-between items-start gap-3 text-[13px]">
                                    <span className="text-[#57544E] shrink-0 max-w-[45%]">{c.que}</span>
                                    <span className={`text-right break-words min-w-0 rounded px-1.5 py-0.5 ${c.tono ? `${c.tono} font-semibold` : 'text-[#1C1B19]'}`}>{c.valor}</span>
                                </div>
                            ))}
                            {reporte && (
                                <button type="button" onClick={() => onAbrir(reporte.id)}
                                    className="mt-1 min-h-[40px] text-[#B43E06] text-xs font-semibold hover:underline">
                                    Abrir el reporte
                                </button>
                            )}
                        </div>
                    </details>
                ))}
            </div>
        </CardContent>
    </Card>
);

// ========== FOTOS DE LOS MENSUALES (21-10, diseño «Progreso en fotos») ==========
//
// Una columna por mensual con fotos y una fila por pose: la misma pose en la misma fila, así
// se ve el cambio de izquierda a derecha. Encima de cada columna, la fecha y el peso. Sin
// filtros de pose: las tres a la vista.
//
// Al abrir, SOLO LOS TRES ÚLTIMOS MENSUALES (21-10), del más antiguo a la izquierda al más
// reciente a la derecha; la inicial solo si es uno de esos tres. «+X ver» carga TODOS en la
// MISMA fila, cada columna del ancho de antes, y la tira se desplaza hacia los lados (28-10:
// antes bajaba de tres en tres). Las fotos de los que no se ven no se piden hasta pulsarlo.
const POSES_DE_LOS_MENSUALES = [['frente', 'Frente'], ['espalda', 'Espalda'], ['perfil', 'Perfil']];
const MENSUALES_POR_FILA = 3;

// Cada fila, su pose. Las fotos que no la traen (las viejas, de antes de pedirla) no se
// pierden: ocupan los huecos que queden.
const fotosPorPose = (fotos) => {
    const sinPose = (fotos || []).filter(f => !f.pose);
    return POSES_DE_LOS_MENSUALES.map(([p]) => (fotos || []).find(f => f.pose === p) || sinPose.shift() || null);
};

// Una foto del punto, como las pinta el resto del panel: las de Calma por la ruta del equipo
// (`CalmaFoto`, la ref es «calma/<fichero>») y las de la app con `AppFoto`. La ruta de los
// puntos (`/reports/foto/{ref}`) es solo del propio cliente: al equipo le da 403.
// Foto contra foto, sin esquinas ni borde (solo una línea fina entre ellas); la inicial se
// marca con su línea naranja POR DENTRO, que no pisa a la de al lado.
const FotoDelMensual = ({ api, clientId, foto, inicial = false }) => (
    <div className="relative [&_img]:rounded-none [&_img]:border-0 [&_div]:rounded-none [&_div]:border-0">
        {inicial && <span className="absolute inset-0 z-10 ring-2 ring-inset ring-[#FF671F] pointer-events-none" aria-hidden="true" />}
        {!foto ? (
            <div className="w-full aspect-[3/4] bg-[#F6F5F2] flex items-center justify-center text-[11px] text-[#6F6B64]">
                Sin foto
            </div>
        ) : String(foto.ref || foto.id || '').startsWith('calma/')
            ? <CalmaFoto api={api} clientId={clientId} ancho={800} sinPie
                foto={{ file: String(foto.ref || foto.id).slice('calma/'.length) }} />
            : <AppFoto api={api} foto={{ id: foto.id }} sinPie />}
    </div>
);

// «+3 meses» desde la inicial; «+2 sem» si no llega al mes.
const _desdeLaInicial = (desde, hasta) => {
    const dias = Math.round((new Date(hasta) - new Date(desde)) / 864e5);
    if (!Number.isFinite(dias) || dias <= 0) return null;
    const meses = Math.round(dias / 30.44);
    return dias < 30 ? `+${Math.max(1, Math.round(dias / 7))} sem` : `+${meses} ${meses === 1 ? 'mes' : 'meses'}`;
};

const CabeceraDeLaRevision = ({ punto, etiqueta, tono }) => (
    <div className="min-w-0 px-1.5 pb-1.5">
        <p className={`m-0 text-[10px] font-extrabold uppercase tracking-wider ${tono}`}>{etiqueta || ' '}</p>
        <p className="m-0 text-[13px] font-bold text-[#1C1B19] tabular-nums truncate">{_diaDelMensual(punto.fecha)}</p>
        <p className="m-0 text-[12px] text-[#57544E] tabular-nums truncate">{_peso(punto)}</p>
    </div>
);

const FotosDeLosMensuales = ({ api, clientId, mensuales }) => {
    const [verTodas, setVerTodas] = useState(false);
    // Al abrir, la tira se queda al final: los tres últimos siguen donde estaban y lo antiguo
    // queda a la izquierda.
    const tiraRef = useRef(null);
    useEffect(() => {
        const t = tiraRef.current;
        if (t) t.scrollLeft = verTodas ? t.scrollWidth : 0;
    }, [verTodas]);
    // Del más antiguo al más reciente: el más reciente, a la derecha.
    const conFotos = mensuales.filter(({ punto }) => (punto.fotos || []).length > 0)
        .map(({ punto }) => punto)
        .sort((a, b) => String(a.fecha).localeCompare(String(b.fecha)));
    if (!conFotos.length) {
        return (
            <Card className="bg-white border-[#E5E2DB] text-[#1C1B19]" data-testid="fotos-mensuales">
                <CardContent className="p-5">
                    <h2 className="m-0 font-heading text-xl font-bold uppercase tracking-wide text-[#1C1B19]">Fotos</h2>
                    <p className="text-[#57544E] text-sm mt-2 mb-0">Sus reportes mensuales todavía no traen fotos.</p>
                </CardContent>
            </Card>
        );
    }
    const inicial = conFotos[0];
    const ultima = conFotos[conFotos.length - 1];
    const ocultas = Math.max(0, conFotos.length - MENSUALES_POR_FILA);
    const todas = verTodas && ocultas > 0;
    const visibles = todas ? conFotos : conFotos.slice(-MENSUALES_POR_FILA);
    const etiquetaDe = (punto) => (
        punto === inicial ? ['Inicial', 'text-[#B43E06]']
            : punto === ultima ? ['Última', 'text-emerald-700']
            : [_desdeLaInicial(inicial.fecha, punto.fecha), 'text-[#6F6B64]']);
    // Cerrada, tres columnas iguales a todo el ancho (con huecos si hay menos). Abierta, una
    // columna por revisión del MISMO ancho que cerrada (un tercio de la tarjeta, `cqw`) y la
    // tira se desplaza hacia los lados.
    const plantilla = { gridTemplateColumns: todas
        ? `24px repeat(${visibles.length}, calc((100cqw - 24px) / ${MENSUALES_POR_FILA}))`
        : `24px repeat(${MENSUALES_POR_FILA}, minmax(0, 1fr))` };
    const huecos = (clave) => (todas ? [] : Array.from({ length: MENSUALES_POR_FILA - visibles.length },
        (_, k) => <span key={`${clave}${k}`} />));

    return (
        <Card className="bg-white border-[#E5E2DB] text-[#1C1B19]" data-testid="fotos-mensuales">
            <CardContent className="p-4 md:p-5 space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <h2 className="m-0 font-heading text-xl font-bold uppercase tracking-wide text-[#1C1B19]">Fotos</h2>
                        <p className="m-0 mt-0.5 text-sm text-[#6F6B64]">
                            {conFotos.length} {conFotos.length === 1 ? 'revisión' : 'revisiones'} · desde el {_diaDelMensual(inicial.fecha)}
                        </p>
                    </div>
                    {ocultas > 0 && (
                        <button type="button" onClick={() => setVerTodas(v => !v)} aria-expanded={verTodas}
                            title={verTodas ? 'Ver solo los tres últimos' : `Ver las ${conFotos.length} revisiones`}
                            data-testid="fotos-ver-ocultas"
                            className="min-h-[44px] px-4 rounded-full border border-[#FF671F] bg-[#FDEBDF] text-[#B43E06] hover:bg-[#FBD9C4] inline-flex items-center gap-1.5">
                            {verTodas
                                ? <><ChevronUp className="w-4 h-4" /><span className="text-sm font-semibold">Ver menos</span></>
                                : <><span className="font-heading text-lg font-extrabold leading-none">+{ocultas}</span><span className="text-sm font-semibold">ver</span></>}
                        </button>
                    )}
                </div>

                {/* UNA SOLA FILA, TAMBIÉN ABIERTA (Enrique, 28-10): «que NO se expanda en
                    vertical, sino que la página crezca en horizontal», de la más antigua a la
                    izquierda a la más nueva a la derecha. Al abrir se queda al final, donde
                    estaban los tres últimos; el nombre de la pose no se va al desplazar. */}
                <div ref={tiraRef} className="overflow-x-auto [container-type:inline-size]" data-testid="fotos-tira">
                    <div className="grid gap-px items-end" style={plantilla}>
                        <span className={todas ? 'sticky left-0 z-20 bg-white self-stretch' : ''} />
                        {visibles.map(p => {
                            const [etiqueta, tono] = etiquetaDe(p);
                            return <CabeceraDeLaRevision key={p.id} punto={p} etiqueta={etiqueta} tono={tono} />;
                        })}
                        {huecos('c')}
                        {POSES_DE_LOS_MENSUALES.map(([pose, nombre], i) => (
                            <React.Fragment key={pose}>
                                <span className={`self-stretch flex items-center justify-center ${todas ? 'sticky left-0 z-20 bg-white' : ''}`}>
                                    <span className="[writing-mode:vertical-rl] rotate-180 text-[11px] font-extrabold uppercase tracking-wider text-[#6F6B64]">
                                        {nombre}
                                    </span>
                                </span>
                                {visibles.map(p => (
                                    <FotoDelMensual key={p.id} api={api} clientId={clientId}
                                        foto={fotosPorPose(p.fotos)[i]} inicial={p === inicial} />
                                ))}
                                {huecos(pose)}
                            </React.Fragment>
                        ))}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

// ========== FOTOS ADJUNTAS AL FEEDBACK (21-10) ==========
//
// El entrenador elige fotos de la sección Fotos (las de los mensuales del cliente) y van con
// su feedback; el cliente las ve debajo del feedback en Seguimiento. Se guarda la `ref` de
// cada una (`PUT /reports/{id}/feedback`, campo `fotos`), que es con la que el cliente la pide.
const MAX_FOTOS_EN_EL_FEEDBACK = 12;
const _fotoDeLaRef = (ref) => ({ id: ref, ref });

const SelectorDeFotos = ({ api, clientId, mensuales, abierto, elegidas, onCerrar, onElegir }) => {
    const [marcadas, setMarcadas] = useState(elegidas);
    useEffect(() => { if (abierto) setMarcadas(elegidas); }, [abierto]); // eslint-disable-line react-hooks/exhaustive-deps
    // Del más reciente al más antiguo: lo normal es adjuntar las de este mes.
    const conFotos = mensuales.map(({ punto }) => punto).filter(p => (p.fotos || []).length > 0);
    const alternar = (ref) => setMarcadas(prev => {
        if (prev.includes(ref)) return prev.filter(r => r !== ref);
        if (prev.length >= MAX_FOTOS_EN_EL_FEEDBACK) {
            toast.error(`Como mucho ${MAX_FOTOS_EN_EL_FEEDBACK} fotos por feedback.`);
            return prev;
        }
        return [...prev, ref];
    });
    return (
        <Dialog open={abierto} onOpenChange={(o) => !o && onCerrar()}>
            {abierto && (
                <DialogContent style={TEMA_CLARO} className="bg-white [color-scheme:light] border-[#E5E2DB] max-w-3xl max-h-[90vh] overflow-y-auto text-[#1C1B19]" data-testid="selector-fotos-feedback">
                    <DialogHeader>
                        <DialogTitle>Adjuntar fotos al feedback</DialogTitle>
                    </DialogHeader>
                    <p className="text-[#57544E] text-sm -mt-2">Toca las fotos que quieres que vea con tu feedback.</p>
                    {!conFotos.length ? (
                        <p className="text-[#57544E] text-sm py-6 text-center">Sus reportes mensuales todavía no traen fotos.</p>
                    ) : (
                        <div className="space-y-4">
                            {conFotos.map(p => (
                                <div key={p.id}>
                                    <p className="m-0 mb-1.5 text-[13px] font-bold text-[#1C1B19] tabular-nums">
                                        {_diaDelMensual(p.fecha)} <span className="font-normal text-[#57544E]">· {_peso(p)}</span>
                                    </p>
                                    <div className="grid grid-cols-3 gap-px">
                                        {fotosPorPose(p.fotos).map((foto, i) => {
                                            if (!foto) return <FotoDelMensual key={i} api={api} clientId={clientId} foto={null} />;
                                            const ref = String(foto.ref || foto.id);
                                            const marcada = marcadas.includes(ref);
                                            return (
                                                <div key={ref} className="relative">
                                                    <FotoDelMensual api={api} clientId={clientId} foto={foto} />
                                                    <button type="button" onClick={() => alternar(ref)} aria-pressed={marcada}
                                                        aria-label={`${marcada ? 'Quitar' : 'Adjuntar'} ${POSES_DE_LOS_MENSUALES[i][1]} del ${_diaDelMensual(p.fecha)}`}
                                                        data-testid={`elegir-foto-${ref}`}
                                                        className={`absolute inset-0 z-20 ${marcada ? 'ring-4 ring-inset ring-[#FF671F] bg-[#FF671F]/15' : 'hover:bg-black/5'}`}>
                                                        {marcada && (
                                                            <span className="absolute top-2 right-2 w-7 h-7 rounded-full bg-[#FF671F] text-white flex items-center justify-center">
                                                                <Check className="w-4 h-4" />
                                                            </span>
                                                        )}
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={onCerrar} className="bg-transparent border-[#E5E2DB] text-[#1C1B19]">Cancelar</Button>
                        <Button onClick={() => { onElegir(marcadas); onCerrar(); }} data-testid="selector-fotos-adjuntar"
                            className="bg-[#FF671F] hover:bg-[#FF671F]/90 text-white">
                            {marcadas.length ? `Adjuntar ${marcadas.length} ${marcadas.length === 1 ? 'foto' : 'fotos'}` : 'Sin fotos'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            )}
        </Dialog>
    );
};

// Las adjuntas, en pequeño debajo del feedback, cada una con su «quitar».
const FotosAdjuntas = ({ api, clientId, refs, onQuitar }) => (
    refs.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="fotos-adjuntas">
            {refs.map(ref => (
                <div key={ref} className="relative w-20">
                    <FotoDelMensual api={api} clientId={clientId} foto={_fotoDeLaRef(ref)} />
                    <button type="button" onClick={() => onQuitar(ref)} aria-label="Quitar esta foto"
                        className="absolute -top-1.5 -right-1.5 z-20 w-6 h-6 rounded-full bg-[#1C1B19] text-white flex items-center justify-center">
                        <X className="w-3.5 h-3.5" />
                    </button>
                </div>
            ))}
        </div>
    )
);

// Reportes del cliente con feedback editable por el coach (cierra el circuito de ReportsPage)
const ReportsFeedbackList = ({ initialReports, clientId, profile, onObjetivoAplicado, hojaDeCalma = null }) => {
    const { api } = useAuth();
    const [reports, setReports] = useState(initialReports || []);
    // LA PROPUESTA DEL CLIENTE YA NO ESCRIBE LA FICHA SOLA (Francisco, 4-09). La pregunta del
    // reporte se queda, pero su respuesta la ve el entrenador al contestar y la aplica si le
    // parece: el objetivo lo pone él (doc de Jesús del 2-09, fase 2).
    const [aplicando, setAplicando] = useState(false);
    const aplicarObjetivo = async (clave) => {
        setAplicando(true);
        try {
            await api.put(`/admin/clients/${clientId}/objetivo`, { objetivo_actual: clave });
            toast.success('Objetivo actual actualizado', { description: `Ahora es ${nombreDelObjetivo(clave)}.` });
            onObjetivoAplicado?.();
        } catch (e) {
            toast.error(mensajeDeError(e, 'No se pudo aplicar el objetivo'));
        } finally { setAplicando(false); }
    };
    const [drafts, setDrafts] = useState({});
    const [savingId, setSavingId] = useState(null);
    const [detalleId, setDetalleId] = useState(null);   // reporte abierto en el modal
    const [verQuincenales, setVerQuincenales] = useState(false);
    // Las fotos adjuntas que aún no se han guardado, por reporte (refs), y el selector abierto.
    const [fotosDraft, setFotosDraft] = useState({});
    const [eligiendoFotos, setEligiendoFotos] = useState(false);
    // LOS PUNTOS DE EVOLUCIÓN DE ESTE CLIENTE (fase 3 del doc de Jesús del 2-09; 4-09): qué
    // reporte es un punto, de qué ciclo, y sus etiquetas (pico de forma, peso máximo, peso
    // mínimo). Se piden UNA vez al montar la lista y los comparten las filas (las pastillas)
    // y el modal (el bloque de marcar el pico). Si el servidor no los da, no se pinta nada.
    const [puntos, setPuntos] = useState(null);
    const cargarPuntos = useCallback(() => {
        api.get(`/admin/clients/${clientId}/puntos`)
            .then(r => setPuntos(r.data || null))
            .catch(e => { console.error('No se pudieron cargar los puntos del cliente', e); });
    }, [api, clientId]);
    useEffect(() => { setPuntos(null); cargarPuntos(); }, [cargarPuntos]);
    // EL INFORME, AQUÍ DENTRO (T9 del doc 16-08). Se genera solo al enviar el reporte y
    // espera a que Jesús lo mire: hasta ahora el endpoint ya se lo permitía y no había
    // pantalla, así que el informe del cliente salía sin que nadie lo hubiera visto.
    const [informe, setInforme] = useState(null);
    const [cargandoInforme, setCargandoInforme] = useState(false);
    const [publicando, setPublicando] = useState(false);

    const verInforme = async (reportId) => {
        setCargandoInforme(true);
        setInforme(null);
        try {
            const r = await api.get(`/reports/${reportId}/informe`);
            setInforme(r.data);
        } catch (e) {
            console.error('No se pudo montar el informe del reporte', e);
            toast.error('No hemos podido crear el informe de este reporte');
        } finally { setCargandoInforme(false); }
    };

    const { confirm: confirmarBorrado } = useConfirm();
    const publicar = async (reportId) => {
        // SIN FEEDBACK SE PREGUNTA ANTES (REP-13, 14-10): se publicaba al primer clic y no
        // tiene vuelta atrás. La regla, en `lib/publicarInforme`.
        const pregunta = preguntaAntesDePublicar({
            guardado: reports.find(r => r.id === reportId)?.trainer_feedback,
            borrador: drafts[reportId],
        });
        if (pregunta && !await confirmarBorrado(pregunta)) return;
        setPublicando(true);
        try {
            const r = await api.post(`/reports/${reportId}/informe/publicar`);
            setReports(prev => prev.map(x => x.id === reportId
                ? { ...x, informe_estado: 'entregado' } : x));
            setInforme(r.data?.informe || informe);
            toast.success('Informe publicado', {
                description: 'El cliente ya lo tiene y le ha llegado el aviso.',
            });
        } catch (e) {
            console.error('No se pudo publicar el informe', e);
            toast.error('No hemos podido publicar el informe');
        } finally { setPublicando(false); }
    };

    const saveFeedback = async (reportId) => {
        const text = (drafts[reportId] ?? (reports.find(r => r.id === reportId)?.trainer_feedback || '')).trim();
        const guardado = (reports.find(r => r.id === reportId)?.trainer_feedback || '').trim();
        const fotos = fotosDraft[reportId];
        if (!text && fotos?.length) {
            toast.error('Escribe el feedback para poder adjuntarle fotos.');
            return;
        }
        // BORRAR NO ES GUARDAR (12-10). Dejar la caja vacía sobre un feedback ya escrito lo
        // eliminaba sin preguntar y decía "Feedback guardado": el cliente perdía una
        // respuesta que quizá ya había leído. Se pregunta, y se dice lo que ha pasado.
        const borra = !text && !!guardado;
        if (borra && !await confirmarBorrado({
            title: '¿Borrar este feedback?',
            description: 'El cliente dejará de verlo en su reporte. Si ya lo leyó, se queda sin esa respuesta.',
            confirmLabel: 'Borrarlo', cancelLabel: 'Dejarlo como estaba',
        })) return;
        setSavingId(reportId);
        try {
            const { data } = await api.put(`/reports/${reportId}/feedback`,
                { feedback: text, borrar: borra, ...(fotos !== undefined ? { fotos } : {}) });
            setReports(prev => prev.map(r => r.id === reportId ? {
                ...r, trainer_feedback: text || null,
                ...(Array.isArray(data?.feedback_fotos) ? { feedback_fotos: data.feedback_fotos } : {}),
            } : r));
            setDrafts(prev => { const d = { ...prev }; delete d[reportId]; return d; });
            setFotosDraft(prev => { const d = { ...prev }; delete d[reportId]; return d; });
            toast.success(borra ? 'Feedback borrado' : 'Feedback guardado');
        } catch (e) {
            console.error('No se pudo guardar el feedback del reporte', e);
            toast.error('No hemos podido guardar el feedback. Inténtalo en un momento.');
        } finally { setSavingId(null); }
    };

    if (!reports.length) {
        return <p className="text-[#6F6B64] text-sm text-center py-10" data-testid="sin-reportes">Todavía no ha mandado ningún reporte.</p>;
    }
    const abierto = reports.find(r => r.id === detalleId) || null;
    const draftAbierto = abierto ? (drafts[abierto.id] ?? (abierto.trainer_feedback || '')) : '';
    // Sin los espacios de los lados: una caja con solo espacios no es un cambio (12-10).
    const dirtyAbierto = abierto ? draftAbierto.trim() !== (abierto.trainer_feedback || '').trim() : false;
    const abrir = (id) => { setDetalleId(id); setInforme(null); };

    // LOS MENSUALES SON LOS PUNTOS DE EVOLUCIÓN (ver «Seguimiento como en el lienzo», arriba):
    // del más reciente al más antiguo, cada uno con su reporte de la ficha si está entre los
    // que trae. El Punto 0 del alta no es un reporte y no entra.
    const porId = new Map(reports.map(r => [r.id, r]));
    const mensuales = (puntos?.puntos || [])
        .filter(p => p.tipo === 'reporte')
        .map(p => ({ punto: p, reporte: porId.get(p.report_id) || null }))
        .sort((a, b) => String(b.punto.fecha).localeCompare(String(a.punto.fecha)));
    // El feedback de arriba es el del último mensual: es el que se contesta esta semana.
    const ultimoMensual = mensuales.find(m => m.reporte)?.reporte || null;
    const draftUltimo = ultimoMensual ? (drafts[ultimoMensual.id] ?? (ultimoMensual.trainer_feedback || '')) : '';
    const fotosGuardadasUltimo = (ultimoMensual?.feedback_fotos || []).map(f => f.ref);
    const fotosUltimo = ultimoMensual ? (fotosDraft[ultimoMensual.id] ?? fotosGuardadasUltimo) : [];
    const dirtyUltimo = ultimoMensual ? (draftUltimo.trim() !== (ultimoMensual.trainer_feedback || '').trim()
        || fotosUltimo.join('|') !== fotosGuardadasUltimo.join('|')) : false;
    const ponerFotosUltimo = (refs) => setFotosDraft(prev => ({ ...prev, [ultimoMensual.id]: refs }));
    const quincenales = reports.filter(r => r.tipo === 'quincenal');

    return (
        <>
        {mensuales.length > 0 && <TablaDeMensuales mensuales={mensuales} onAbrir={abrir} hojaDeCalma={hojaDeCalma} />}
        {mensuales.length > 0 && <FotosDeLosMensuales api={api} clientId={clientId} mensuales={mensuales} />}

        {ultimoMensual && (
            <Card className="bg-white border-[#FF671F]/40 text-[#1C1B19]" data-testid="feedback-del-mensual">
                <CardContent className="p-5 space-y-2.5">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <h2 className="m-0 text-base font-bold text-[#1C1B19]">
                            Feedback del mensual del {_diaDelMensual(diaDeCalendario(ultimoMensual.created_at))}
                        </h2>
                        <button type="button" onClick={() => abrir(ultimoMensual.id)}
                            className="text-[#B43E06] text-xs font-semibold hover:underline">Abrir el reporte</button>
                    </div>
                    <Label htmlFor="feedback-ultimo-mensual" className="sr-only">Feedback para el cliente</Label>
                    <Textarea id="feedback-ultimo-mensual" value={draftUltimo} rows={3}
                        onChange={e => setDrafts(prev => ({ ...prev, [ultimoMensual.id]: e.target.value }))}
                        placeholder="Ej: muy buen mes, se nota en la espalda. Ajustamos los macros esta semana."
                        data-testid="feedback-del-mensual-texto"
                        className="bg-[#F6F5F2] border-[#E5E2DB] text-[#1C1B19] text-base md:text-sm" />
                    <FotosAdjuntas api={api} clientId={clientId} refs={fotosUltimo}
                        onQuitar={(ref) => ponerFotosUltimo(fotosUltimo.filter(r => r !== ref))} />
                    {ultimoMensual.informe_estado === 'pendiente_revision' && (
                        <p className="text-[#B43E06] text-[11px] uppercase tracking-wider">Informe por publicar</p>
                    )}
                    <div className="flex flex-col md:flex-row gap-2">
                        <Button onClick={() => saveFeedback(ultimoMensual.id)} disabled={!dirtyUltimo || savingId === ultimoMensual.id}
                            data-testid="feedback-del-mensual-guardar"
                            className="w-full md:w-auto min-h-[44px] bg-[#FF671F] hover:bg-[#FF671F]/90 text-white disabled:opacity-40">
                            {savingId === ultimoMensual.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" />Guardar feedback</>}
                        </Button>
                        <Button variant="outline" onClick={() => setEligiendoFotos(true)} data-testid="feedback-adjuntar-fotos"
                            className="w-full md:w-auto min-h-[44px] bg-transparent border-[#FF671F]/50 text-[#B43E06] hover:bg-[#FF671F]/10">
                            <Paperclip className="w-4 h-4 mr-1" />Adjuntar foto(s)
                        </Button>
                    </div>
                    <SelectorDeFotos api={api} clientId={clientId} mensuales={mensuales} abierto={eligiendoFotos}
                        elegidas={fotosUltimo} onCerrar={() => setEligiendoFotos(false)} onElegir={ponerFotosUltimo} />
                </CardContent>
            </Card>
        )}

        {quincenales.length > 0 && (
            <Card className="bg-white border-[#E5E2DB] text-[#1C1B19]" data-testid="quincenales">
                <CardContent className="px-5 py-3">
                    <button type="button" onClick={() => setVerQuincenales(v => !v)} aria-expanded={verQuincenales}
                        data-testid="quincenales-abrir"
                        className="w-full min-h-[44px] flex items-center gap-2.5 text-left">
                        <span className="flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5">
                            <span className="font-bold text-[#1C1B19]">Reportes quincenales · {quincenales.length}</span>
                        </span>
                        <ChevronDown className={`ml-auto w-4 h-4 text-[#57544E] transition-transform ${verQuincenales ? 'rotate-180' : ''}`} />
                    </button>
                    {verQuincenales && (
                        <ul className="m-0 p-0 list-none space-y-1 pb-2">
                            {quincenales.map(r => (
                                <li key={r.id}>
                                    <button type="button" onClick={() => abrir(r.id)}
                                        className="w-full text-left text-sm text-[#57544E] hover:text-[#1C1B19] py-1.5 tabular-nums">
                                        <b className="text-[#1C1B19]">{_diaDelMensual(diaDeCalendario(r.created_at))}</b>
                                        {r.weight != null && <> · {kilos(r.weight)} kg</>}
                                        {r.entreno_grado ? <> · Entrenamiento {estrellas(r.entreno_grado)}</> : null}
                                        {r.descanso_grado ? <> · Descanso {estrellas(r.descanso_grado)}</> : null}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </CardContent>
            </Card>
        )}

            <Dialog open={!!abierto} onOpenChange={(o) => !o && setDetalleId(null)}>
                {abierto && (
                    <DialogContent style={TEMA_CLARO} className="bg-white [color-scheme:light] border-[#E5E2DB] max-w-2xl max-h-[90vh] overflow-y-auto text-[#1C1B19]" data-testid="report-detail">
                        <DialogHeader>
                            <DialogTitle className="uppercase tracking-wider">
                                Reporte del {new Date(abierto.created_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}
                            </DialogTitle>
                        </DialogHeader>
                        <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm bg-[#F6F5F2] rounded-lg p-3 border border-[#E5E2DB]">
                            {abierto.weight != null && <span className="text-[#57544E]">Peso <b className="text-[#1C1B19]">{kilos(abierto.weight)} kg</b></span>}
                            {abierto.training_compliance != null && <span className="text-[#57544E]">Entreno <b className="text-[#1C1B19]">{abierto.training_compliance}%</b></span>}
                            {abierto.nutrition_compliance != null && <span className="text-[#57544E]">Nutrición <b className="text-[#1C1B19]">{abierto.nutrition_compliance}%</b></span>}
                        </div>
                        {/* EL PICO DE FORMA SE MARCA AQUÍ, con la foto y el peso del reporte
                            delante (fase 3 del doc de Jesús del 2-09; 4-09). Solo sale si el
                            reporte es un punto de Evolución: un quincenal no lo es. */}
                        <PicoDeForma api={api} reporte={abierto} puntos={puntos} onCambio={cargarPuntos} />
                        {/* Las tres preguntas del formulario de siempre (punto 5 del 05-08).
                            El objetivo que marca el cliente es una PROPUESTA (Francisco,
                            4-09): antes reescribía la ficha al mandar el reporte; ahora se
                            enseña aquí y el entrenador la aplica con el botón si le parece. */}
                        {(abierto.proximo_objetivo || abierto.viabilidad_ajuste || abierto.cumplimiento_entreno) && (
                            <div className="space-y-1.5 text-sm bg-[#F6F5F2] rounded-lg p-3 border border-[#E5E2DB]">
                                {abierto.proximo_objetivo && (() => {
                                    const propuesto = normalizarObjetivo(abierto.proximo_objetivo);
                                    const yaLoTiene = !!propuesto && propuesto === normalizarObjetivo(profile?.objetivo_actual);
                                    return (
                                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1" data-testid="propuesta-objetivo">
                                            <p className="text-[#57544E]">El cliente dice que su objetivo ahora es{' '}
                                                <b className="text-[#B43E06]">{nombreDeLaPropuesta(abierto.proximo_objetivo)}</b>
                                            </p>
                                            {propuesto && (yaLoTiene
                                                ? <span className="text-emerald-700 text-xs" data-testid="objetivo-ya-lo-tiene">Es el que ya tiene</span>
                                                : <Button size="sm" variant="outline" onClick={() => aplicarObjetivo(propuesto)} disabled={aplicando}
                                                    data-testid="aplicar-objetivo"
                                                    className="bg-transparent border-[#FF671F]/50 text-[#B43E06] hover:bg-[#FF671F]/10 h-7 text-xs">
                                                    {aplicando ? 'Aplicando...' : 'Aplicar como objetivo actual'}
                                                </Button>)}
                                        </div>
                                    );
                                })()}
                                {abierto.viabilidad_ajuste && (
                                    <p className="text-[#57544E]">Margen para ajustar{' '}
                                        <b className="text-[#1C1B19]">{VIABILIDAD_REPORTE[abierto.viabilidad_ajuste] || abierto.viabilidad_ajuste}</b>
                                    </p>
                                )}
                                {abierto.cumplimiento_entreno && (
                                    <p className="text-[#57544E]">Entrenamientos{' '}
                                        <b className="text-[#1C1B19]">{ENTRENO_REPORTE[abierto.cumplimiento_entreno] || abierto.cumplimiento_entreno}</b>
                                    </p>
                                )}
                            </div>
                        )}
                        {/* LO QUE CONTESTÓ EN EL FORMULARIO NUEVO (T7 y T8). Sin esto, el
                            panel enseñaba peso, cumplimiento y tres preguntas viejas: todo
                            lo que el cliente cuenta del entreno, las lesiones, el cardio o
                            la suplementación se quedaba guardado y sin ver. */}
                        <RespuestasDelReporte reporte={abierto} />

                        {/* Y LO QUE CONTESTÓ EN CALMA, para los meses de antes de la app. Es
                            el mismo formulario mensual, y hasta hoy de esos meses solo se
                            guardaba el peso: las respuestas estaban escritas en la base y no
                            las enseñaba ninguna pantalla. */}
                        <RespuestasDeCalma reporte={abierto} />

                        <MedidasDelReporte reporte={abierto} tomas={puntos?.tomas_medidas} />

                        {/* Por la misma regla que Macros: ni «Importado de Calma» ni «XX» (punto 104). */}
                        {/* Como la escribió (14-10, QUI-03): con sus saltos de línea, y una palabra
                            larguísima no ensancha la ventana. Igual que el feedback hacia él (REP-06). */}
                        <TextoDelCliente texto={notaDelCliente(abierto)} testId="nota-del-cliente" />

                        {/* EL INFORME MONTADO. Se pide al abrirlo y no al cargar la ficha:
                            cruza dietas, cierres y macros de todo el mes, y no tiene
                            sentido montar los cincuenta de la lista para pintar una fila. */}
                        <div className="rounded-lg border border-[#E5E2DB] bg-[#F6F5F2] p-3 space-y-2">
                            <div className="flex items-center justify-between gap-2">
                                {/* Se llama como el reporte que se abre: el de un quincenal no es "del mes" (13-10). */}
                                <span className="text-[#57544E] text-xs uppercase tracking-wider" data-testid="titulo-del-informe">{nombreDelInforme(abierto.tipo)}</span>
                                <Button size="sm" variant="outline" data-testid="ver-informe"
                                    onClick={() => verInforme(abierto.id)} disabled={cargandoInforme}
                                    className="bg-transparent border-[#E5E2DB] text-[#1C1B19] h-7 text-xs">
                                    {cargandoInforme ? 'Creando...' : informe ? 'Actualizar' : 'Ver el informe'}
                                </Button>
                            </div>
                            {informe && (
                                <div className="max-h-[45vh] overflow-y-auto pr-1" data-testid="informe-montado">
                                    {informe.generado === false
                                        ? <p className="text-[#57544E] text-sm">{informe.mensaje || 'Todavía no se puede crear.'}</p>
                                        : <InformeMensual informe={informe} />}
                                </div>
                            )}
                            {abierto.informe_estado === 'pendiente_revision' ? (
                                <>
                                    <Button size="sm" onClick={() => publicar(abierto.id)} disabled={publicando}
                                        data-testid="publicar-informe"
                                        className="bg-[#FF671F] hover:bg-[#FF671F]/90 text-white h-8 text-xs">
                                        {publicando ? 'Publicando...' : 'Publicar el informe'}
                                    </Button>
                                </>
                            ) : abierto.informe_estado === 'entregado' ? (
                                <p className="text-emerald-700 text-[11px]">Publicado</p>
                            ) : null}
                        </div>

                        <div>
                            <Label className="text-[#57544E] text-xs">Feedback para el cliente</Label>
                            <Textarea value={draftAbierto} onChange={e => setDrafts(prev => ({ ...prev, [abierto.id]: e.target.value }))}
                                placeholder="Escribe feedback para el cliente..." rows={3}
                                className="bg-[#F6F5F2] border-[#E5E2DB] text-[#1C1B19] mt-1" />
                            {/* Los cuatro mensajes de siempre, en un botón (punto 4.1). */}
                            <PlantillasFeedback actual={draftAbierto}
                                onInsertar={t => setDrafts(prev => ({ ...prev, [abierto.id]: t }))} />
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setDetalleId(null)} className="bg-transparent border-[#E5E2DB] text-[#1C1B19]">Cerrar</Button>
                            <Button onClick={() => saveFeedback(abierto.id)} disabled={!dirtyAbierto || savingId === abierto.id}
                                className="bg-[#FF671F] hover:bg-[#FF671F]/90 text-white disabled:opacity-40">
                                {savingId === abierto.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" />Guardar feedback</>}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                )}
            </Dialog>
        </>
    );
};

const DietDetail = ({ diet }) => {
    const comidas = diet?.comidas || {};
    const keys = Object.keys(comidas).sort((a, b) => (MEAL_ORDER[a] || 99) - (MEAL_ORDER[b] || 99));
    const dayTotal = { P: 0, H: 0, G: 0 };
    const meals = keys.map((k) => {
        const foods = comidas[k]?.alimentos || [];
        const mt = { P: 0, H: 0, G: 0 };
        foods.forEach((a) => {
            const e = a.macros_efectivos || {};
            mt.P += e.P || 0; mt.H += e.H || 0; mt.G += e.G || 0;
            dayTotal.P += e.P || 0; dayTotal.H += e.H || 0; dayTotal.G += e.G || 0;
        });
        return { k, foods, mt };
    });
    // QUIÉN GUARDÓ ESTE DÍA (punto 4.11). Se anota desde que el entrenador puede entrar en la
    // calculadora de un cliente, y no se veía en ninguna pantalla. Aquí es donde contesta a la
    // pregunta de Jesús: si le montaste el martes una dieta y el miércoles aparece firmada por
    // él, es que la ha cambiado -- y esa es justo la conversación que hay que tener.
    const loGuardo = diet?.editado_por;
    const loGuardoElCoach = diet?.editado_como === 'entrenador';
    return (
        <div className="space-y-2.5">
            {loGuardo && (
                <p className="text-[11px] text-[#6F6B64]" data-testid="dieta-quien-la-guardo">
                    Lo guardó{' '}
                    <span className={loGuardoElCoach ? 'text-[#B43E06] font-semibold' : 'text-[#57544E] font-semibold'}>
                        {loGuardo}
                    </span>
                    {loGuardoElCoach ? ' (del equipo)' : ' (el cliente)'}
                    {diet?.updated_at ? ` · ${_fechaCorta(diaDeCalendario(diet.updated_at))}` : ''}
                </p>
            )}
            {meals.map(({ k, foods, mt }) => (
                <div key={k} className="bg-[#F6F5F2] rounded-lg p-3 border border-[#E5E2DB]">
                    <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[#1C1B19] text-sm font-semibold">{MEAL_LABEL[k] || k}</span>
                        <span className="text-[11px]"><span className="text-[#C2410C]">P{Math.round(mt.P)}</span> <span className="text-[#1D4ED8]">H{Math.round(mt.H)}</span> <span className="text-[#854D0E]">G{Math.round(mt.G)}</span></span>
                    </div>
                    <div className="space-y-1">
                        {foods.length ? foods.map((a, i) => {
                            const e = a.macros_efectivos || {};
                            return (
                                <div key={i} className="flex items-center gap-2 text-xs">
                                    <span className="text-[#1C1B19] flex-1 min-w-0 truncate">{a.nombre}</span>
                                    {/* LA CANTIDAD, CON SU UNIDAD. Aquí se pintaba el número
                                        pelado con una «g» pegada detrás, así que un huevo salía
                                        como «1 g» y una lata de atún, también (Jesús, 16-08).
                                        Ahora el texto viene resuelto del backend -- «2 ud
                                        (126 g)» --, que es el único que sabe si el alimento va
                                        por unidades y cuánto pesa una. */}
                                    <span className="text-[#6F6B64] whitespace-nowrap w-24 text-right" title="Cantidad">
                                        {a.cantidad_texto || `${Math.round(a.cantidad_g || 0)} g`}
                                    </span>
                                    <span className="whitespace-nowrap tabular-nums w-24 text-right">
                                        <span className="text-[#C2410C]">P{Math.round(e.P || 0)}</span> <span className="text-[#1D4ED8]">H{Math.round(e.H || 0)}</span> <span className="text-[#854D0E]">G{Math.round(e.G || 0)}</span>
                                    </span>
                                </div>
                            );
                        }) : <span className="text-[#6F6B64] text-xs">Vacía</span>}
                    </div>
                </div>
            ))}
            <div className="flex items-center justify-between pt-2 border-t border-[#E5E2DB]">
                <span className="text-[#57544E] text-sm font-semibold">Total del día</span>
                <span className="text-sm font-bold"><span className="text-[#C2410C]">P{Math.round(dayTotal.P)}</span> · <span className="text-[#1D4ED8]">H{Math.round(dayTotal.H)}</span> · <span className="text-[#854D0E]">G{Math.round(dayTotal.G)}</span></span>
            </div>
        </div>
    );
};

const EmptyState = ({ icon: Icon, message, action }) => (
    <Card className="bg-white border-[#E5E2DB]"><CardContent className="p-8 text-center">
        <Icon className="w-10 h-10 text-[#E5E2DB] mx-auto mb-3" />
        <p className="text-[#6F6B64] text-sm">{message}</p>
        {action}
    </CardContent></Card>
);

export default ClientDetailPage;
